import React, { useState, useEffect, useContext, useRef } from 'react';
import DisclaimerInfo from '../components/DisclaimerInfo';
import MapSelectModal from '../components/MapSelectModal';
import { ThemeContext } from '../App';
import { supabase } from '../supabaseClient';
import { useToast } from '../components/Toast';
import { BalancoHidricoService } from '../services/BalancoHidricoService';
import ReactApexChart from 'react-apexcharts';
import { useAddonIrrigacao } from '../hooks/useAddonIrrigacao';
import AddonIrrigacaoModal from '../components/AddonIrrigacaoModal';
import { useUserEvent } from '../hooks';
import { isFutureLocalDate } from '../utils/localDate';
import { listarSugestoesKcFao56, obterSugestaoKcFao56 } from '../constants/kcFao56';
import { buscarContextosCultivo } from '../services/ciclosCultivoService';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import '../styles/module-shell.css';
import '../styles/tech-components.css';
import './Irrigacao.css';
pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts;
import { Capacitor } from '@capacitor/core';
import { FaArrowLeft, FaBroadcastTower, FaChartBar, FaChevronRight, FaClock, FaExclamationTriangle, FaHome, FaLayerGroup, FaMapMarkerAlt, FaSeedling, FaShieldAlt, FaTint } from 'react-icons/fa';
import {
  deriveIrrigationSummary,
  formatLocalDate,
  getPriorityBalance,
  isIrrigationDataStale,
  normalizeBalanceItem,
} from './irrigationDecision';
import {
  geometryBoundaryPolygons,
  propertyReferencePoint,
  validateStationLocation,
} from './sensorGeoValidation';
import {
  applyCanonicalAreasToIrrigationBalances,
  integrarTalhoesPermanentesNaIrrigacao,
} from './irrigationPlotModel';
import { loadOperationalPropertyStructure } from '../services/plotMigrationReviewService';
import { applyCanonicalPhysicalPlots } from '../services/operationalPropertiesService';
import { loadVisibleAuthorizedProperties } from '../services/authorizedPropertiesService';
import { propertyActionAllowed } from '../utils/collaborationAccess';

// Importação condicional do Capacitor Filesystem
let Filesystem, Directory;
if (typeof window !== 'undefined' && window.Capacitor) {
  try {
    // eslint-disable-next-line
    ({ Filesystem, Directory } = require('@capacitor/filesystem'));
  } catch (e) {}
}

// Importação condicional do File Opener
let FileOpener;
if (typeof window !== 'undefined' && window.Capacitor) {
  try {
    ({ FileOpener } = require('@capacitor-community/file-opener'));
  } catch (e) {}
}

// Hook reativo para detectar telas móveis (reagente a redimensionamento)
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

/**
 * COMPONENTE PRINCIPAL: MANEJO DE IRRIGAÇÃO
 * Sistema profissional de manejo de irrigação baseado em estações meteorológicas locais
 * Totalmente independente dos outros módulos do app
 */
export default function Irrigacao({ user, onBack, previewMode = false, previewData = null }) {
  const { darkMode } = useContext(ThemeContext);
  const toast = useToast();
  const isMobile = useIsMobile();
  useUserEvent({ userId: user?.id, tela: 'Irrigacao', evento: 'page_view', comDuração: true });

  // Add-on irrigação
  const addonReal = useAddonIrrigacao(previewMode ? undefined : user?.id);
  const addon = previewMode
    ? {
        status: 'ativo',
        expiracao: null,
        trialUsado: false,
        diasRestantes: null,
        loading: false,
        temAcesso: true,
        ativarTrial: async () => ({ sucesso: true }),
      }
    : addonReal;
  const [showAddonModal, setShowAddonModal] = useState(false);

  // IDs de eventos deletados otimisticamente — impede que qualquer fetch os restaure
  const deletedEventIdsRef = useRef(new Set());

  // Estados
  const [loading, setLoading] = useState(!previewMode);
  const [processandoBalanco, setProcessandoBalanco] = useState(false);
  const [balancoDone, setBalancoDone] = useState(0);
  const [propriedades, setPropriedades] = useState(() =>
    previewMode && previewData?.propriedade ? [previewData.propriedade] : []
  );
  const [propriedadeSelecionada, setPropriedadeSelecionada] = useState(() =>
    previewMode ? previewData?.propriedade || null : null
  );
  const [resumo, setResumo] = useState(() => previewMode ? previewData?.resumo || null : null);
  const [balancoSnapshot, setBalancoSnapshot] = useState(() => ({
    loading: !previewMode,
    ready: previewMode,
    items: previewMode ? previewData?.balancos || [] : [],
    dataReferencia: null,
    stale: false,
  }));
  const [telaAtual, setTelaAtual] = useState('dashboard'); // dashboard, estacoes, talhoes, eventos
  const [estacoes, setEstacoes] = useState(() => previewMode ? previewData?.estacoes || [] : []);
  const [talhoes, setTalhoes] = useState(() => previewMode ? previewData?.talhoes || [] : []);
  const [sensoresDisponiveis, setSensoresDisponiveis] = useState([]);
  
  // Estados para modais de formulário
  const [modalEstacao, setModalEstacao] = useState(false);
  const [estacaoEdicao, setEstacaoEdicao] = useState(null);
  const [modalConfigTalhao, setModalConfigTalhao] = useState(false);
  const [talhaoConfig, setTalhaoConfig] = useState(null);
  const [modalEvento, setModalEvento] = useState(false);
  const [eventoEdicao, setEventoEdicao] = useState(null);
  const autorizarColaboracao = action => {
    const allowed = propertyActionAllowed(propriedadeSelecionada, 'irrigacao', action, true);
    if (!allowed) toast.info('Seu acesso a esta propriedade não permite esta ação de irrigação. Consulte o proprietário.');
    return allowed;
  };
  const [eventos, setEventos] = useState(() => previewMode ? previewData?.eventos || [] : []);
  const [modalOpcaoCalculo, setModalOpcaoCalculo] = useState(false);
  
  // Carregar propriedades do usuário
  useEffect(() => {
    if (previewMode) return;
    carregarPropriedades();
  }, [user, previewMode]);
  
  // Carregar resumo quando selecionar propriedade
  useEffect(() => {
    if (propriedadeSelecionada && !previewMode) {
      setResumo(null);
      setEstacoes([]);
      setTalhoes([]);
      setBalancoSnapshot({ loading: true, ready: false, items: [], dataReferencia: null, stale: false });
      carregarResumo();
      carregarEstacoes();
      carregarTalhoes();
      carregarSensores();
      carregarEventos();
    }
  }, [propriedadeSelecionada, previewMode]);

  const temManejoAtivo = talhoes.some(t => {
    const config = Array.isArray(t.configuracao_irrigacao) ? t.configuracao_irrigacao[0] : t.configuracao_irrigacao;
    return Boolean(config?.manejo_ativo);
  });
  const resumoExibicao = previewMode
    ? resumo
    : balancoSnapshot.ready
      ? deriveIrrigationSummary(balancoSnapshot.items, {
        ...(resumo || {}),
        estacoes_ativas: estacoes.filter(estacao => estacao.ativa).length,
      })
      : null;
  const irrigationDataStale = !previewMode && balancoSnapshot.stale;

  const previewAction = () => {
    toast.info('Esta ação está desativada na prévia local.');
  };
  
  const carregarPropriedades = async () => {
    try {
      setLoading(true);

      const { properties: lista } = await loadVisibleAuthorizedProperties(
        user.id,
        'id, nome, municipio, estado, coordenada_x, coordenada_y, geometria, user_id', 'irrigacao'
      );
      setPropriedades(lista);
      // Auto-selecionar se só houver uma propriedade
      if (lista.length === 1) setPropriedadeSelecionada(lista[0]);

    } catch (error) {
      console.error('Erro ao carregar propriedades:', error);
      toast.error('Erro ao carregar propriedades');
    } finally {
      setLoading(false);
    }
  };
  
  const carregarResumo = async () => {
    try {
      // Buscar resumo da view (criada na migração)
      const { data, error } = await supabase
        .from('resumo_irrigacao_propriedade')
        .select('*')
        .eq('propriedade_id', propriedadeSelecionada.id)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao carregar resumo:', error);
      }
      
      setResumo(data || {
        total_talhoes: 0,
        talhoes_manejo_ativo: 0,
        talhoes_urgente: 0,
        talhoes_irrigar: 0,
        talhoes_aguardar: 0,
        talhoes_ok: 0,
        estacoes_ativas: 0
      });
      
    } catch (error) {
      console.error('Erro ao carregar resumo:', error);
    }
  };
  
  const carregarEstacoes = async () => {
    try {
      const { data, error } = await supabase
        .from('estacoes_irrigacao')
        .select('*')
        .eq('propriedade_id', propriedadeSelecionada.id)
        .order('nome');
      
      if (error) throw error;
      setEstacoes(data || []);
    } catch (error) {
      console.error('Erro ao carregar estações:', error);
    }
  };
  
  const carregarTalhoes = async () => {
    try {
      const propriedadeId = propriedadeSelecionada.id;
      const { data: talhoesPermanentes, error } = await supabase
        .from('talhoes')
        .select('*')
        .eq('propriedade_id', propriedadeId)
        .order('nome');

      if (error) throw error;
      let talhoesCanonicos = talhoesPermanentes || [];
      try {
        const estrutura = await loadOperationalPropertyStructure();
        talhoesCanonicos = applyCanonicalPhysicalPlots(
          talhoesCanonicos,
          estrutura,
          propriedadeSelecionada
        );
      } catch (plotError) {
        console.warn('Talhões atuais indisponíveis na irrigação; mantendo o último cadastro conhecido.', plotError);
      }
      const ids = talhoesCanonicos.map(talhao => talhao.id);
      let configuracoes = [];
      let contextosCultivo = new Map();

      if (ids.length) {
        const { data: configuracoesData, error: configuracoesError } = await supabase
          .from('configuracao_irrigacao_talhao')
          .select('*')
          .in('talhao_id', ids);
        if (configuracoesError) {
          console.warn('Configurações de irrigação indisponíveis; exibindo os talhões sem configuração.', configuracoesError);
        } else {
          configuracoes = configuracoesData || [];
        }

        try {
          contextosCultivo = await buscarContextosCultivo(ids);
        } catch (cycleError) {
          console.warn('Ciclos de cultivo indisponíveis; usando os campos legados dos talhões.', cycleError);
        }
      }

      setTalhoes(integrarTalhoesPermanentesNaIrrigacao(
        talhoesCanonicos,
        configuracoes,
        contextosCultivo
      ));
    } catch (error) {
      console.error('Erro ao carregar talhões:', error);
      toast.error('Não foi possível carregar os talhões desta propriedade.');
    }
  };
  
  const carregarSensores = async () => {
    try {
      // Buscar sensores do usuário
      const { data, error } = await supabase
        .from('sensores_usuarios')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) throw error;
      setSensoresDisponiveis(data || []);
    } catch (error) {
      console.error('Erro ao carregar sensores:', error);
    }
  };
  
  // ============================================
  // FUNÇÕES DE SALVAR
  // ============================================
  
  const salvarEstacao = async (dados) => {
    if (!autorizarColaboracao(estacaoEdicao ? 'edit' : 'create')) return;
    try {
      const estacaoData = {
        ...dados,
        propriedade_id: propriedadeSelecionada.id,
        user_id: propriedadeSelecionada._collaborationPermissions ? propriedadeSelecionada.user_id : user.id
      };
      
      if (estacaoEdicao) {
        // Editar estação existente
        const { error } = await supabase
          .from('estacoes_irrigacao')
          .update(estacaoData)
          .eq('id', estacaoEdicao.id);
        
        if (error) throw error;
        toast.success('Estação atualizada com sucesso!');
      } else {
        // Criar nova estação
        const { error } = await supabase
          .from('estacoes_irrigacao')
          .insert([estacaoData]);
        
        if (error) throw error;
        toast.success('Estação cadastrada com sucesso!');
      }
      
      setModalEstacao(false);
      setEstacaoEdicao(null);
      carregarEstacoes();
      carregarResumo();
    } catch (error) {
      console.error('Erro ao salvar estação:', error);
      toast.error('Erro ao salvar estação: ' + error.message);
    }
  };

  const excluirEstacao = async (estacao) => {
    if (!autorizarColaboracao('delete')) return;
    const alvo = estacao || estacaoEdicao;
    if (!alvo) return;
    if (!window.confirm(`Excluir a estação "${alvo.nome}"? Esta ação não pode ser desfeita.`)) return;
    try {
      const { error } = await supabase
        .from('estacoes_irrigacao')
        .delete()
        .eq('id', alvo.id);
      if (error) throw error;
      toast.success('Estação excluída com sucesso!');
      setModalEstacao(false);
      setEstacaoEdicao(null);
      carregarEstacoes();
      carregarResumo();
    } catch (error) {
      console.error('Erro ao excluir estação:', error);
      toast.error('Erro ao excluir estação: ' + error.message);
    }
  };
  
  const salvarConfiguracao = async (dados) => {
    if (!autorizarColaboracao('edit')) return;
    try {
      if (isFutureLocalDate(talhaoConfig?.data_plantio)) {
        toast.error('O manejo de irrigação só pode ser configurado a partir da data de plantio.');
        return;
      }
      console.log('Salvando configuração para talhão:', talhaoConfig.id);
      console.log('Dados a salvar:', dados);
      
      const configData = {
        ...dados,
        talhao_id: talhaoConfig.id
      };
      
      // Usar UPSERT: insere se não existe, atualiza se existe (baseado em UNIQUE constraint do talhao_id)
      const { error } = await supabase
        .from('configuracao_irrigacao_talhao')
        .upsert(configData, { 
          onConflict: 'talhao_id' // Usa a constraint UNIQUE(talhao_id)
        });
      
      if (error) throw error;
      
      toast.success('Configuração salva com sucesso!');
      
      setModalConfigTalhao(false);
      setTalhaoConfig(null);
      carregarTalhoes();
      carregarResumo();
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      console.error('Detalhes do erro:', error.message, error.details, error.hint);
      toast.error('Erro ao salvar: ' + error.message);
    }
  };
  
  // ============================================
  // FUNÇÕES DE EVENTOS
  // ============================================

  /**
   * Apaga os registros de balanco_hidrico_diario a partir de `dataDesde` para o talhão
   * e recalcula o balanço para que o evento de irrigação seja refletido.
   */
  const invalidarERecalcularBalanco = async (talhaoId, dataDesde) => {
    if (!talhaoId || !dataDesde || !propriedadeSelecionada) return;
    try {
      await supabase
        .from('balanco_hidrico_diario')
        .delete()
        .eq('talhao_id', talhaoId)
        .gte('data', dataDesde);

      await BalancoHidricoService.processarPropriedade(
        propriedadeSelecionada.id,
        new Date()
      );
      carregarResumo();
      carregarTalhoes();
      setBalancoDone(prev => prev + 1);
    } catch (err) {
      console.warn('Erro ao recalcular balanço após evento:', err);
    }
  };

  const carregarEventos = async () => {
    try {
      const { data, error } = await supabase
        .from('eventos_irrigacao')
        .select(`
          *,
          talhoes!inner(
            nome,
            propriedade_id,
            data_colheita
          )
        `)
        .eq('talhoes.propriedade_id', propriedadeSelecionada.id)
        .order('data_planejada', { ascending: false })
        .limit(100); // Limitar aos últimos 100 eventos
      
      if (error) throw error;
      // Filtra eventos que foram deletados otimisticamente — impede restauração por fetch tardio
      setEventos((data || []).filter(e => !deletedEventIdsRef.current.has(e.id)));
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    }
  };
  
  const salvarEvento = async (dados) => {
    if (!autorizarColaboracao(eventoEdicao ? 'edit' : 'create')) return;
    try {
      const talhaoEvento = talhoes.find(talhao => Number(talhao.id) === Number(dados.talhao_id));
      if (isFutureLocalDate(talhaoEvento?.data_plantio)) {
        toast.error('Não é possível registrar irrigação antes da data de plantio do talhão.');
        return;
      }
      console.log('Salvando evento:', dados);
      
      const eventoData = {
        ...dados,
        user_id: user.id
      };

      // Guardar data anterior para invalidar balanço se estava concluído antes
      const dataAnteriorExecutada = eventoEdicao?.status === 'concluido' && eventoEdicao?.data_executada
        ? eventoEdicao.data_executada : null;
      
      if (eventoEdicao?.id) {
        // Editar evento existente
        const { error } = await supabase
          .from('eventos_irrigacao')
          .update(eventoData)
          .eq('id', eventoEdicao.id);
        
        if (error) throw error;
        toast.success('Evento atualizado!');
      } else {
        // Criar novo evento
        const { error } = await supabase
          .from('eventos_irrigacao')
          .insert([eventoData]);
        
        if (error) throw error;
        toast.success('Evento registrado!');
      }
      
      setModalEvento(false);
      setEventoEdicao(null);
      carregarEventos();
      carregarResumo();

      // Invalidar e recalcular balanço se o evento afeta dias já calculados
      const novaDataExecutada = eventoData.status === 'concluido' ? eventoData.data_executada : null;
      const datasAfetadas = [dataAnteriorExecutada, novaDataExecutada].filter(Boolean).sort();
      if (datasAfetadas.length > 0 && eventoData.talhao_id) {
        await invalidarERecalcularBalanco(eventoData.talhao_id, datasAfetadas[0]);
      }
    } catch (error) {
      console.error('Erro ao salvar evento:', error);
      toast.error('Erro ao salvar: ' + error.message);
    }
  };
  
  const marcarEventoExecutado = async (evento, dadosExecucao) => {
    if (!autorizarColaboracao('edit')) return;
    try {
      const { error } = await supabase
        .from('eventos_irrigacao')
        .update({
          status: 'concluido',
          tipo_evento: 'executado',
          data_executada: dadosExecucao.data_executada,
          hora_inicio: dadosExecucao.hora_inicio,
          hora_fim: dadosExecucao.hora_fim,
          lamina_aplicada_mm: dadosExecucao.lamina_aplicada_mm,
          tempo_real_horas: dadosExecucao.tempo_real_horas,
          observacoes: dadosExecucao.observacoes
        })
        .eq('id', evento.id);
      
      if (error) throw error;
      
      toast.success('Evento marcado como executado!');
      carregarEventos();
      carregarResumo();

      // Invalidar e recalcular balanço a partir da data de execução
      if (dadosExecucao.data_executada && evento.talhao_id) {
        await invalidarERecalcularBalanco(evento.talhao_id, dadosExecucao.data_executada);
      }
    } catch (error) {
      console.error('Erro ao marcar evento:', error);
      toast.error('Erro ao atualizar: ' + error.message);
    }
  };

  const cancelarEvento = async (evento) => {
    if (!autorizarColaboracao('edit')) return;
    // Atualiza estado local imediatamente
    setEventos(prev => prev.map(e => e.id === evento.id ? { ...e, status: 'cancelado' } : e));
    try {
      const { error } = await supabase
        .from('eventos_irrigacao')
        .update({ status: 'cancelado' })
        .eq('id', evento.id);

      if (error) throw error;

      toast.success('Evento cancelado.');
      carregarResumo();

      // Invalidar e recalcular balanço se o evento estava concluído (lâmina já contabilizada)
      if (evento.status === 'concluido' && evento.data_executada && evento.talhao_id) {
        await invalidarERecalcularBalanco(evento.talhao_id, evento.data_executada);
      }
    } catch (error) {
      carregarEventos();
      console.error('Erro ao cancelar evento:', error);
      toast.error('Erro ao cancelar: ' + error.message);
    }
  };

  const excluirEvento = async (evento) => {
    if (!autorizarColaboracao('delete')) return;
    // Marca como deletado e remove do estado imediatamente
    deletedEventIdsRef.current.add(evento.id);
    setEventos(prev => prev.filter(e => e.id !== evento.id));
    try {
      const { error, count } = await supabase
        .from('eventos_irrigacao')
        .delete()
        .eq('id', evento.id)
        .select('id', { count: 'exact', head: true });

      if (error) throw error;
      if (count === 0) throw new Error('Sem permissão para excluir (RLS bloqueou)');

      toast.success('Evento excluído.');
      carregarResumo();

      // Invalidar e recalcular balanço se o evento estava concluído (lâmina já contabilizada)
      if (evento.status === 'concluido' && evento.data_executada && evento.talhao_id) {
        await invalidarERecalcularBalanco(evento.talhao_id, evento.data_executada);
      }
    } catch (error) {
      // Reverte se der erro: remove do set e recarrega
      deletedEventIdsRef.current.delete(evento.id);
      carregarEventos();
      console.error('Erro ao excluir evento:', error);
      toast.error('Erro ao excluir: ' + error.message);
    }
  };
  
  // ============================================
  // FUNÇÃO: PROCESSAR BALANÇO HÍDRICO
  // ============================================
  const processarBalancoHidrico = async (opcoes = {}) => {
    if (!autorizarColaboracao('edit')) return;
    if (!propriedadeSelecionada) {
      toast.error('Selecione uma propriedade');
      return;
    }
    
    setProcessandoBalanco(true);
    toast.info('Processando balanço hídrico...');
    
    try {
      const resultado = await BalancoHidricoService.processarPropriedade(
        propriedadeSelecionada.id,
        new Date(),
        opcoes
      );
      
      console.log('Resultado do processamento:', resultado);
      
      if (resultado.sucesso > 0) {
        toast.success(`✅ Balanço calculado! ${resultado.mensagem}`);
        // Recarregar resumo e talhões para mostrar dados atualizados
        carregarResumo();
        carregarTalhoes();
        setBalancoDone(prev => prev + 1);
      } else if (resultado.indisponibilidade_nasa > 0) {
        toast.warning(`NASA POWER temporariamente indisponível. Os dados em cache foram preservados; aguarde alguns minutos e tente novamente.`);
      } else if (resultado.nao_configurados > 0) {
        toast.warning(`⚙️ Configure os talhões na aba "Talhões" e ative o "Manejo Automático"`);
      } else if (resultado.inativos > 0) {
        toast.warning(`⏸️ Ative o "Manejo Automático" nas configurações dos talhões`);
      } else if (resultado.sem_dados > 0) {
        toast.error(`📡 Estação sem dados para hoje. ${resultado.mensagem}`);
      } else if (resultado.erro > 0) {
        toast.error(`❌ ${resultado.erro} talhões com erro. ${resultado.mensagem}`);
      } else {
        toast.info(resultado.mensagem || 'Nenhum talhão para processar');
      }
      
    } catch (error) {
      console.error('Erro ao processar balanço:', error);
      toast.error('Erro ao calcular: ' + error.message);
    } finally {
      setProcessandoBalanco(false);
    }
  };
  
  if (loading || addon.loading) {
    return (
      <div className={`module-shell module-shell-irrigacao ${darkMode ? 'is-dark' : ''} irrigation-loading-state`} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        color: darkMode ? '#fff' : '#333'
      }}>
        <div className="irrigation-loading-content">
          <FaTint aria-hidden="true" />
          <strong>Carregando manejo de irrigação</strong>
          <span>Preparando propriedades e dados hídricos.</span>
        </div>
      </div>
    );
  }

  // ── PAYWALL: bloqueia acesso se não tem add-on ativo ─────────────────────
  if (!addon.temAcesso) {
    return (
      <>
        <AddonIrrigacaoModal
          show={true}
          onClose={onBack}
          usuario={user}
          trialUsado={addon.trialUsado}
          statusAtual={addon.status}
          onTrialAtivado={async () => {
            const resultado = await addon.ativarTrial();
            return resultado;
          }}
        />
        {/* Fundo desfocado enquanto modal está aberto */}
        <div className="irrigation-calculation-panel" style={{
          minHeight: '100vh',
          background: darkMode ? '#111827' : '#f0f4f8',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          padding: 32,
          filter: 'blur(2px)',
          pointerEvents: 'none'
        }}>
          <div style={{ fontSize: 64 }}>💧</div>
          <h2 style={{ color: darkMode ? '#f1f5f9' : '#0f172a', margin: 0 }}>Manejo de Irrigação</h2>
          <p style={{ color: darkMode ? '#6b7280' : '#94a3b8', margin: 0 }}>Add-on disponível</p>
        </div>
      </>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────
  
  if (propriedades.length === 0) {
    return (
      <div className={`module-shell module-shell-irrigacao ${darkMode ? 'is-dark' : ''}`} style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)',
        paddingLeft: 'calc(env(safe-area-inset-left, 0px) + 20px)',
        paddingRight: 'calc(env(safe-area-inset-right, 0px) + 20px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
        background: 'transparent',
        minHeight: '100vh'
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'transparent',
            border: 'none',
            color: darkMode ? '#64b5f6' : '#2196f3',
            fontSize: 16,
            cursor: 'pointer',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          ← Voltar
        </button>
        
        <div className="irrigation-setup-notice" style={{
          textAlign: 'center',
          padding: 40,
          maxWidth: 600,
          margin: '0 auto'
        }}>
          <div style={{ fontSize: 48, marginBottom: 20 }}>💧</div>
          <h2 style={{ 
            color: darkMode ? '#fff' : '#333',
            marginBottom: 16 
          }}>
            Sistema de Manejo de Irrigação
          </h2>
          <p style={{ 
            color: darkMode ? '#aaa' : '#666',
            fontSize: 16,
            lineHeight: 1.6,
            marginBottom: 24
          }}>
            Para usar o sistema de irrigação, você precisa primeiro cadastrar uma propriedade na aba "Propriedades".
          </p>
          <button
            onClick={onBack}
            style={{
              background: 'linear-gradient(135deg, #2196f3, #1976d2)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Ir para Propriedades
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`module-shell module-shell-irrigacao ${darkMode ? 'is-dark' : ''}`} style={{
      paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingRight: `calc(env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'})`,
      paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${isMobile ? '12px' : '24px'})`,
      minHeight: '100vh',
      fontFamily: 'var(--font-body)'
    }}>
      {/* Header */}
      <div className="module-topbar irrigation-topbar" style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        marginBottom: 16,
        marginLeft: `calc(-1 * (env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'}))`,
        marginRight: `calc(-1 * (env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'}))`,
        paddingLeft: `calc(env(safe-area-inset-left, 0px) + ${isMobile ? '12px' : '24px'})`,
        paddingRight: `calc(env(safe-area-inset-right, 0px) + ${isMobile ? '12px' : '24px'})`,
        paddingTop: isMobile ? 10 : 14,
        paddingBottom: isMobile ? 10 : 14,
      }}>
        <div className="irrigation-page-header" style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 10 : 14
        }}>
          {/* Back button */}
          <button
            onClick={onBack}
            className="irrigation-back-button"
            aria-label="Voltar ao painel principal"
            style={{
              flexShrink: 0,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              color: darkMode ? '#60a5fa' : '#0284c7',
              fontSize: isMobile ? 13 : 14,
              fontWeight: 600,
              borderRadius: 6,
              transition: 'opacity 0.15s',
              whiteSpace: 'nowrap'
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <FaArrowLeft aria-hidden="true" />
            {!isMobile && <span>Voltar</span>}
          </button>

          {/* Divider */}
          <div className="irrigation-header-divider" style={{
            width: 1,
            height: isMobile ? 28 : 32,
            background: darkMode ? '#374151' : '#e2e8f0',
            flexShrink: 0
          }} />

          {/* Icon + title */}
          <div className="irrigation-page-identity" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 12, minWidth: 0 }}>
            <div className="irrigation-page-icon" style={{
              width: isMobile ? 34 : 40,
              height: isMobile ? 34 : 40,
              borderRadius: 11,
              background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isMobile ? 16 : 20,
              boxShadow: '0 3px 10px rgba(14,165,233,0.35)',
              flexShrink: 0
            }}><FaTint aria-hidden="true" /></div>
            <div className="irrigation-page-copy" style={{ minWidth: 0 }}>
              <span className="irrigation-page-eyebrow">Inteligência hídrica</span>
              <h1 className="module-title irrigation-page-title" style={{
                fontSize: isMobile ? 16 : 20,
                fontWeight: 700,
                color: darkMode ? '#f1f5f9' : '#0f172a',
                margin: 0,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                Manejo de Irrigação
              </h1>
              <p className="irrigation-page-subtitle" style={{
                margin: 0,
                fontSize: 11,
                color: darkMode ? '#6b7280' : '#94a3b8',
                marginTop: 2,
                whiteSpace: 'nowrap'
              }}>
                Balanço hídrico por propriedade
              </p>
              {/* Badge trial */}
              {addon.status === 'trial' && addon.diasRestantes !== null && (
                <span className="irrigation-trial-badge" style={{
                  display: 'inline-block',
                  marginTop: 4,
                  background: '#fef3c7',
                  color: '#92400e',
                  border: '1px solid #fde68a',
                  borderRadius: 8,
                  padding: '2px 8px',
                  fontSize: 10,
                  fontWeight: 700
                }}>
                  Trial · {addon.diasRestantes} dias restantes
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Seletor de Propriedade */}
      <DisclaimerInfo tipo="irrigacao" />
      <div className="irrigation-property-switcher" style={{
        background: darkMode ? '#1f2937' : '#fff',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 24,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        border: darkMode ? '1px solid #374151' : '1px solid #e2e8f0'
      }}>
        <div className="irrigation-switcher-heading">
          <span><FaMapMarkerAlt aria-hidden="true" /></span>
          <div><small>Área monitorada</small><h2>{propriedadeSelecionada ? propriedadeSelecionada.nome : 'Selecione uma propriedade'}</h2></div>
          {propriedadeSelecionada && <b>Em análise</b>}
        </div>
        <label style={{
          display: 'block',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: darkMode ? '#6b7280' : '#94a3b8',
          marginBottom: 8
        }}>
          Trocar propriedade
        </label>
        <select
          value={propriedadeSelecionada?.id || ''}
          onChange={(e) => {
            if (!e.target.value) { setPropriedadeSelecionada(null); return; }
            const prop = propriedades.find(p => p.id === e.target.value);
            setPropriedadeSelecionada(prop);
          }}
          style={{
            width: '100%',
            padding: '10px 14px',
            fontSize: 15,
            fontWeight: 500,
            borderRadius: 8,
            border: darkMode ? '1px solid #374151' : '1px solid #e2e8f0',
            background: darkMode ? '#111827' : '#f8fafc',
            color: propriedadeSelecionada ? (darkMode ? '#f1f5f9' : '#1e293b') : (darkMode ? '#6b7280' : '#94a3b8'),
            cursor: 'pointer',
            outline: 'none',
            appearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%2394a3b8' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 14px center',
            paddingRight: 36
          }}
        >
          <option value="">Selecione uma propriedade...</option>
          {propriedades.map(prop => (
            <option key={prop.id} value={prop.id}>
              {prop.nome} — {prop.municipio}/{prop.estado}
            </option>
          ))}
        </select>
      </div>

      {/* Estado: nenhuma propriedade selecionada */}
      {!propriedadeSelecionada && (
        <section className="irrigation-property-onboarding" style={{
          background: darkMode ? '#1f2937' : '#fff',
          borderRadius: 16,
          padding: '32px 24px',
          border: darkMode ? '1px solid #374151' : '1px solid #e2e8f0',
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
          {propriedades.length === 0 ? (
            <div className="irrigation-property-empty" style={{ textAlign: 'center', padding: '16px 0' }}>
              <div><FaSeedling aria-hidden="true" /></div>
              <p style={{ fontSize: 14, color: darkMode ? '#6b7280' : '#94a3b8', margin: 0 }}>
                Nenhuma propriedade cadastrada.
              </p>
            </div>
          ) : (
            <>
              <div className="irrigation-onboarding-heading">
                <div><span>Comece por aqui</span><h2>Escolha a área para analisar</h2><p>O balanço hídrico e as recomendações serão carregados para a propriedade selecionada.</p></div>
                <b>{propriedades.length} propriedade{propriedades.length !== 1 ? 's' : ''}</b>
              </div>
              <div className="irrigation-property-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                {propriedades.map(prop => (
                  <button
                    className="irrigation-property-card"
                    key={prop.id}
                    onClick={() => setPropriedadeSelecionada(prop)}
                    style={{
                      background: darkMode ? '#111827' : '#f8fafc',
                      border: darkMode ? '1px solid #374151' : '1px solid #e2e8f0',
                      borderRadius: 12,
                      padding: '18px 20px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'box-shadow 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 0 0 2px #3b82f6'; e.currentTarget.style.borderColor = '#3b82f6'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = darkMode ? '#374151' : '#e2e8f0'; }}
                  >
                    <span className="irrigation-property-card-icon"><FaHome aria-hidden="true" /></span>
                    <div className="irrigation-property-card-copy" style={{ fontSize: 15, fontWeight: 700, color: darkMode ? '#f1f5f9' : '#1e293b', marginBottom: 4, lineHeight: 1.3 }}>
                      <strong>{prop.nome}</strong>
                      <small><FaMapMarkerAlt aria-hidden="true" /> {prop.municipio}/{prop.estado}</small>
                    </div>
                    <FaChevronRight className="irrigation-property-card-arrow" aria-hidden="true" />
                  </button>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      {/* Conteúdo principal — só exibe quando há propriedade selecionada */}
      {propriedadeSelecionada && (<>

      {/* Navegação por Abas */}
      <div className="tech-tabbar irrigation-tabbar" style={{ flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
        {[
          { id: 'dashboard', label: 'Resumo', icon: <FaChartBar /> },
          { id: 'estacoes', label: 'Estações', icon: <FaBroadcastTower />, badge: estacoes.length },
          { id: 'talhoes', label: 'Talhões', icon: <FaLayerGroup />, badge: talhoes.length },
          // Não conta eventos de talhões já colhidos — mesmo filtro padrão da lista de Eventos
          { id: 'eventos', label: 'Eventos', icon: <FaTint />, badge: eventos.filter(e => !e.talhoes?.data_colheita).length }
        ].map(aba => (
          <button
            className={`tech-tab-btn ${telaAtual === aba.id ? 'is-active' : ''}`}
            key={aba.id}
            onClick={() => {
              setTelaAtual(aba.id);
              if (!previewMode && aba.id === 'talhoes') carregarTalhoes();
            }}
            style={{
              flex: isMobile ? '1 1 48%' : 1,
              fontSize: isMobile ? 12 : 13,
              fontWeight: telaAtual === aba.id ? 700 : 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
              whiteSpace: 'nowrap',
              boxSizing: 'border-box',
              boxShadow: telaAtual === aba.id ? '0 6px 12px rgba(14,165,233,0.26)' : 'none'
            }}
          >
            <span>{aba.icon}</span>
            <span>{aba.label}</span>
            {aba.badge !== undefined && (
              <span style={{
                background: telaAtual === aba.id ? 'rgba(255,255,255,0.25)' : (darkMode ? '#374151' : '#f1f5f9'),
                color: telaAtual === aba.id ? '#fff' : (darkMode ? '#9ca3af' : '#64748b'),
                borderRadius: 10,
                padding: '1px 7px',
                fontSize: 11,
                fontWeight: 700
              }}>
                {aba.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      
      {/* Conteúdo por Aba */}
      {telaAtual === 'dashboard' && (
        <>

      {!previewMode && balancoSnapshot.loading && (
        <IrrigationDecisionHero
          resumo={{}}
          balancos={[]}
          darkMode={darkMode}
          previewMode={false}
          loading
          dataReferencia={null}
          stale={false}
          onAbrirAgenda={() => setTelaAtual('eventos')}
          onAtualizar={() => setModalOpcaoCalculo(true)}
        />
      )}

      {resumoExibicao && !balancoSnapshot.loading && (
        <IrrigationDecisionHero
          resumo={resumoExibicao}
          balancos={previewMode ? previewData?.balancos || [] : balancoSnapshot.items}
          darkMode={darkMode}
          previewMode={previewMode}
          loading={!previewMode && balancoSnapshot.loading}
          dataReferencia={balancoSnapshot.dataReferencia}
          stale={!previewMode && balancoSnapshot.stale}
          onAbrirAgenda={() => setTelaAtual('eventos')}
          onAtualizar={() => setModalOpcaoCalculo(true)}
        />
      )}
      
      {/* Dashboard - Resumo */}
      {resumoExibicao && (
        <div className="tech-panel irrigation-summary-panel" style={{
          borderRadius: 16,
          padding: '20px 24px',
          marginBottom: 16,
          boxShadow: darkMode ? '0 8px 22px rgba(0,0,0,0.2)' : '0 10px 24px rgba(9,53,97,0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            flexWrap: 'wrap',
            gap: 12
          }}>
            <div>
              <h2 style={{
                fontSize: 15,
                fontWeight: 700,
                color: darkMode ? '#f1f5f9' : '#1e293b',
                margin: 0
              }}>
                Panorama da operação
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: 12, color: darkMode ? '#6b7280' : '#94a3b8' }}>
                {irrigationDataStale
                  ? 'Recomendações suspensas até a atualização do balanço'
                  : 'Distribuição dos talhões por nível de atenção'}
              </p>
            </div>
            
            <button
              className="tech-action-btn"
              onClick={() => {
                if (previewMode) {
                  previewAction();
                  return;
                }
                // Se nenhum talhão ativo usa sensor físico, pula o modal (NASA POWER já é a fonte)
                const temSensorFisico = talhoes.some(t => {
                  const cfg = Array.isArray(t.configuracao_irrigacao) ? t.configuracao_irrigacao[0] : t.configuracao_irrigacao;
                  if (!cfg?.manejo_ativo) return false;
                  const est = estacoes.find(e => e.id === cfg?.estacao_id);
                  return est?.tipo_fonte === 'sensor_proprio';
                });
                if (temSensorFisico) {
                  setModalOpcaoCalculo(true);
                } else {
                  processarBalancoHidrico({ fallbackSemEstacao: 'nasapower' });
                }
              }}
              disabled={processandoBalanco}
              style={{
                background: processandoBalanco
                  ? (darkMode ? '#374151' : '#e2e8f0')
                  : 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                color: processandoBalanco ? (darkMode ? '#6b7280' : '#94a3b8') : '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: processandoBalanco ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                boxShadow: processandoBalanco ? 'none' : '0 2px 8px rgba(14,165,233,0.3)'
              }}
            >
              {processandoBalanco ? '⏳ Processando...' : previewMode ? '✓ Dados atualizados' : '🔄 Calcular Balanço'}
            </button>
            {!previewMode && (
              <button
                type="button"
                onClick={() => {
                  if (processandoBalanco) return;
                  const confirmado = window.confirm(
                    'Isso vai reprocessar TODO o histórico de balanço hídrico desde o plantio, ' +
                    'mesmo os dias já calculados. Use após uma mudança na fórmula de cálculo. Continuar?'
                  );
                  if (confirmado) processarBalancoHidrico({ fallbackSemEstacao: 'nasapower', recalcularTudo: true });
                }}
                disabled={processandoBalanco}
                title="Reprocessa todo o histórico, ignorando dias já calculados"
                style={{
                  background: 'transparent',
                  border: `1px solid ${darkMode ? '#374151' : '#cbd5e1'}`,
                  color: processandoBalanco ? (darkMode ? '#6b7280' : '#94a3b8') : (darkMode ? '#94a3b8' : '#475569'),
                  fontSize: 12,
                  fontWeight: 600,
                  borderRadius: 8,
                  padding: '6px 10px',
                  cursor: processandoBalanco ? 'not-allowed' : 'pointer',
                }}
              >
                🔁 Recalcular histórico
              </button>
            )}
          </div>
          
          {irrigationDataStale ? (
            <div
              role="status"
              style={{
                padding: '16px 18px',
                marginBottom: 16,
                borderRadius: 12,
                border: `1px solid ${darkMode ? '#7c5b18' : '#f1c46b'}`,
                background: darkMode ? '#34290f' : '#fff8e7',
                color: darkMode ? '#f8d889' : '#8a5700',
                fontWeight: 700,
                lineHeight: 1.5,
              }}
            >
              Dados antigos não são usados como orientação operacional. Atualize o balanço para liberar urgências, lâminas e janelas de irrigação.
            </div>
          ) : <div className="irrigation-summary-grid" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap: 12,
            marginBottom: 16
          }}>
            <CardResumo
              icon={<FaExclamationTriangle />}
              label="Urgente"
              valor={resumoExibicao.talhoes_urgente || 0}
              cor="#ef4444"
              darkMode={darkMode}
            />
            <CardResumo
              icon={<FaTint />}
              label="Irrigar"
              valor={resumoExibicao.talhoes_irrigar || 0}
              cor="#f59e0b"
              darkMode={darkMode}
            />
            <CardResumo
              icon={<FaClock />}
              label="Aguardar"
              valor={resumoExibicao.talhoes_aguardar || 0}
              cor="#0ea5e9"
              darkMode={darkMode}
            />
            <CardResumo
              icon={<FaShieldAlt />}
              label="OK"
              valor={resumoExibicao.talhoes_ok || 0}
              cor="#22c55e"
              darkMode={darkMode}
            />
          </div>}
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            rowGap: 6,
            padding: '12px 16px',
            background: darkMode ? '#111827' : '#f8fafc',
            borderRadius: 8,
            fontSize: 13,
            color: darkMode ? '#9ca3af' : '#64748b'
          }}>
            <span>📡 Estações ativas: <strong style={{ color: darkMode ? '#f1f5f9' : '#1e293b' }}>{resumoExibicao.estacoes_ativas || 0}</strong></span>
            <span>🌾 Talhões em manejo: <strong style={{ color: darkMode ? '#f1f5f9' : '#1e293b' }}>{resumoExibicao.talhoes_manejo_ativo || 0}</strong> de {resumoExibicao.total_talhoes || 0}</span>
          </div>
        </div>
      )}
      
      {/* Lista de Talhões com Status Hídrico */}
      {(temManejoAtivo || (resumoExibicao?.talhoes_manejo_ativo || 0) > 0) && (
        previewMode ? (
          <ListaTalhoesBalancoPreview
            itens={previewData?.balancos || []}
            darkMode={darkMode}
            onIrrigar={previewAction}
          />
        ) : (
          <ListaTalhoesBalanco
            propriedadeId={propriedadeSelecionada?.id}
            canonicalPlots={talhoes}
            darkMode={darkMode}
            refreshKey={balancoDone}
            onSnapshot={setBalancoSnapshot}
            onIrrigar={(talhao) => {
              setEventoEdicao(null);
              // Pré-preenche o modal de evento com o talhão selecionado
              setEventoEdicao({ talhao_id: talhao.id, _prefill: true });
              setTelaAtual('eventos');
              setModalEvento(true);
            }}
          />
        )
      )}

      {/* Painel de Transparência do Cálculo */}
      {resumoExibicao && resumoExibicao.talhoes_manejo_ativo > 0 && talhoes.some(t =>
        Array.isArray(t.configuracao_irrigacao)
          ? t.configuracao_irrigacao[0]?.manejo_ativo
          : t.configuracao_irrigacao?.manejo_ativo
      ) && (
        <div className="irrigation-no-property-state" style={{
          background: darkMode ? '#1f2937' : '#fff',
          borderRadius: 14,
          padding: '16px 20px',
          marginBottom: 16,
          border: darkMode ? '1px solid #374151' : '1px solid #e2e8f0'
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12
          }}>
            <span style={{ fontSize: 16 }}>🔬</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: darkMode ? '#f1f5f9' : '#1e293b' }}>
              Como o balanço está sendo calculado
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {talhoes
              .filter(t => {
                const cfg = Array.isArray(t.configuracao_irrigacao)
                  ? t.configuracao_irrigacao[0] : t.configuracao_irrigacao;
                return cfg?.manejo_ativo;
              })
              .map(t => {
                const cfg = Array.isArray(t.configuracao_irrigacao)
                  ? t.configuracao_irrigacao[0] : t.configuracao_irrigacao;
                const fonte = cfg?.fonte_dados || 'estacao';
                const estacao = estacoes.find(e => e.id === cfg?.estacao_id);
                const fonteLabel = fonte === 'nasapower'
                  ? '🛰️ NASA POWER'
                  : estacao
                    ? `📡 ${estacao.nome}`
                    : '📡 Estação';
                // Verifica se estação tem sensores de vento/radiação pela estrutura do objeto
                const semVento    = fonte === 'estacao' && estacao && !estacao.tem_vento    && estacao.tem_vento    !== undefined;
                const semRadiacao = fonte === 'estacao' && estacao && !estacao.tem_radiacao && estacao.tem_radiacao !== undefined;
                const estimativas = fonte === 'estacao' ? (semVento || semRadiacao) : false;
                const fonteCor = fonte === 'nasapower'
                  ? (darkMode ? '#818cf8' : '#4f46e5')
                  : (darkMode ? '#34d399' : '#059669');
                return (
                  <div key={t.id} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px',
                    background: darkMode ? '#111827' : '#f8fafc',
                    borderRadius: 8,
                    flexWrap: 'wrap', gap: 8
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: darkMode ? '#f1f5f9' : '#1e293b' }}>
                      🌾 {t.nome}
                    </span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 8px',
                        borderRadius: 6, background: `${fonteCor}20`, color: fonteCor,
                        border: `1px solid ${fonteCor}40`
                      }}>
                        {fonteLabel}
                      </span>
                      <span style={{
                        fontSize: 11, padding: '3px 8px', borderRadius: 6,
                        background: darkMode ? '#374151' : '#f1f5f9',
                        color: darkMode ? '#9ca3af' : '#64748b',
                        border: darkMode ? '1px solid #4b5563' : '1px solid #e2e8f0'
                      }}>
                        ET₀ Penman-Monteith (FAO-56)
                      </span>
                      {estimativas && (
                        <span style={{
                          fontSize: 11, padding: '3px 8px', borderRadius: 6,
                          background: darkMode ? '#78350f20' : '#fef3c720',
                          color: darkMode ? '#fbbf24' : '#b45309',
                          border: `1px solid ${darkMode ? '#78350f' : '#fcd34d'}`
                        }}>
                          ⚠️ {[semVento && 'vento=2m/s', semRadiacao && 'radiação estimada'].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      <span style={{
                        fontSize: 11, padding: '3px 8px', borderRadius: 6,
                        background: darkMode ? '#374151' : '#f1f5f9',
                        color: darkMode ? '#9ca3af' : '#64748b',
                        border: darkMode ? '1px solid #4b5563' : '1px solid #e2e8f0'
                      }}>
                        {(() => {
                          const zrs = [cfg?.zr_inicial||20, cfg?.zr_desenvolvimento||40, cfg?.zr_meio||70, cfg?.zr_final||70];
                          const cadCm = cfg?.cad_por_cm_solo;
                          const cadStr = cadCm
                            ? `CAD ${Math.round(cadCm*Math.min(...zrs))}–${Math.round(cadCm*Math.max(...zrs))} mm/fase`
                            : `CAD ${cfg?.capacidade_agua_disponivel || 75} mm`;
                          const fasesKc = [
                            cfg?.duracao_fase_inicial || 20,
                            cfg?.duracao_fase_desenvolvimento || 35,
                            cfg?.duracao_fase_meio || 40,
                            cfg?.duracao_fase_final || 25,
                          ].join('/');
                          return `Kc ${cfg?.kc_inicial || 0.4}→${cfg?.kc_meio || 1.15}→${cfg?.kc_final || 0.5} · L ${fasesKc}d · ${cadStr}`;
                        })()}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
          <div style={{
            marginTop: 10, padding: '8px 12px',
            background: darkMode ? '#111827' : '#f8fafc',
            borderRadius: 8, fontSize: 11,
            color: darkMode ? '#6b7280' : '#94a3b8', lineHeight: 1.6
          }}>
            <strong>Método:</strong> Balanço Hídrico FAO-56 · ETc = ET₀ × Kc × Kl · curva de Kc único com ajuste climático opcional · armazenamento baseado na CAD configurada.
            {' '}
            <strong>NASA POWER:</strong> dados diários de satélite com resolução ~50km, atraso de 1-2 dias.
            {' '}
            <strong>Estação própria:</strong> dados horários do sensor físico, agregados diariamente.
            {' '}
            <strong>⚠️ Estimativas:</strong> quando a estação não envia radiação solar, ela é estimada pela amplitude térmica (Rs = 0,17·√ΔT·Ra, FAO-56 eq.50). Sem sensor de vento, usa-se 2,0 m/s (padrão FAO).
          </div>
        </div>
      )}
      
      {/* Aviso quando há estação mas sem talhões configurados */}
      {resumo && resumo.estacoes_ativas > 0 && resumo.talhoes_manejo_ativo === 0 && (
        <div style={{
          background: darkMode ? '#1f2937' : '#fff',
          borderRadius: 16,
          padding: 24,
          marginBottom: 16,
          border: `1px solid ${darkMode ? '#92400e' : '#fcd34d'}`,
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: darkMode ? '#422006' : '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, flexShrink: 0
            }}>⚠️</div>
            <div>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: darkMode ? '#f1f5f9' : '#1e293b', margin: '0 0 6px 0' }}>
                Configure seus talhões para iniciar o manejo
              </h3>
              <p style={{ fontSize: 13, color: darkMode ? '#9ca3af' : '#64748b', margin: 0, lineHeight: 1.6 }}>
                Você tem <strong>{resumo.total_talhoes || 0} talhão{resumo.total_talhoes !== 1 ? 'ões' : ''}</strong> cadastrado{resumo.total_talhoes !== 1 ? 's' : ''}.
                Acesse a aba <strong>Talhões</strong>, configure os parâmetros de solo e cultura, e ative o <strong>Manejo Automático</strong>.
              </p>
            </div>
          </div>
          <button
            onClick={() => setTelaAtual('talhoes')}
            style={{
              padding: '9px 20px',
              borderRadius: 8,
              border: 'none',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(245,158,11,0.35)'
            }}
          >
            🌾 Ir para Talhões
          </button>
        </div>
      )}
      
      {/* Introdução inicial (sem estações) */}
      {(!resumo || resumo.estacoes_ativas === 0) && (
        <IntroducaoIrrigacao 
          darkMode={darkMode} 
          onIrEstacoes={() => setTelaAtual('estacoes')}
          onIrTalhoes={() => setTelaAtual('talhoes')}
        />
      )}
        </>
      )}
      
      {/* Tela: Estações */}
      {telaAtual === 'estacoes' && (
        <TelaEstacoes
          darkMode={darkMode}
          propriedade={propriedadeSelecionada}
          estacoes={estacoes}
          sensoresDisponiveis={sensoresDisponiveis}
          onAtualizar={previewMode ? previewAction : carregarEstacoes}
          toast={toast}
          user={user}
          onNovaEstacao={() => {
            if (previewMode) { previewAction(); return; }
            if (!autorizarColaboracao('create')) return;
            setEstacaoEdicao(null);
            setModalEstacao(true);
          }}
          onEditarEstacao={(estacao) => {
            if (previewMode) { previewAction(); return; }
            if (!autorizarColaboracao('edit')) return;
            setEstacaoEdicao(estacao);
            setModalEstacao(true);
          }}
          onExcluirEstacao={previewMode ? previewAction : excluirEstacao}
        />
      )}
      
      {/* Tela: Talhões */}
      {telaAtual === 'talhoes' && (
        <TelaTalhoes
          darkMode={darkMode}
          propriedade={propriedadeSelecionada}
          talhoes={talhoes}
          estacoes={estacoes}
          onAtualizar={previewMode ? previewAction : carregarTalhoes}
          toast={toast}
          onConfigurarTalhao={(talhao) => {
            if (previewMode) { previewAction(); return; }
            if (!autorizarColaboracao('edit')) return;
            if (isFutureLocalDate(talhao.data_plantio)) {
              toast.info('Este talhão estará disponível para manejo a partir da data de plantio.');
              return;
            }
            setTalhaoConfig(talhao);
            setModalConfigTalhao(true);
          }}
        />
      )}
      
      {/* Tela: Eventos */}
      {telaAtual === 'eventos' && (
        <TelaEventos
          darkMode={darkMode}
          propriedade={propriedadeSelecionada}
          eventos={eventos}
          talhoes={talhoes}
          onAtualizar={previewMode ? previewAction : carregarEventos}
          toast={toast}
          onNovoEvento={() => {
            if (previewMode) { previewAction(); return; }
            if (!autorizarColaboracao('create')) return;
            setEventoEdicao(null);
            setModalEvento(true);
          }}
          onEditarEvento={(evento) => {
            if (previewMode) { previewAction(); return; }
            if (!autorizarColaboracao('edit')) return;
            setEventoEdicao(evento);
            setModalEvento(true);
          }}
          onMarcarExecutado={previewMode ? previewAction : marcarEventoExecutado}
          onCancelarEvento={previewMode ? previewAction : cancelarEvento}
          onExcluirEvento={previewMode ? previewAction : excluirEvento}
        />
      )}
      
      {/* Modal: Opções de Cálculo */}
      {modalOpcaoCalculo && (
        <ModalOpcaoCalculo
          darkMode={darkMode}
          onFechar={() => setModalOpcaoCalculo(false)}
          onConfirmar={(opcoes) => processarBalancoHidrico(opcoes)}
        />
      )}

      {/* Modal: Formulário de Estação */}
      {modalEstacao && (
        <div className="irrigation-modal-backdrop" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div className="irrigation-modal-dialog irrigation-modal-dialog-station" style={{
            background: darkMode ? '#1a1a1a' : '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 500,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <FormularioEstacao
              darkMode={darkMode}
              estacao={estacaoEdicao}
              sensores={sensoresDisponiveis}
              propriedade={propriedadeSelecionada}
              onSalvar={salvarEstacao}
              onExcluir={excluirEstacao}
              onCancelar={() => {
                setModalEstacao(false);
                setEstacaoEdicao(null);
              }}
            />
          </div>
        </div>
      )}
      
      {/* Modal: Formulário de Configuração de Talhão */}
      {modalConfigTalhao && talhaoConfig && (
        <div className="irrigation-modal-backdrop" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div className="irrigation-modal-dialog irrigation-modal-dialog-plot" style={{
            background: darkMode ? '#1a1a1a' : '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <FormularioConfigTalhao
              darkMode={darkMode}
              talhao={talhaoConfig}
              config={Array.isArray(talhaoConfig.configuracao_irrigacao)
                ? talhaoConfig.configuracao_irrigacao[0]
                : talhaoConfig.configuracao_irrigacao || null}
              estacoes={estacoes}
              onSalvar={salvarConfiguracao}
              onCancelar={() => {
                setModalConfigTalhao(false);
                setTalhaoConfig(null);
              }}
            />
          </div>
        </div>
      )}
      
      {/* Modal: Formulário de Evento */}
      {modalEvento && (
        <div className="irrigation-modal-backdrop" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20
        }}>
          <div className="irrigation-modal-dialog irrigation-modal-dialog-event" style={{
            background: darkMode ? '#1a1a1a' : '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 600,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <FormularioEvento
              darkMode={darkMode}
              evento={eventoEdicao}
              talhoes={talhoes}
              onSalvar={salvarEvento}
              onCancelar={() => {
                setModalEvento(false);
                setEventoEdicao(null);
              }}
            />
          </div>
        </div>
      )}
      </>)}
    </div>
  );
}

function pivotSettingLabel(setting) {
  if (!setting) return null;
  if (setting.minimumApplication) {
    return `Pivô 100% · mínimo operacional ${setting.estimatedTotalMm.toFixed(1)} mm`;
  }
  if (setting.passes > 1) {
    return `Pivô ${setting.percent}% · ${setting.passes} voltas de ${setting.depthPerPassMm.toFixed(1)} mm`;
  }
  return `Pivô ${setting.percent}% · ${setting.estimatedTotalMm.toFixed(1)} mm por volta`;
}

function IrrigationDecisionHero({ resumo, balancos, previewMode, loading, dataReferencia, stale, onAbrirAgenda, onAtualizar }) {
  const prioritario = getPriorityBalance(balancos);
  const totalAtencao = (resumo.talhoes_urgente || 0) + (resumo.talhoes_irrigar || 0);
  const temAtencao = !stale && totalAtencao > 0;

  if (loading) {
    return (
      <section className="irrigation-decision-hero is-loading" aria-busy="true">
        <div className="irrigation-decision-main">
          <span className="irrigation-eyebrow">Prioridade operacional</span>
          <h2>Atualizando o balanço hídrico</h2>
          <p>Aguarde a conferência das medições antes de tomar uma decisão de irrigação.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={`irrigation-decision-hero ${temAtencao ? 'has-attention' : 'is-stable'}`}>
      <div className="irrigation-decision-main">
        <div className="irrigation-decision-topline">
          <span className="irrigation-eyebrow">{stale ? 'Dados requerem atualização' : 'Prioridade do dia'}</span>
          <span className="irrigation-freshness">
            <span aria-hidden="true" />
            {previewMode ? 'Atualizado há 18 min' : dataReferencia ? `Dados até ${formatLocalDate(dataReferencia)}` : 'Sem balanço calculado'}
          </span>
        </div>
        <h2>
          {stale
            ? 'Atualize os dados antes de decidir'
            : temAtencao
            ? `${totalAtencao} ${totalAtencao === 1 ? 'talhão precisa' : 'talhões precisam'} de irrigação`
            : 'Operação hídrica dentro do planejado'}
        </h2>
        <p>
          {stale
            ? `O último dado confiável é de ${formatLocalDate(dataReferencia)}. Recomendações antigas não devem ser tratadas como orientação para hoje.`
            : prioritario && temAtencao
            ? <>Comece pelo <strong>{prioritario.nome}</strong>. A área está com {prioritario.armazenamentoPercentual}% da água disponível no solo.</>
            : temAtencao
              ? 'Há áreas que exigem atenção. Consulte a lista de talhões para revisar a lâmina e programar o atendimento.'
              : 'Nenhuma intervenção imediata foi identificada. Continue acompanhando a atualização das estações.'}
        </p>
        <button type="button" onClick={stale ? onAtualizar : onAbrirAgenda}>
          {stale ? 'Atualizar balanço hídrico' : 'Abrir agenda de irrigação'}
          <span aria-hidden="true">→</span>
        </button>
      </div>

      <div className="irrigation-decision-metrics" aria-label="Resumo da recomendação prioritária">
        <div>
          <span>Lâmina recomendada</span>
          <strong>{stale ? 'Aguardando atualização' : prioritario?.necessidadeMm ? `${prioritario.necessidadeMm.toFixed(1)} mm` : temAtencao ? 'Ver talhões' : 'Sem aplicação'}</strong>
          <small>{stale ? 'Sem recomendação vigente' : pivotSettingLabel(prioritario?.pivotSetting) || prioritario?.tempoAplicacao || (temAtencao ? 'Detalhes por área' : 'Conforme configuração')}</small>
        </div>
        <div>
          <span>Janela ideal</span>
          <strong>{stale ? 'Não calculada' : prioritario?.janela || (temAtencao ? 'Hoje' : 'Acompanhar')}</strong>
          <small>{stale ? 'Atualize o balanço para recalcular' : temAtencao ? 'Evitar horário de maior vento' : 'Sem urgência operacional'}</small>
        </div>
        <div>
          <span>Cobertura de dados</span>
          <strong>{resumo.estacoes_ativas || 0} ativas</strong>
          <small>{resumo.talhoes_manejo_ativo || 0} talhões monitorados</small>
        </div>
      </div>
    </section>
  );
}

function ListaTalhoesBalancoPreview({ itens, onIrrigar }) {
  const infoStatus = {
    urgente: { label: 'Irrigar agora', icon: '!', action: 'Planejar irrigação' },
    irrigar: { label: 'Irrigar hoje', icon: '↓', action: 'Planejar irrigação' },
    aguardar: { label: 'Acompanhar', icon: '≈', action: 'Ver tendência' },
    ok: { label: 'Solo adequado', icon: '✓', action: 'Ver detalhes' },
  };

  return (
    <section className="irrigation-plots-panel">
      <div className="irrigation-section-heading">
        <div>
          <span className="irrigation-eyebrow">Ordem de atendimento</span>
          <h3>Status hídrico por talhão</h3>
          <p>Recomendações organizadas pela urgência da operação.</p>
        </div>
        <span className="irrigation-section-count">{itens.length} talhões</span>
      </div>

      <div className="irrigation-plot-list">
        {itens.map((item, index) => {
          const status = infoStatus[item.recomendacao] || infoStatus.aguardar;
          const pivotSetting = normalizeBalanceItem(item).pivotSetting;
          return (
            <article className={`irrigation-plot-card status-${item.recomendacao}`} key={item.id}>
              <div className="irrigation-plot-rank" aria-label={`Prioridade ${index + 1}`}>
                {String(index + 1).padStart(2, '0')}
              </div>
              <div className="irrigation-plot-content">
                <div className="irrigation-plot-title-row">
                  <div>
                    <h4>{item.nome}</h4>
                    <p>{item.cultura} · {item.area} ha</p>
                  </div>
                  <span className="irrigation-status-pill">
                    <b aria-hidden="true">{status.icon}</b>
                    {status.label}
                  </span>
                </div>

                <div className="irrigation-water-level">
                  <div>
                    <span>Água disponível no solo</span>
                    <strong>{item.armazenamentoPercentual}%</strong>
                  </div>
                  <div className="irrigation-water-track" aria-label={`${item.armazenamentoPercentual}% de água disponível`}>
                    <span style={{ width: `${Math.max(4, item.armazenamentoPercentual)}%` }} />
                  </div>
                </div>

                <div className="irrigation-plot-facts">
                  <div><span>Lâmina</span><strong>{item.necessidadeMm > 0 ? `${item.necessidadeMm} mm` : '0 mm'}</strong></div>
                  <div><span>Janela</span><strong>{item.janela}</strong></div>
                  <div><span>Fonte</span><strong>{item.fonte}</strong></div>
                  {pivotSetting && (
                    <div className="irrigation-pivot-setting"><span>Ajuste operacional</span><strong>{pivotSettingLabel(pivotSetting)}</strong></div>
                  )}
                </div>
              </div>
              <button type="button" className="irrigation-plot-action" onClick={() => onIrrigar(item)}>
                {status.action}
                <span aria-hidden="true">→</span>
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

// ============================================
// COMPONENTE: FORMULÁRIO DE ESTAÇÃO METEOROLÓGICA
// ============================================
function FormularioEstacao({ darkMode, estacao, sensores, propriedade, onSalvar, onExcluir, onCancelar }) {
  const [nome, setNome] = useState(estacao?.nome || '');
  const [tipoFonte, setTipoFonte] = useState(estacao?.tipo_fonte || 'sensor_proprio');
  const [deviceId, setDeviceId] = useState(estacao?.device_id || '');
  const [codigoEstacao, setCodigoEstacao] = useState(estacao?.codigo_estacao || '');
  const [latitude, setLatitude] = useState(estacao?.latitude || '');
  const [longitude, setLongitude] = useState(estacao?.longitude || '');
  const [altitude, setAltitude] = useState(estacao?.altitude || 800);
  const [raioCobertura, setRaioCobertura] = useState(estacao?.raio_cobertura_km || 5.0);
  const [ativa, setAtiva] = useState(estacao?.ativa ?? true);
  const [showMapa, setShowMapa] = useState(false);
  const [loadingAltitude, setLoadingAltitude] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const isNasaPower = tipoFonte === 'nasapower';
  const sensorSelecionado = sensores.find(sensor => sensor.device_id === deviceId) || null;
  const referenciaPropriedade = propertyReferencePoint(propriedade);
  const perimetrosPropriedade = geometryBoundaryPolygons(propriedade?.geometria);
  const validacaoGeografica = (latitude && longitude)
    ? validateStationLocation(propriedade, { latitude, longitude })
    : null;

  const preencherComReferenciaPropriedade = () => {
    if (!referenciaPropriedade) return false;
    setLatitude(referenciaPropriedade.lat.toFixed(6));
    setLongitude(referenciaPropriedade.lng.toFixed(6));
    return true;
  };

  const handleTipoFonteChange = (event) => {
    const proximoTipo = event.target.value;
    setTipoFonte(proximoTipo);
    if (proximoTipo === 'nasapower') preencherComReferenciaPropriedade();
  };

  useEffect(() => {
    if (tipoFonte !== 'nasapower' || latitude || longitude) return;
    if (!referenciaPropriedade) return;
    setLatitude(referenciaPropriedade.lat.toFixed(6));
    setLongitude(referenciaPropriedade.lng.toFixed(6));
  }, [
    tipoFonte,
    propriedade?.id,
    latitude,
    longitude,
    referenciaPropriedade?.lat,
    referenciaPropriedade?.lng,
  ]);

  useEffect(() => {
    if (tipoFonte !== 'sensor_proprio' || !sensorSelecionado) return;
    const sensorLatitude = sensorSelecionado.latitude ?? sensorSelecionado.lat;
    const sensorLongitude = sensorSelecionado.longitude ?? sensorSelecionado.lon;
    setLatitude(sensorLatitude == null ? '' : String(sensorLatitude));
    setLongitude(sensorLongitude == null ? '' : String(sensorLongitude));
  }, [tipoFonte, sensorSelecionado]);

  const fetchAltitude = async (lat, lon) => {
    setLoadingAltitude(true);
    try {
      const res = await fetch(`https://api.open-elevation.com/api/v1/lookup?locations=${lat},${lon}`);
      const data = await res.json();
      if (data?.results?.[0]?.elevation != null) {
        setAltitude(Math.round(data.results[0].elevation));
      }
    } catch {
      // mantém valor anterior em caso de falha
    } finally {
      setLoadingAltitude(false);
    }
  };

  const handleMapSelect = ({ lat, lng }) => {
    setLatitude(lat.toFixed(6));
    setLongitude(lng.toFixed(6));
    setShowMapa(false);
    fetchAltitude(lat, lng);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (salvando) return; // evita duplo envio
    
    if (!nome.trim()) {
      alert('Nome da estação é obrigatório');
      return;
    }
    
    if (tipoFonte === 'sensor_proprio' && !deviceId) {
      alert('Selecione um sensor');
      return;
    }
    
    if (tipoFonte === 'rede_inmet' && !codigoEstacao) {
      alert('Informe o código da estação INMET');
      return;
    }

    if (!latitude || !longitude) {
      alert(tipoFonte === 'sensor_proprio'
        ? 'O sensor selecionado não possui localização. Atualize o ponto no módulo Sensores antes de vinculá-lo.'
        : 'Informe a latitude e longitude para a estação virtual NASA POWER');
      return;
    }

    const geoCheck = validateStationLocation(propriedade, { latitude, longitude });
    if (!geoCheck.valid) {
      alert(geoCheck.message);
      return;
    }
    
    const dados = {
      nome,
      tipo_fonte: tipoFonte,
      device_id: tipoFonte === 'sensor_proprio' ? deviceId : null,
      codigo_estacao: tipoFonte === 'rede_inmet' || tipoFonte === 'estacao_comercial' ? codigoEstacao : null,
      latitude: latitude ? parseFloat(latitude) : null,
      longitude: longitude ? parseFloat(longitude) : null,
      altitude: altitude ? parseFloat(altitude) : 800,
      raio_cobertura_km: raioCobertura ? parseFloat(raioCobertura) : 5.0,
      ativa
    };
    
    setSalvando(true);
    try {
      await onSalvar(dados);
    } finally {
      setSalvando(false);
    }
  };
  
  return (
    <form className="irrigation-form irrigation-station-form" onSubmit={handleSubmit} style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 16
    }}>
      <h3 style={{
        fontSize: 18,
        fontWeight: 700,
        color: darkMode ? '#fff' : '#333',
        marginBottom: 8
      }}>
        {estacao
          ? (isNasaPower ? '🛰️ Editar Estação Virtual' : '📡 Editar Estação')
          : (isNasaPower ? '🛰️ Nova Estação Virtual (NASA POWER)' : '📡 Nova Estação Meteorológica')}
      </h3>
      
      <div>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#aaa' : '#666',
          marginBottom: 4
        }}>
          Nome da Estação *
        </label>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Ex: Estação Principal"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            fontSize: 14,
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? '#fff' : '#333',
            boxSizing: 'border-box'
          }}
        />
      </div>
      
      <div>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#aaa' : '#666',
          marginBottom: 4
        }}>
          Fonte de Dados *
        </label>
        <select
          value={tipoFonte}
          onChange={handleTipoFonteChange}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            fontSize: 14,
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? '#fff' : '#333',
            cursor: 'pointer',
            boxSizing: 'border-box'
          }}
        >
          <option value="sensor_proprio">📱 Sensor Próprio</option>
          <option value="nasapower">🛰️ NASA POWER (Estação Virtual)</option>
        </select>
      </div>
      
      {/* Info card: NASA POWER virtual station */}
      {isNasaPower && (
        <div style={{
          padding: '12px 14px',
          borderRadius: 8,
          background: darkMode ? '#0d001a' : '#faf5ff',
          border: darkMode ? '1px solid #4c1d95' : '1px solid #ddd6fe',
          fontSize: 13,
          color: darkMode ? '#c4b5fd' : '#6d28d9',
          lineHeight: 1.6
        }}>
          <strong>🛰️ Estação Virtual</strong> — sem hardware necessário. O sistema busca automaticamente dados diários do satélite NASA POWER (temperatura, umidade, chuva, vento, radiação) para as coordenadas informadas.
          <br />
          <span style={{ fontSize: 11, opacity: 0.8 }}>Resolução ~50 km · Atraso de 1–2 dias · Cobertura global</span>
        </div>
      )}

      {isNasaPower && referenciaPropriedade && (
        <div className="irrigation-station-location-hint" role="status">
          <span aria-hidden="true">⌖</span>
          <div>
            <strong>Localização iniciada pela propriedade</strong>
            <small>
              As coordenadas usam o centro de {propriedade?.nome || 'sua propriedade'}. Abra o mapa para marcar o ponto exato da estação dentro do perímetro.
            </small>
          </div>
        </div>
      )}

      {tipoFonte === 'sensor_proprio' && (
        <div>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: darkMode ? '#aaa' : '#666',
            marginBottom: 4
          }}>
            Selecionar Sensor *
          </label>
          <select
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              fontSize: 14,
              background: darkMode ? '#2a2a2a' : '#fff',
              color: darkMode ? '#fff' : '#333',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
          >
            <option value="">Selecione...</option>
            {sensores.map(s => (
              <option key={s.device_id} value={s.device_id}>
                {s.nome_sensor || s.device_id}
              </option>
            ))}
          </select>
        </div>
      )}
      
      {(tipoFonte === 'rede_inmet' || tipoFonte === 'estacao_comercial') && (
        <div>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: darkMode ? '#aaa' : '#666',
            marginBottom: 4
          }}>
            Código da Estação *
          </label>
          <input
            type="text"
            value={codigoEstacao}
            onChange={(e) => setCodigoEstacao(e.target.value)}
            placeholder="Ex: A001"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              fontSize: 14,
              background: darkMode ? '#2a2a2a' : '#fff',
              color: darkMode ? '#fff' : '#333',
              boxSizing: 'border-box'
            }}
          />
        </div>
      )}
      
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666' }}>
            Coordenadas <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <button
            type="button"
            onClick={() => setShowMapa(true)}
            disabled={!isNasaPower}
            style={{
              padding: '4px 10px', borderRadius: 5, fontSize: 12, fontWeight: 600,
              border: `1px solid ${darkMode ? '#374151' : '#d1d5db'}`,
              background: darkMode ? '#1f2937' : '#f0f9ff',
              color: darkMode ? '#60a5fa' : '#1976d2',
              cursor: isNasaPower ? 'pointer' : 'not-allowed',
              opacity: isNasaPower ? 1 : 0.72
            }}
          >
            {isNasaPower ? '🗺️ Selecionar no mapa' : '📍 Definido no módulo Sensores'}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: 12,
              color: darkMode ? '#9ca3af' : '#888',
              marginBottom: 3
            }}>
              Latitude <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="number"
              step="0.000001"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              readOnly={tipoFonte === 'sensor_proprio'}
              placeholder="-15.794229"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 6,
                border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                fontSize: 14,
                background: darkMode ? '#2a2a2a' : '#fff',
                color: darkMode ? '#fff' : '#333',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: 'block',
              fontSize: 12,
              color: darkMode ? '#9ca3af' : '#888',
              marginBottom: 3
            }}>
              Longitude <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="number"
              step="0.000001"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              readOnly={tipoFonte === 'sensor_proprio'}
              placeholder="-47.882166"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 6,
                border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                fontSize: 14,
                background: darkMode ? '#2a2a2a' : '#fff',
                color: darkMode ? '#fff' : '#333',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
        {validacaoGeografica && (
          <div role={validacaoGeografica.valid ? 'status' : 'alert'} style={{
            marginTop: 8,
            padding: '9px 11px',
            borderRadius: 7,
            fontSize: 12,
            fontWeight: 600,
            color: validacaoGeografica.valid ? '#166534' : '#b91c1c',
            background: validacaoGeografica.valid ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${validacaoGeografica.valid ? '#bbf7d0' : '#fecaca'}`,
          }}>
            {validacaoGeografica.valid
              ? `✓ Localização compatível com ${propriedade?.nome || 'a propriedade'}`
              : `⚠ ${validacaoGeografica.message}`}
          </div>
        )}
      </div>
      
      <div>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#aaa' : '#666',
          marginBottom: 4
        }}>
          Altitude (m){loadingAltitude && <span style={{ fontWeight: 400, fontSize: 12, marginLeft: 6, color: '#1976d2' }}>⏳ buscando...</span>}
        </label>
        <input
          type="number"
          step="0.01"
          value={altitude}
          onChange={(e) => setAltitude(e.target.value)}
          disabled={loadingAltitude}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            fontSize: 14,
            background: loadingAltitude ? (darkMode ? '#1a1a1a' : '#f5f5f5') : (darkMode ? '#2a2a2a' : '#fff'),
            color: darkMode ? '#fff' : '#333',
            boxSizing: 'border-box',
            opacity: loadingAltitude ? 0.6 : 1
          }}
        />
      </div>
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 16,
        borderRadius: 6,
        background: darkMode ? '#1a1a1a' : '#f5f5f5'
      }}>
        <input
          type="checkbox"
          id="ativa"
          checked={ativa}
          onChange={(e) => setAtiva(e.target.checked)}
          style={{ cursor: 'pointer' }}
        />
        <label
          htmlFor="ativa"
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: darkMode ? '#aaa' : '#666',
            cursor: 'pointer'
          }}
        >
          ✓ Estação Ativa
        </label>
      </div>
      
      {estacao && onExcluir && (
        <button
          type="button"
          onClick={onExcluir}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            border: `1px solid ${darkMode ? '#c62828' : '#ef9a9a'}`,
            background: 'transparent',
            color: darkMode ? '#ef9a9a' : '#c62828',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
            alignSelf: 'flex-start'
          }}
        >
          🗑️ Excluir Estação
        </button>
      )}

      <div style={{
        display: 'flex',
        gap: 12,
        marginTop: 8
      }}>
        <button
          type="button"
          onClick={onCancelar}
          style={{
            flex: 1,
            padding: '12px 20px',
            borderRadius: 8,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            background: 'transparent',
            color: darkMode ? '#aaa' : '#666',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={salvando}
          style={{
            flex: 1,
            padding: '12px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#4caf50',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: salvando ? 'not-allowed' : 'pointer',
            opacity: salvando ? 0.7 : 1
          }}
        >
          {salvando ? 'Salvando...' : (estacao ? 'Salvar Alterações' : 'Cadastrar Estação')}
        </button>
      </div>

      {showMapa && (
        <MapSelectModal
          darkMode={darkMode}
          title={`Localizar estação em ${propriedade?.nome || 'sua propriedade'}`}
          description={perimetrosPropriedade.length
            ? 'O mapa foi enquadrado nos limites da propriedade. Toque no ponto exato onde a estação virtual deve representar o clima.'
            : 'A propriedade ainda não possui perímetro desenhado. Use a referência disponível ou busque uma cidade e marque o ponto desejado.'}
          initialCenter={Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude))
            ? { lat: Number(latitude), lon: Number(longitude) }
            : (referenciaPropriedade
              ? { lat: referenciaPropriedade.lat, lon: referenciaPropriedade.lng }
              : null)}
          initialZoom={16}
          boundaryPolygons={perimetrosPropriedade}
          boundaryLabel={propriedade?.nome || ''}
          onClose={() => setShowMapa(false)}
          onSelect={handleMapSelect}
        />
      )}
    </form>
  );
}

// ============================================
// COMPONENTE: FORMULÁRIO DE CONFIGURAÇÃO DE TALHÃO
// ============================================
function FormularioConfigTalhao({ darkMode, talhao, config, estacoes, onSalvar, onCancelar }) {
  const isMobile = useIsMobile();
  const sugestaoKcFao = obterSugestaoKcFao56(talhao.cultura);
  const sugestoesKcFao = listarSugestoesKcFao56();
  const [fonteDados, setFonteDados] = useState(config?.fonte_dados || 'estacao');
  const [estacaoId, setEstacaoId] = useState(config?.estacao_id || '');
  const [sistemaIrrigacao, setSistemaIrrigacao] = useState(config?.sistema_irrigacao || 'gotejamento');
  const [eficienciaSistema, setEficienciaSistema] = useState(config?.eficiencia_sistema || 85);
  const [laminaPivo100Mm, setLaminaPivo100Mm] = useState(config?.lamina_pivo_100_mm ?? '');
  const [percentimetroMinimo, setPercentimetroMinimo] = useState(config?.percentimetro_minimo ?? 10);
  const [areaMolhadaPerc, setAreaMolhadaPerc] = useState(config?.area_molhada_perc ?? 100);
  const [areaSombreadaPerc, setAreaSombreadaPerc] = useState(
    config?.area_sombreada_perc ?? config?.area_molhada_perc ?? 100
  );
  // Abordagem CAD: 'simples' (textura + profundidade) ou 'avancada' (θcc, θpmp, Ds)
  const [metodoCad, setMetodoCad] = useState(config?.metodo_cad || 'simples');
  // Abordagem simples
  const [texturaSolo, setTexturaSolo] = useState(config?.textura_solo || 'medio');
  const [profundidadeRaiz, setProfundidadeRaiz] = useState(config?.profundidade_raiz || 60);
  // Abordagem avançada
  const [umidadeCC, setUmidadeCC] = useState(config?.umidade_capacidade_campo ?? '');
  const [umidadePMP, setUmidadePMP] = useState(config?.umidade_ponto_murcha ?? '');
  const [densidadeSolo, setDensidadeSolo] = useState(config?.densidade_solo ?? 1.3);
  // CAD manual (fallback) ou calculada
  const [cad, setCad] = useState(config?.capacidade_agua_disponivel || 75);
  const [fatorDisponibilidade, setFatorDisponibilidade] = useState(config?.fator_disponibilidade || 0.5);
  const [ajustarFadEtc, setAjustarFadEtc] = useState(config?.ajustar_fad_etc ?? true);

  // CAD/cm por textura (valores típicos FAO)
  const CAD_POR_TEXTURA = {
    arenoso:  { label: 'Arenoso',          cadCm: 0.9,  faixa: '0.7–1.1 mm/cm' },
    medio:    { label: 'Médio (Franco)',    cadCm: 1.5,  faixa: '1.2–1.8 mm/cm' },
    argiloso: { label: 'Argiloso',          cadCm: 1.9,  faixa: '1.5–2.3 mm/cm' },
    manual:   { label: 'Informar CAD manual', cadCm: null, faixa: '' }
  };

  // Calcula CAD para uma dada profundidade de raiz
  const calcularCADParaZr = (zr) => {
    if (metodoCad === 'avancada') {
      const cc = parseFloat(umidadeCC);
      const pmp = parseFloat(umidadePMP);
      const ds = parseFloat(densidadeSolo);
      if (!isNaN(cc) && !isNaN(pmp) && !isNaN(ds) && !isNaN(zr)) {
        return parseFloat((ds * (cc - pmp) / 100 * zr * 10).toFixed(1));
      }
      return null;
    }
    if (metodoCad === 'simples' && texturaSolo !== 'manual') {
      const cadCm = CAD_POR_TEXTURA[texturaSolo]?.cadCm;
      if (cadCm && !isNaN(zr)) return parseFloat((cadCm * zr).toFixed(1));
      return null;
    }
    return parseFloat(cad); // manual
  };

  const [kcInicial, setKcInicial] = useState(config?.kc_inicial ?? sugestaoKcFao?.kcIni ?? 0.4);
  const [kcMeio, setKcMeio] = useState(config?.kc_meio ?? sugestaoKcFao?.kcMid ?? 1.15);
  const [kcFinal, setKcFinal] = useState(config?.kc_final ?? sugestaoKcFao?.kcEnd ?? 0.5);
  const [duracaoFaseInicial, setDuracaoFaseInicial] = useState(config?.duracao_fase_inicial ?? sugestaoKcFao?.lIni ?? 20);
  const [duracaoFaseDesenvolvimento, setDuracaoFaseDesenvolvimento] = useState(config?.duracao_fase_desenvolvimento ?? sugestaoKcFao?.lDev ?? 35);
  const [duracaoFaseMeio, setDuracaoFaseMeio] = useState(config?.duracao_fase_meio ?? sugestaoKcFao?.lMid ?? 40);
  const [duracaoFaseFinal, setDuracaoFaseFinal] = useState(config?.duracao_fase_final ?? sugestaoKcFao?.lLate ?? 25);
  const [alturaCultura, setAlturaCultura] = useState(config?.altura_cultura_m ?? sugestaoKcFao?.altura ?? 0.5);
  const [ajustarKcClima, setAjustarKcClima] = useState(config?.ajustar_kc_clima ?? true);
  const [kcOrigem, setKcOrigem] = useState(config?.kc_origem || (sugestaoKcFao ? `FAO-56: ${sugestaoKcFao.label}` : 'manual'));
  const [mostrarTabelaKc, setMostrarTabelaKc] = useState(false);
  // Profundidade de raiz (Zr) por fase fenológica — afeta CAD dinamicamente
  const [zrInicial, setZrInicial] = useState(config?.zr_inicial || 20);
  const [zrDesenvolvimento, setZrDesenvolvimento] = useState(config?.zr_desenvolvimento || 40);
  const [zrMeio, setZrMeio] = useState(config?.zr_meio || 70);
  const [zrFinal, setZrFinal] = useState(config?.zr_final || 70);
  const calcularCAD = () => calcularCADParaZr(parseFloat(zrMeio));
  const cadCalculada = calcularCAD();
  const [manejoAtivo, setManejoAtivo] = useState(config?.manejo_ativo || false);
  const [observacoes, setObservacoes] = useState(config?.observacoes || '');
  const [salvando, setSalvando] = useState(false);
  const sistemaLocalizado = ['gotejamento', 'microaspersao'].includes(sistemaIrrigacao);
  const percentualLocalizacao = Math.max(
    Math.min(100, Math.max(1, Number(areaMolhadaPerc) || 100)),
    Math.min(100, Math.max(1, Number(areaSombreadaPerc) || 100))
  );
  const coeficienteLocalizacao = sistemaLocalizado
    ? Math.min(1, 0.1 * Math.sqrt(percentualLocalizacao))
    : 1;

  const aplicarReferenciaKcFao = (referencia) => {
    if (!referencia) return;
    setKcInicial(referencia.kcIni);
    setKcMeio(referencia.kcMid);
    setKcFinal(referencia.kcEnd);
    if (referencia.lIni) setDuracaoFaseInicial(referencia.lIni);
    if (referencia.lDev) setDuracaoFaseDesenvolvimento(referencia.lDev);
    if (referencia.lMid) setDuracaoFaseMeio(referencia.lMid);
    if (referencia.lLate) setDuracaoFaseFinal(referencia.lLate);
    setAlturaCultura(referencia.altura);
    setKcOrigem(`FAO-56: ${referencia.label}`);
    setMostrarTabelaKc(false);
  };

  const aplicarSugestaoKcFao = () => aplicarReferenciaKcFao(sugestaoKcFao);

  const atualizarKcManual = (setter) => (event) => {
    setter(event.target.value);
    setKcOrigem('manual');
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (salvando) return; // evita duplo envio

    if (fonteDados === 'estacao' && !estacaoId) {
      alert('Selecione uma estação meteorológica ou mude a fonte para NASA POWER');
      return;
    }
    if (sistemaIrrigacao === 'pivo_central' && (!Number.isFinite(Number(laminaPivo100Mm)) || Number(laminaPivo100Mm) <= 0)) {
      alert('Informe a lâmina aferida do pivô com o percentímetro em 100%.');
      return;
    }
    
    const cadFinal = cadCalculada ?? parseFloat(cad);

    // cad_por_cm_solo: usado pelo serviço para recalcular CAD dinamicamente por fase (Zr)
    // Só tem sentido quando o método não é manual
    let cadPorCmSolo = null;
    if (metodoCad === 'simples' && texturaSolo !== 'manual') {
      cadPorCmSolo = CAD_POR_TEXTURA[texturaSolo]?.cadCm ?? null;
    } else if (metodoCad === 'avancada') {
      const cc = parseFloat(umidadeCC), pmp = parseFloat(umidadePMP), ds = parseFloat(densidadeSolo);
      if (!isNaN(cc) && !isNaN(pmp) && !isNaN(ds)) {
        // mm/cm = Ds * (θcc - θpmp)/100 * 10
        cadPorCmSolo = parseFloat((ds * (cc - pmp) / 100 * 10).toFixed(3));
      }
    }

    const dados = {
      fonte_dados: fonteDados,
      estacao_id: estacaoId ? parseInt(estacaoId) : null,
      sistema_irrigacao: sistemaIrrigacao,
      eficiencia_sistema: parseFloat(eficienciaSistema),
      lamina_pivo_100_mm: sistemaIrrigacao === 'pivo_central' ? parseFloat(laminaPivo100Mm) : null,
      percentimetro_minimo: sistemaIrrigacao === 'pivo_central' ? parseFloat(percentimetroMinimo) : null,
      area_molhada_perc: sistemaLocalizado ? parseFloat(areaMolhadaPerc) : 100,
      area_sombreada_perc: sistemaLocalizado ? parseFloat(areaSombreadaPerc) : 100,
      // CAD: salva o valor calculado (fase inicial) + parâmetros de origem
      capacidade_agua_disponivel: cadFinal,
      cad_por_cm_solo: cadPorCmSolo,
      metodo_cad: metodoCad,
      textura_solo: texturaSolo,
      profundidade_raiz: parseFloat(profundidadeRaiz),
      umidade_capacidade_campo: umidadeCC !== '' ? parseFloat(umidadeCC) : null,
      umidade_ponto_murcha: umidadePMP !== '' ? parseFloat(umidadePMP) : null,
      densidade_solo: parseFloat(densidadeSolo),
      fator_disponibilidade: parseFloat(fatorDisponibilidade),
      ajustar_fad_etc: ajustarFadEtc,
      kc_inicial: parseFloat(kcInicial),
      kc_meio: parseFloat(kcMeio),
      kc_final: parseFloat(kcFinal),
      duracao_fase_inicial: parseInt(duracaoFaseInicial, 10),
      duracao_fase_desenvolvimento: parseInt(duracaoFaseDesenvolvimento, 10),
      duracao_fase_meio: parseInt(duracaoFaseMeio, 10),
      duracao_fase_final: parseInt(duracaoFaseFinal, 10),
      altura_cultura_m: parseFloat(alturaCultura),
      ajustar_kc_clima: ajustarKcClima,
      kc_origem: kcOrigem,
      zr_inicial: parseFloat(zrInicial),
      zr_desenvolvimento: parseFloat(zrDesenvolvimento),
      zr_meio: parseFloat(zrMeio),
      zr_final: parseFloat(zrFinal),
      manejo_ativo: manejoAtivo,
      observacoes: observacoes.trim() || null
    };
    
    setSalvando(true);
    try {
      await onSalvar(dados);
    } finally {
      setSalvando(false);
    }
  };
  
  return (
    <form className="irrigation-form irrigation-plot-form" onSubmit={handleSubmit} style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      maxHeight: '70vh',
      overflowY: 'auto',
      padding: '0 4px'
    }}>
      <div style={{
        position: 'sticky',
        top: 0,
        background: darkMode ? '#1a1a1a' : '#fff',
        paddingBottom: 12,
        zIndex: 1
      }}>
        <h3 style={{
          fontSize: 18,
          fontWeight: 700,
          color: darkMode ? '#fff' : '#333',
          marginBottom: 4
        }}>
          ⚙️ Configurar Manejo de Irrigação
        </h3>
        <div style={{
          fontSize: 14,
          color: darkMode ? '#aaa' : '#666'
        }}>
          Talhão: <strong>{talhao.nome}</strong> ({talhao.area} ha)
        </div>
      </div>

      {/* Seletor de Fonte de Dados */}
      <div style={{
        background: darkMode ? '#111827' : '#f0f7ff',
        borderRadius: 10,
        padding: 16,
        border: `1px solid ${darkMode ? '#1e3a5f' : '#bfdbfe'}`
      }}>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 700,
          color: darkMode ? '#93c5fd' : '#1d4ed8',
          marginBottom: 10,
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          🌦️ Fonte dos Dados Meteorológicos
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[
            {
              valor: 'estacao',
              titulo: '📡 Estação Própria',
              descricao: 'Usa dados do sensor físico cadastrado. Mais preciso para sua localidade.'
            },
            {
              valor: 'nasapower',
              titulo: '🛰️ NASA POWER / Satélite',
              descricao: 'Dados do satélite NASA para qualquer local do Brasil. Ideal quando não há sensor físico. Atraso de 1-2 dias.'
            }
          ].map(opcao => (
            <label
              key={opcao.valor}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 8,
                border: fonteDados === opcao.valor
                  ? `2px solid ${darkMode ? '#3b82f6' : '#2563eb'}`
                  : `1px solid ${darkMode ? '#374151' : '#d1d5db'}`,
                background: fonteDados === opcao.valor
                  ? (darkMode ? '#1e3a5f' : '#eff6ff')
                  : (darkMode ? '#1f2937' : '#fff'),
                cursor: 'pointer',
                transition: 'all 0.15s'
              }}
            >
              <input
                type="radio"
                name="fonte_dados"
                value={opcao.valor}
                checked={fonteDados === opcao.valor}
                onChange={() => { setFonteDados(opcao.valor); setEstacaoId(''); }}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
              <div>
                <div style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: darkMode ? '#f1f5f9' : '#1e293b',
                  marginBottom: 2
                }}>
                  {opcao.titulo}
                </div>
                <div style={{ fontSize: 12, color: darkMode ? '#9ca3af' : '#64748b', lineHeight: 1.5 }}>
                  {opcao.descricao}
                </div>
              </div>
            </label>
          ))}
        </div>
      </div>
      
      <div>
        {(() => {
          // Filtra estações pelo tipo correspondente à fonte selecionada
          const estacoesVisiveis = fonteDados === 'nasapower'
            ? estacoes.filter(e => e.ativa && e.tipo_fonte === 'nasapower')
            : estacoes.filter(e => e.ativa && e.tipo_fonte === 'sensor_proprio');

          return (
            <>
              <label style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 600,
                color: darkMode ? '#aaa' : '#666',
                marginBottom: 4
              }}>
                {fonteDados === 'nasapower' ? 'Estação Virtual NASA POWER *' : 'Estação Meteorológica *'}
              </label>
              <select
                value={estacaoId}
                onChange={(e) => setEstacaoId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: 6,
                  border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                  fontSize: 14,
                  background: darkMode ? '#2a2a2a' : '#fff',
                  color: darkMode ? '#fff' : '#333',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
              >
                <option value="">
                  {fonteDados === 'nasapower' ? 'Selecione uma estação virtual...' : 'Selecione uma estação...'}
                </option>
                {estacoesVisiveis.map(estacao => (
                  <option key={estacao.id} value={estacao.id}>
                    {fonteDados === 'nasapower' ? '🛰️' : '📡'} {estacao.nome}
                  </option>
                ))}
              </select>
              {estacoesVisiveis.length === 0 && (
                <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 4 }}>
                  {fonteDados === 'nasapower'
                    ? '⚠️ Nenhuma estação virtual cadastrada. Vá em "Estações" e crie uma do tipo NASA POWER.'
                    : '⚠️ Nenhum sensor ativo. Cadastre um sensor na aba "Estações".'}
                </div>
              )}
            </>
          );
        })()}
      </div>
      
      <div>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#aaa' : '#666',
          marginBottom: 4
        }}>
          Sistema de Irrigação
        </label>
        <select
          value={sistemaIrrigacao}
          onChange={(e) => setSistemaIrrigacao(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            fontSize: 14,
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? '#fff' : '#333',
            cursor: 'pointer',
            boxSizing: 'border-box'
          }}
        >
          <option value="gotejamento">💧 Gotejamento</option>
          <option value="aspersao">🌧️ Aspersão</option>
          <option value="pivo_central">🔄 Pivô Central</option>
          <option value="microaspersao">💦 Microaspersão</option>
          <option value="sulcos">➖ Sulcos</option>
          <option value="outro">📋 Outro</option>
        </select>
      </div>

      {sistemaLocalizado && (
        <div style={{
          padding: 16,
          borderRadius: 8,
          background: darkMode ? '#102a2a' : '#ecfeff',
          border: `1px solid ${darkMode ? '#155e75' : '#a5f3fc'}`
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: darkMode ? '#67e8f9' : '#155e75', marginBottom: 4 }}>
            Coeficiente de localização (Kl)
          </div>
          <div style={{ fontSize: 12, color: darkMode ? '#cbd5e1' : '#475569', lineHeight: 1.5, marginBottom: 12 }}>
            Método Keller/Bliesner: P = maior valor entre PAM e PAS; Kl = 0,1 × √P.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: darkMode ? '#cbd5e1' : '#475569', marginBottom: 4 }}>
                Área molhada — PAM (%)
              </label>
              <input type="number" step="0.1" min="1" max="100" value={areaMolhadaPerc}
                onChange={(e) => setAreaMolhadaPerc(e.target.value)} required
                style={{ width: '100%', padding: '9px 10px', borderRadius: 6, border: `1px solid ${darkMode ? '#475569' : '#cbd5e1'}`, fontSize: 14, background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: darkMode ? '#cbd5e1' : '#475569', marginBottom: 4 }}>
                Área sombreada — PAS (%)
              </label>
              <input type="number" step="0.1" min="1" max="100" value={areaSombreadaPerc}
                onChange={(e) => setAreaSombreadaPerc(e.target.value)} required
                style={{ width: '100%', padding: '9px 10px', borderRadius: 6, border: `1px solid ${darkMode ? '#475569' : '#cbd5e1'}`, fontSize: 14, background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ marginTop: 12, padding: '9px 12px', borderRadius: 6, background: darkMode ? '#164e63' : '#cffafe', color: darkMode ? '#cffafe' : '#164e63', fontSize: 12 }}>
            P adotado: <strong>{percentualLocalizacao.toFixed(1)}%</strong> · Kl calculado: <strong>{coeficienteLocalizacao.toFixed(3)}</strong>
          </div>
        </div>
      )}

      {sistemaIrrigacao === 'pivo_central' && (
        <div style={{
          padding: 16,
          borderRadius: 8,
          background: darkMode ? '#172033' : '#eff6ff',
          border: `1px solid ${darkMode ? '#31517d' : '#bfdbfe'}`
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: darkMode ? '#93c5fd' : '#1e40af', marginBottom: 4 }}>
            Calibração operacional do pivô
          </div>
          <div style={{ fontSize: 12, color: darkMode ? '#cbd5e1' : '#475569', lineHeight: 1.5, marginBottom: 12 }}>
            Use preferencialmente a lâmina aferida em campo. Ela será combinada com a lâmina bruta recomendada para calcular o percentímetro.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: darkMode ? '#cbd5e1' : '#475569', marginBottom: 4 }}>
                Lâmina a 100% (mm por volta)
              </label>
              <input
                type="number"
                step="0.1"
                min="0.1"
                value={laminaPivo100Mm}
                onChange={(event) => setLaminaPivo100Mm(event.target.value)}
                placeholder="Ex.: 4,0"
                required
                style={{ width: '100%', padding: '9px 10px', borderRadius: 6, border: `1px solid ${darkMode ? '#475569' : '#cbd5e1'}`, fontSize: 14, background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: darkMode ? '#cbd5e1' : '#475569', marginBottom: 4 }}>
                Percentímetro mínimo (%)
              </label>
              <input
                type="number"
                step="1"
                min="1"
                max="100"
                value={percentimetroMinimo}
                onChange={(event) => setPercentimetroMinimo(event.target.value)}
                required
                style={{ width: '100%', padding: '9px 10px', borderRadius: 6, border: `1px solid ${darkMode ? '#475569' : '#cbd5e1'}`, fontSize: 14, background: darkMode ? '#1f2937' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        </div>
      )}
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>
            💧 Método de cálculo da CAD (Capacidade de Água Disponível)
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[
              { value: 'simples', label: '🌱 Simples (textura do solo)' },
              { value: 'avancada', label: '🔬 Avançado (θcc / θpmp)' },
            ].map(op => (
              <button key={op.value} type="button"
                onClick={() => setMetodoCad(op.value)}
                style={{
                  padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: `2px solid ${metodoCad === op.value ? '#2563eb' : (darkMode ? '#444' : '#ddd')}`,
                  background: metodoCad === op.value ? (darkMode ? '#1e3a5f' : '#dbeafe') : (darkMode ? '#2a2a2a' : '#fff'),
                  color: metodoCad === op.value ? (darkMode ? '#93c5fd' : '#1d4ed8') : (darkMode ? '#ccc' : '#555'),
                  cursor: 'pointer'
                }}
              >{op.label}</button>
            ))}
          </div>
        </div>

        {/* --- ABORDAGEM SIMPLES --- */}
        {metodoCad === 'simples' && (<>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>
              Textura do Solo
            </label>
            <select value={texturaSolo} onChange={(e) => setTexturaSolo(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }}>
              {Object.entries(CAD_POR_TEXTURA).map(([k, v]) => (
                <option key={k} value={k}>{v.label}{v.faixa ? ` — ${v.faixa}` : ''}</option>
              ))}
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 6 }}>
              🌿 Profundidade de Raiz (Zr) por Fase — cm
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[['Inicial', zrInicial, setZrInicial, '15–30 cm'], ['Desenvolvimento', zrDesenvolvimento, setZrDesenvolvimento, '30–60 cm'], ['Meio', zrMeio, setZrMeio, '60–100 cm'], ['Final', zrFinal, setZrFinal, '60–100 cm']].map(([label, val, setter, hint]) => (
                <div key={label}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 3 }}>Zr {label}</label>
                  <input type="number" step="1" min="5" value={val} onChange={(e) => setter(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{hint}</div>
                </div>
              ))}
            </div>
          </div>
          {texturaSolo === 'manual' && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>
                CAD manual (mm)
              </label>
              <input type="number" step="0.1" value={cad} onChange={(e) => setCad(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
            </div>
          )}
          {texturaSolo !== 'manual' && (
            <div style={{ gridColumn: '1 / -1', padding: '10px 14px', borderRadius: 8, background: darkMode ? '#1a2e1a' : '#f0fdf4', border: `1px solid ${darkMode ? '#2d5a2d' : '#86efac'}`, fontSize: 12, color: darkMode ? '#86efac' : '#166534' }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>✅ CAD por fase — {CAD_POR_TEXTURA[texturaSolo]?.cadCm} mm/cm</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 4 }}>
                {[['Inicial', zrInicial], ['Desenv.', zrDesenvolvimento], ['Meio', zrMeio], ['Final', zrFinal]].map(([label, zr]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ opacity: 0.7 }}>{label}</div>
                    <div style={{ fontWeight: 700 }}>{calcularCADParaZr(parseFloat(zr)) ?? '—'} mm</div>
                    <div style={{ opacity: 0.6, fontSize: 11 }}>Zr {zr} cm</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>)}

        {/* --- ABORDAGEM AVANÇADA --- */}
        {metodoCad === 'avancada' && (<>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>
              Umidade na Capacidade de Campo — θcc (%)
            </label>
            <input type="number" step="0.1" min="0" max="100" placeholder="Ex: 32" value={umidadeCC} onChange={(e) => setUmidadeCC(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Solo argiloso ≈ 30–45% | médio ≈ 22–32% | arenoso ≈ 10–18%</div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>
              Umidade no Ponto de Murcha — θpmp (%)
            </label>
            <input type="number" step="0.1" min="0" max="100" placeholder="Ex: 14" value={umidadePMP} onChange={(e) => setUmidadePMP(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Solo argiloso ≈ 15–25% | médio ≈ 10–15% | arenoso ≈ 4–8%</div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>
              Densidade do Solo — Ds (g/cm³)
            </label>
            <input type="number" step="0.01" min="0.5" max="2.0" value={densidadeSolo} onChange={(e) => setDensidadeSolo(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Típico: 1.1–1.5 g/cm³</div>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 6 }}>
              🌿 Profundidade de Raiz (Zr) por Fase — cm
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[['Inicial', zrInicial, setZrInicial, '15–30 cm'], ['Desenvolvimento', zrDesenvolvimento, setZrDesenvolvimento, '30–60 cm'], ['Meio', zrMeio, setZrMeio, '60–100 cm'], ['Final', zrFinal, setZrFinal, '60–100 cm']].map(([label, val, setter, hint]) => (
                <div key={label}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 3 }}>Zr {label}</label>
                  <input type="number" step="1" min="5" value={val} onChange={(e) => setter(e.target.value)}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{hint}</div>
                </div>
              ))}
            </div>
          </div>
          {cadCalculada !== null && (
            <div style={{ gridColumn: '1 / -1', padding: '10px 14px', borderRadius: 8, background: darkMode ? '#1a2e1a' : '#f0fdf4', border: `1px solid ${darkMode ? '#2d5a2d' : '#86efac'}`, fontSize: 12, color: darkMode ? '#86efac' : '#166534' }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>✅ CAD por fase — Ds {densidadeSolo} × (θcc {umidadeCC}% − θpmp {umidadePMP}%)</div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 4 }}>
                {[['Inicial', zrInicial], ['Desenv.', zrDesenvolvimento], ['Meio', zrMeio], ['Final', zrFinal]].map(([label, zr]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <div style={{ opacity: 0.7 }}>{label}</div>
                    <div style={{ fontWeight: 700 }}>{calcularCADParaZr(parseFloat(zr)) ?? '—'} mm</div>
                    <div style={{ opacity: 0.6, fontSize: 11 }}>Zr {zr} cm</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>)}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>
            Eficiência do Sistema (%)
          </label>
          <input type="number" step="0.1" min="0" max="100" value={eficienciaSistema} onChange={(e) => setEficienciaSistema(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>
            Fração de depleção permitida (p)
          </label>
          <input type="number" step="0.01" min="0" max="1" value={fatorDisponibilidade} onChange={(e) => setFatorDisponibilidade(e.target.value)}
            style={{ width: '100%', padding: '10px 12px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>FAO-56: RAW = p × CAD. Típico: 0,4–0,6.</div>
          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 8, padding: '9px 10px', borderRadius: 6, background: darkMode ? '#0f2f2b' : '#ecfdf5', color: darkMode ? '#a7f3d0' : '#166534', fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={ajustarFadEtc} onChange={(event) => setAjustarFadEtc(event.target.checked)} style={{ marginTop: 2 }} />
            <span><strong>Ajustar p pela ETc do dia</strong><br />FAO-56 Tabela 22: p = p base + 0,04 × (5 − ETc), entre 0,1 e 0,8. Aciona a irrigação mais cedo em dias de alta demanda.</span>
          </label>
        </div>
      </div>

      <div style={{
        padding: 16,
        borderRadius: 8,
        background: darkMode ? '#1a1a1a' : '#f0f9ff',
        border: `1px solid ${darkMode ? '#333' : '#bfdbfe'}`
      }}>
        <div style={{
          fontSize: 13,
          fontWeight: 700,
          color: darkMode ? '#fff' : '#1e40af',
          marginBottom: 12
        }}>
          Curva de coeficiente único (Kc) — FAO-56
        </div>

        <div style={{ fontSize: 12, color: darkMode ? '#cbd5e1' : '#475569', lineHeight: 1.5, marginBottom: 12 }}>
          Kc constante nas fases inicial e de meio de ciclo, com interpolação linear no desenvolvimento e na fase final.
        </div>

        {sugestaoKcFao ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 10px', marginBottom: 12, borderRadius: 6, background: darkMode ? '#172554' : '#dbeafe', color: darkMode ? '#bfdbfe' : '#1e40af', fontSize: 12 }}>
            <span>Sugestão: <strong>{sugestaoKcFao.label}</strong></span>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 6 }}>
              <button type="button" onClick={() => setMostrarTabelaKc(value => !value)} aria-expanded={mostrarTabelaKc} style={{ border: `1px solid ${darkMode ? '#60a5fa' : '#2563eb'}`, borderRadius: 6, padding: '6px 9px', background: 'transparent', color: 'inherit', fontWeight: 700, cursor: 'pointer' }}>
                {mostrarTabelaKc ? 'Ocultar tabela' : 'Ver tabela FAO'}
              </button>
              <button type="button" onClick={aplicarSugestaoKcFao} style={{ border: 'none', borderRadius: 6, padding: '6px 9px', background: '#2563eb', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                Aplicar FAO
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 10px', marginBottom: 12, borderRadius: 6, background: darkMode ? '#422006' : '#fef3c7', color: darkMode ? '#fbbf24' : '#92400e', fontSize: 12 }}>
            <span>Sem referência direta para {talhao.cultura || 'esta cultura'}; use calibração local ou consulte outras referências.</span>
            <button type="button" onClick={() => setMostrarTabelaKc(value => !value)} aria-expanded={mostrarTabelaKc} style={{ flexShrink: 0, border: `1px solid ${darkMode ? '#fbbf24' : '#92400e'}`, borderRadius: 6, padding: '6px 9px', background: 'transparent', color: 'inherit', fontWeight: 700, cursor: 'pointer' }}>
              {mostrarTabelaKc ? 'Ocultar tabela' : 'Ver tabela FAO'}
            </button>
          </div>
        )}

        {mostrarTabelaKc && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ overflowX: 'auto', border: `1px solid ${darkMode ? '#334155' : '#cbd5e1'}`, borderRadius: 6 }}>
              <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 11, color: darkMode ? '#e2e8f0' : '#334155' }}>
                <thead style={{ background: darkMode ? '#1e293b' : '#e2e8f0' }}>
                  <tr>
                    {['Cultura / variante', 'Kc ini', 'Kc mid', 'Kc end', 'Lini', 'Ldev', 'Lmid', 'Llate', 'Altura', ''].map(cabecalho => (
                      <th key={cabecalho} scope="col" style={{ padding: '7px 8px', textAlign: cabecalho === 'Cultura / variante' ? 'left' : 'center', whiteSpace: 'nowrap' }}>{cabecalho}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sugestoesKcFao.map(referencia => {
                    const recomendada = referencia.label === sugestaoKcFao?.label;
                    return (
                      <tr key={referencia.label} style={{ background: recomendada ? (darkMode ? '#172554' : '#eff6ff') : (darkMode ? '#0f172a' : '#fff'), borderTop: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}` }}>
                        <th scope="row" style={{ padding: '7px 8px', textAlign: 'left', fontWeight: recomendada ? 700 : 500 }}>{referencia.label}{recomendada ? ' (recomendada)' : ''}</th>
                        {[referencia.kcIni, referencia.kcMid, referencia.kcEnd, referencia.lIni, referencia.lDev, referencia.lMid, referencia.lLate, `${referencia.altura} m`].map((valor, indice) => (
                          <td key={indice} style={{ padding: '7px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>{valor ?? '—'}</td>
                        ))}
                        <td style={{ padding: '5px 8px', textAlign: 'right' }}>
                          <button type="button" onClick={() => aplicarReferenciaKcFao(referencia)} style={{ border: 'none', borderRadius: 5, padding: '5px 8px', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Usar</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 6, color: darkMode ? '#94a3b8' : '#64748b', fontSize: 11 }}>
              As durações são referências gerais e devem ser ajustadas à cultivar, data de plantio e condições locais.
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 12 }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              color: darkMode ? '#aaa' : '#666',
              marginBottom: 4
            }}>
              Kc Inicial
            </label>
            <input
              type="number"
              step="0.01"
              min="0.05"
              max="2"
              value={kcInicial}
              onChange={atualizarKcManual(setKcInicial)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 6,
                border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                fontSize: 14,
                background: darkMode ? '#2a2a2a' : '#fff',
                color: darkMode ? '#fff' : '#333',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              color: darkMode ? '#aaa' : '#666',
              marginBottom: 4
            }}>
              Kc Mid
            </label>
            <input
              type="number"
              step="0.01"
              min="0.05"
              max="2"
              value={kcMeio}
              onChange={atualizarKcManual(setKcMeio)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 6,
                border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                fontSize: 14,
                background: darkMode ? '#2a2a2a' : '#fff',
                color: darkMode ? '#fff' : '#333',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{
              display: 'block',
              fontSize: 12,
              fontWeight: 600,
              color: darkMode ? '#aaa' : '#666',
              marginBottom: 4
            }}>
              Kc End
            </label>
            <input
              type="number"
              step="0.01"
              min="0.05"
              max="2"
              value={kcFinal}
              onChange={atualizarKcManual(setKcFinal)}
              style={{
                width: '100%',
                padding: '8px 10px',
                borderRadius: 6,
                border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                fontSize: 14,
                background: darkMode ? '#2a2a2a' : '#fff',
                color: darkMode ? '#fff' : '#333',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: darkMode ? '#e2e8f0' : '#334155', margin: '14px 0 8px' }}>
          Duração das fases (dias)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {[
            ['Lini', duracaoFaseInicial, setDuracaoFaseInicial],
            ['Ldev', duracaoFaseDesenvolvimento, setDuracaoFaseDesenvolvimento],
            ['Lmid', duracaoFaseMeio, setDuracaoFaseMeio],
            ['Llate', duracaoFaseFinal, setDuracaoFaseFinal],
          ].map(([label, value, setter]) => (
            <div key={label}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>{label}</label>
              <input type="number" step="1" min="1" max="730" value={value} onChange={(event) => setter(event.target.value)} required
                style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: 12, marginTop: 14, alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: darkMode ? '#aaa' : '#666', marginBottom: 4 }}>Altura média no meio do ciclo (m)</label>
            <input type="number" step="0.1" min="0.1" max="10" value={alturaCultura} onChange={(event) => setAlturaCultura(event.target.value)} required
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: `1px solid ${darkMode ? '#444' : '#ddd'}`, fontSize: 14, background: darkMode ? '#2a2a2a' : '#fff', color: darkMode ? '#fff' : '#333', boxSizing: 'border-box' }} />
          </div>
          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 10px', borderRadius: 6, background: darkMode ? '#0f2f2b' : '#ecfdf5', color: darkMode ? '#a7f3d0' : '#166534', fontSize: 12, cursor: 'pointer' }}>
            <input type="checkbox" checked={ajustarKcClima} onChange={(event) => setAjustarKcClima(event.target.checked)} style={{ marginTop: 2 }} />
            <span><strong>Ajustar Kc Mid/End ao clima</strong><br />Equações 62/65 com vento a 2 m e RHmin medida ou estimada pela Eq. 64.</span>
          </label>
        </div>

        <div style={{ marginTop: 10, fontSize: 11, color: darkMode ? '#94a3b8' : '#64748b' }}>
          Origem atual: <strong>{kcOrigem}</strong>. O ajuste não é aplicado ao Kc End quando seu valor base é menor que 0,45.
        </div>
      </div>



      <div>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#aaa' : '#666',
          marginBottom: 4
        }}>
          Observações
        </label>
        <textarea
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          placeholder="Informações adicionais sobre a configuração..."
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            fontSize: 14,
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? '#fff' : '#333',
            resize: 'vertical',
            fontFamily: 'inherit',
            boxSizing: 'border-box'
          }}
        />
      </div>
      
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 16,
        borderRadius: 6,
        background: manejoAtivo 
          ? (darkMode ? '#1a3a1a' : '#e8f5e9')
          : (darkMode ? '#1a1a1a' : '#f5f5f5'),
        border: manejoAtivo 
          ? '2px solid #4caf50'
          : `1px solid ${darkMode ? '#444' : '#ddd'}`
      }}>
        <input
          type="checkbox"
          id="manejoAtivo"
          checked={manejoAtivo}
          onChange={(e) => setManejoAtivo(e.target.checked)}
          style={{ cursor: 'pointer', width: 18, height: 18 }}
        />
        <label
          htmlFor="manejoAtivo"
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: manejoAtivo ? '#4caf50' : (darkMode ? '#aaa' : '#666'),
            cursor: 'pointer'
          }}
        >
          {manejoAtivo ? '✓ Ativar Manejo Automático de Irrigação' : '⏸️ Ativar Manejo Automático de Irrigação'}
        </label>
      </div>
      
      <div style={{
        display: 'flex',
        gap: 12,
        marginTop: 8,
        position: 'sticky',
        bottom: 0,
        background: darkMode ? '#1a1a1a' : '#fff',
        paddingTop: 12
      }}>
        <button
          type="button"
          onClick={onCancelar}
          style={{
            flex: 1,
            padding: '12px 20px',
            borderRadius: 8,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            background: 'transparent',
            color: darkMode ? '#aaa' : '#666',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={salvando}
          style={{
            flex: 1,
            padding: '12px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#4caf50',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: salvando ? 'not-allowed' : 'pointer',
            opacity: salvando ? 0.7 : 1
          }}
        >
          {salvando ? 'Salvando...' : (config ? 'Atualizar Configuração' : 'Salvar Configuração')}
        </button>
      </div>
    </form>
  );
}

// ============================================
// TELA: EVENTOS
// ============================================
function TelaEventos({ darkMode, eventos, talhoes, onNovoEvento, onEditarEvento, onMarcarExecutado, onCancelarEvento, onExcluirEvento, toast }) {
  const isMobile = useIsMobile();
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [mostrarColhidos, setMostrarColhidos] = useState(false);
  
  const eventosFiltrados = eventos.filter(e => {
    // Oculta eventos de talhões colhidos por padrão
    if (!mostrarColhidos && e.talhoes?.data_colheita) return false;
    if (filtroStatus === 'todos') return true;
    if (filtroStatus === 'pendente') return e.status === 'pendente';
    if (filtroStatus === 'executado') return e.status === 'concluido';
    if (filtroStatus === 'cancelado') return e.status === 'cancelado';
    return true;
  });

  const temEventosColhidos = eventos.some(e => e.talhoes?.data_colheita);
  
  const getTalhaoNome = (evento) => {
    // Usa o nome embutido na join (disponível mesmo para talhões colhidos)
    if (evento.talhoes?.nome) return evento.talhoes.nome;
    const talhao = talhoes.find(t => t.id === evento.talhao_id);
    return talhao?.nome || 'Talhão não encontrado';
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ff9800';
      case 'em_andamento': return '#2196f3';
      case 'concluido': return '#4caf50';
      case 'cancelado': return '#f44336';
      default: return '#666';
    }
  };
  
  const getStatusLabel = (status) => {
    switch (status) {
      case 'pendente': return '⏳ Pendente';
      case 'em_andamento': return '▶️ Em Andamento';
      case 'concluido': return '✓ Concluído';
      case 'cancelado': return '✕ Cancelado';
      default: return status;
    }
  };
  
  return (
    <div className="irrigation-management-screen irrigation-events-screen">
      <div className="irrigation-management-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        gap: 12,
        flexWrap: 'wrap'
      }}>
        <h2 className="irrigation-management-title" style={{
          fontSize: 20,
          fontWeight: 700,
          color: darkMode ? '#fff' : '#333',
          margin: 0
        }}>
          <FaTint /> Eventos de Irrigação
        </h2>
        
        <div className="irrigation-management-actions" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
          <select
            className="irrigation-management-select"
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              fontSize: 14,
              background: darkMode ? '#2a2a2a' : '#fff',
              color: darkMode ? '#fff' : '#333',
              cursor: 'pointer',
              flex: isMobile ? 1 : 'none'
            }}
          >
            <option value="todos">📋 Todos</option>
            <option value="pendente">⏳ Pendentes</option>
            <option value="executado">✓ Executados</option>
            <option value="cancelado">✕ Cancelados</option>
          </select>

          {temEventosColhidos && (
            <button
              className="irrigation-secondary-action"
              onClick={() => setMostrarColhidos(v => !v)}
              title={mostrarColhidos ? 'Ocultar talhões colhidos' : 'Exibir talhões colhidos'}
              style={{
                padding: '8px 12px',
                borderRadius: 6,
                border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                fontSize: 13,
                background: mostrarColhidos ? (darkMode ? '#374151' : '#fef3c7') : (darkMode ? '#2a2a2a' : '#fff'),
                color: mostrarColhidos ? (darkMode ? '#fbbf24' : '#b45309') : (darkMode ? '#9ca3af' : '#64748b'),
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
                flex: isMobile ? 1 : 'none'
              }}
            >
              {mostrarColhidos ? '🌾 Colhidos visíveis' : '🌾 Mostrar colhidos'}
            </button>
          )}
          
          <button
            className="irrigation-primary-action"
            onClick={onNovoEvento}
            style={{
              padding: '10px 20px',
              borderRadius: 8,
              border: 'none',
              background: '#4caf50',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              flex: isMobile ? 1 : 'none'
            }}
          >
            + Novo Evento
          </button>
        </div>
      </div>
      
      {eventosFiltrados.length === 0 ? (
        <div className="irrigation-management-empty" style={{
          background: darkMode ? '#2a2a2a' : '#fff',
          borderRadius: 8,
          padding: 40,
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📅</div>
          <h3 style={{ color: darkMode ? '#fff' : '#333', marginBottom: 8 }}>
            Nenhum evento registrado
          </h3>
          <p style={{ color: darkMode ? '#aaa' : '#666', fontSize: 14 }}>
            {!mostrarColhidos && temEventosColhidos
              ? 'Existem eventos de talhões colhidos. Clique em "Mostrar colhidos" para visualizá-los.'
              : 'Clique em "Novo Evento" para registrar uma irrigação planejada ou executada.'}
          </p>
        </div>
      ) : (
        <div className="irrigation-management-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {eventosFiltrados.map(evento => (
            <div
              className="irrigation-management-card irrigation-event-card"
              key={evento.id}
              style={{
                background: darkMode ? '#2a2a2a' : '#fff',
                borderRadius: 8,
                padding: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderLeft: `4px solid ${getStatusColor(evento.status)}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                    <h3 style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: darkMode ? '#fff' : '#333',
                      margin: 0
                    }}>
                      {getTalhaoNome(evento)}
                    </h3>

                    {evento.talhoes?.data_colheita && (
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 600,
                        background: darkMode ? '#451a03' : '#fef3c7',
                        color: darkMode ? '#fbbf24' : '#92400e',
                        border: `1px solid ${darkMode ? '#78350f' : '#fde68a'}`
                      }}>
                        🌾 Colhido
                      </span>
                    )}
                    
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 600,
                      background: getStatusColor(evento.status),
                      color: '#fff'
                    }}>
                      {getStatusLabel(evento.status)}
                    </span>
                    
                    {evento.tipo_evento && (
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: 12,
                        fontSize: 11,
                        fontWeight: 600,
                        background: darkMode ? '#1a1a1a' : '#f5f5f5',
                        color: darkMode ? '#aaa' : '#666'
                      }}>
                        {evento.tipo_evento}
                      </span>
                    )}
                  </div>
                  
                  <div style={{ color: darkMode ? '#aaa' : '#666', fontSize: 14, lineHeight: 1.8 }}>
                    <div>
                      <strong>Data Planejada:</strong> {new Date(evento.data_planejada + 'T12:00:00').toLocaleDateString('pt-BR')}
                    </div>
                    
                    {evento.lamina_planejada_mm && (
                      <div>
                        <strong>Lâmina Planejada:</strong> {evento.lamina_planejada_mm} mm
                        {evento.tempo_planejado_horas && ` (${evento.tempo_planejado_horas}h)`}
                      </div>
                    )}
                    
                    {evento.data_executada && (
                      <div style={{ color: '#4caf50', fontWeight: 600 }}>
                        <strong>✓ Executado em:</strong> {new Date(evento.data_executada + 'T12:00:00').toLocaleDateString('pt-BR')}
                        {evento.hora_inicio && ` ${evento.hora_inicio}`}
                        {evento.lamina_aplicada_mm && ` - ${evento.lamina_aplicada_mm} mm aplicados`}
                      </div>
                    )}
                    
                    {evento.motivo && (
                      <div>
                        <strong>Motivo:</strong> {evento.motivo}
                      </div>
                    )}
                    
                    {evento.observacoes && (
                      <div style={{
                        marginTop: 8,
                        padding: 8,
                        background: darkMode ? '#1a1a1a' : '#f9f9f9',
                        borderRadius: 4,
                        fontSize: 13,
                        fontStyle: 'italic'
                      }}>
                        {evento.observacoes}
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
                  {evento.status === 'pendente' && (
                    <button
                      onClick={() => {
                        const dados = {
                          data_executada: new Date().toISOString().split('T')[0],
                          hora_inicio: new Date().toTimeString().slice(0, 5),
                          hora_fim: null,
                          lamina_aplicada_mm: evento.lamina_planejada_mm,
                          tempo_real_horas: evento.tempo_planejado_horas,
                          observacoes: ''
                        };
                        
                        if (window.confirm('Marcar este evento como executado agora?')) {
                          onMarcarExecutado(evento, dados);
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: 'none',
                        background: '#4caf50',
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      ✓ Executar
                    </button>
                  )}

                  {(evento.status === 'pendente' || evento.status === 'em_andamento') && (
                    <button
                      onClick={() => {
                        if (window.confirm('Cancelar este evento? Esta ação não pode ser desfeita.')) {
                          onCancelarEvento(evento);
                        }
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 6,
                        border: `1px solid ${darkMode ? '#7f1d1d' : '#fca5a5'}`,
                        background: darkMode ? '#1a0a0a' : '#fff5f5',
                        color: darkMode ? '#f87171' : '#dc2626',
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      ✕ Cancelar
                    </button>
                  )}
                  
                  <button
                    onClick={() => onEditarEvento(evento)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                      background: darkMode ? '#1a1a1a' : '#f5f5f5',
                      color: darkMode ? '#aaa' : '#666',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    ✏️ Editar
                  </button>

                  <button
                    onClick={() => {
                      if (window.confirm('Excluir este evento permanentemente? Esta ação não pode ser desfeita.')) {
                        onExcluirEvento(evento);
                      }
                    }}
                    style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      border: `1px solid ${darkMode ? '#7f1d1d' : '#fca5a5'}`,
                      background: darkMode ? '#1a0a0a' : '#fff5f5',
                      color: darkMode ? '#f87171' : '#dc2626',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                    title="Excluir evento"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// COMPONENTE: FORMULÁRIO DE EVENTO
// ============================================
function FormularioEvento({ darkMode, evento, talhoes, onSalvar, onCancelar }) {
  const isMobile = useIsMobile();
  const [talhaoId, setTalhaoId] = useState(evento?.talhao_id || '');
  const [dataPlanejada, setDataPlanejada] = useState(evento?.data_planejada || new Date().toISOString().split('T')[0]);
  const [laminaPlanejada, setLaminaPlanejada] = useState(evento?.lamina_planejada_mm || '');
  const [tempoPlanejado, setTempoPlanejado] = useState(evento?.tempo_planejado_horas || '');
  const [motivo, setMotivo] = useState(evento?.motivo || 'balanco_hidrico');
  const [status, setStatus] = useState(evento?.status || 'pendente');
  const [observacoes, setObservacoes] = useState(evento?.observacoes || '');
  
  // Campos de execução
  const [dataExecutada, setDataExecutada] = useState(evento?.data_executada || '');
  const [horaInicio, setHoraInicio] = useState(evento?.hora_inicio || '');
  const [horaFim, setHoraFim] = useState(evento?.hora_fim || '');
  const [laminaAplicada, setLaminaAplicada] = useState(evento?.lamina_aplicada_mm || '');
  const [tempoReal, setTempoReal] = useState(evento?.tempo_real_horas || '');
  const [salvando, setSalvando] = useState(false);

  const handleStatusChange = (novoStatus) => {
    setStatus(novoStatus);
    if (novoStatus === 'concluido' || novoStatus === 'em_andamento') {
      setDataExecutada(valorAtual => valorAtual || dataPlanejada);
      setLaminaAplicada(valorAtual => valorAtual || laminaPlanejada);
      setTempoReal(valorAtual => valorAtual || tempoPlanejado);
    }
  };

  const handleDataPlanejadaChange = (novaData) => {
    setDataExecutada(valorAtual =>
      (status === 'concluido' || status === 'em_andamento') && (!valorAtual || valorAtual === dataPlanejada)
        ? novaData
        : valorAtual
    );
    setDataPlanejada(novaData);
  };

  const handleLaminaPlanejadaChange = (novaLamina) => {
    setLaminaAplicada(valorAtual =>
      (status === 'concluido' || status === 'em_andamento') && (!valorAtual || String(valorAtual) === String(laminaPlanejada))
        ? novaLamina
        : valorAtual
    );
    setLaminaPlanejada(novaLamina);
  };

  const handleTempoPlanejadoChange = (novoTempo) => {
    setTempoReal(valorAtual =>
      (status === 'concluido' || status === 'em_andamento') && (!valorAtual || String(valorAtual) === String(tempoPlanejado))
        ? novoTempo
        : valorAtual
    );
    setTempoPlanejado(novoTempo);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (salvando) return; // evita duplo envio
    
    if (!talhaoId) {
      alert('Selecione um talhão');
      return;
    }
    
    if (!dataPlanejada) {
      alert('Informe a data planejada');
      return;
    }

    if (status === 'concluido' && !dataExecutada) {
      alert('Informe a data executada');
      return;
    }

    if (status === 'concluido' && !laminaAplicada) {
      alert('Informe a lâmina aplicada');
      return;
    }
    
    const dados = {
      talhao_id: parseInt(talhaoId),
      data_planejada: dataPlanejada,
      lamina_planejada_mm: laminaPlanejada ? parseFloat(laminaPlanejada) : null,
      tempo_planejado_horas: tempoPlanejado ? parseFloat(tempoPlanejado) : null,
      motivo,
      status,
      tipo_evento: status === 'concluido' ? 'executado' : 'planejado',
      observacoes: observacoes.trim() || null,
      data_executada: dataExecutada || null,
      hora_inicio: horaInicio || null,
      hora_fim: horaFim || null,
      lamina_aplicada_mm: laminaAplicada ? parseFloat(laminaAplicada) : null,
      tempo_real_horas: tempoReal ? parseFloat(tempoReal) : null
    };
    
    setSalvando(true);
    try {
      await onSalvar(dados);
    } finally {
      setSalvando(false);
    }
  };
  
  const talhoesVigentes = talhoes.filter(t => !t.data_colheita && !isFutureLocalDate(t.data_plantio));
  
  return (
    <form className="irrigation-form irrigation-event-form" onSubmit={handleSubmit} style={{
      display: 'flex',
      flexDirection: 'column',
      gap: 16
    }}>
      <h3 style={{
        fontSize: 18,
        fontWeight: 700,
        color: darkMode ? '#fff' : '#333',
        marginBottom: 8
      }}>
        {evento ? '✏️ Editar Evento' : '📅 Novo Evento de Irrigação'}
      </h3>
      
      <div>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#aaa' : '#666',
          marginBottom: 4
        }}>
          Talhão *
        </label>
        <select
          value={talhaoId}
          onChange={(e) => setTalhaoId(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            fontSize: 14,
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? '#fff' : '#333',
            cursor: 'pointer',
            boxSizing: 'border-box'
          }}
        >
          <option value="">Selecione um talhão...</option>
          {talhoesVigentes.map(t => (
            <option key={t.id} value={t.id}>
              🌾 {t.nome} ({t.area} ha)
            </option>
          ))}
        </select>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: darkMode ? '#aaa' : '#666',
            marginBottom: 4
          }}>
            Data Planejada *
          </label>
          <input
            type="date"
            value={dataPlanejada}
            onChange={(e) => handleDataPlanejadaChange(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              fontSize: 14,
              background: darkMode ? '#2a2a2a' : '#fff',
              color: darkMode ? '#fff' : '#333',
              boxSizing: 'border-box'
            }}
          />
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: darkMode ? '#aaa' : '#666',
            marginBottom: 4
          }}>
            Status
          </label>
          <select
            value={status}
            onChange={(e) => handleStatusChange(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              fontSize: 14,
              background: darkMode ? '#2a2a2a' : '#fff',
              color: darkMode ? '#fff' : '#333',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
          >
            <option value="pendente">⏳ Pendente</option>
            <option value="em_andamento">▶️ Em Andamento</option>
            <option value="concluido">✓ Concluído</option>
            <option value="cancelado">✕ Cancelado</option>
          </select>
        </div>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: darkMode ? '#aaa' : '#666',
            marginBottom: 4
          }}>
            Lâmina Planejada (mm)
          </label>
          <input
            type="number"
            step="0.1"
            value={laminaPlanejada}
            onChange={(e) => handleLaminaPlanejadaChange(e.target.value)}
            placeholder="Ex: 25"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              fontSize: 14,
              background: darkMode ? '#2a2a2a' : '#fff',
              color: darkMode ? '#fff' : '#333',
              boxSizing: 'border-box'
            }}
          />
        </div>
        
        <div>
          <label style={{
            display: 'block',
            fontSize: 13,
            fontWeight: 600,
            color: darkMode ? '#aaa' : '#666',
            marginBottom: 4
          }}>
            Tempo Planejado (horas)
          </label>
          <input
            type="number"
            step="0.1"
            value={tempoPlanejado}
            onChange={(e) => handleTempoPlanejadoChange(e.target.value)}
            placeholder="Ex: 4"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 6,
              border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
              fontSize: 14,
              background: darkMode ? '#2a2a2a' : '#fff',
              color: darkMode ? '#fff' : '#333',
              boxSizing: 'border-box'
            }}
          />
        </div>
      </div>
      
      <div>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#aaa' : '#666',
          marginBottom: 4
        }}>
          Motivo
        </label>
        <select
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            fontSize: 14,
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? '#fff' : '#333',
            cursor: 'pointer',
            boxSizing: 'border-box'
          }}
        >
          <option value="balanco_hidrico">💧 Balanço Hídrico</option>
          <option value="fase_critica">🌱 Fase Crítica</option>
          <option value="manual">✋ Manual</option>
          <option value="programacao_fixa">📅 Programação Fixa</option>
        </select>
      </div>
      
      {(status === 'concluido' || status === 'em_andamento') && (
        <>
          <hr style={{ border: `1px solid ${darkMode ? '#333' : '#eee'}`, margin: '8px 0' }} />
          
          <div style={{
            padding: 12,
            borderRadius: 6,
            background: darkMode ? '#1a3a1a' : '#e8f5e9',
            border: '1px solid #4caf50'
          }}>
            <div style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#4caf50',
              marginBottom: 2
            }}>
              ✓ Dados de Execução
            </div>
            <div style={{
              fontSize: 11,
              color: darkMode ? '#a7f3d0' : '#3f6f46',
              marginBottom: 12
            }}>
              Preenchidos com os dados planejados. Ajuste somente o que foi diferente na execução.
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 12 }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: darkMode ? '#aaa' : '#666',
                  marginBottom: 4
                }}>
                  Data Executada
                </label>
                <input
                  type="date"
                  value={dataExecutada}
                  onChange={(e) => setDataExecutada(e.target.value)}
                  required={status === 'concluido'}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                    fontSize: 13,
                    background: darkMode ? '#2a2a2a' : '#fff',
                    color: darkMode ? '#fff' : '#333',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: darkMode ? '#aaa' : '#666',
                  marginBottom: 4
                }}>
                  Hora Início
                </label>
                <input
                  type="time"
                  value={horaInicio}
                  onChange={(e) => setHoraInicio(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                    fontSize: 13,
                    background: darkMode ? '#2a2a2a' : '#fff',
                    color: darkMode ? '#fff' : '#333',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: darkMode ? '#aaa' : '#666',
                  marginBottom: 4
                }}>
                  Hora Fim
                </label>
                <input
                  type="time"
                  value={horaFim}
                  onChange={(e) => setHoraFim(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                    fontSize: 13,
                    background: darkMode ? '#2a2a2a' : '#fff',
                    color: darkMode ? '#fff' : '#333',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: darkMode ? '#aaa' : '#666',
                  marginBottom: 4
                }}>
                  Lâmina Aplicada (mm)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={laminaAplicada}
                  onChange={(e) => setLaminaAplicada(e.target.value)}
                  required={status === 'concluido'}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                    fontSize: 13,
                    background: darkMode ? '#2a2a2a' : '#fff',
                    color: darkMode ? '#fff' : '#333',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: darkMode ? '#aaa' : '#666',
                  marginBottom: 4
                }}>
                  Tempo Real (horas)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={tempoReal}
                  onChange={(e) => setTempoReal(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                    fontSize: 13,
                    background: darkMode ? '#2a2a2a' : '#fff',
                    color: darkMode ? '#fff' : '#333',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
      
      <div>
        <label style={{
          display: 'block',
          fontSize: 13,
          fontWeight: 600,
          color: darkMode ? '#aaa' : '#666',
          marginBottom: 4
        }}>
          Observações
        </label>
        <textarea
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          placeholder="Informações adicionais sobre o evento..."
          rows={3}
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 6,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            fontSize: 14,
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? '#fff' : '#333',
            resize: 'vertical',
            fontFamily: 'inherit',
            boxSizing: 'border-box'
          }}
        />
      </div>
      
      <div style={{
        display: 'flex',
        gap: 12,
        marginTop: 8
      }}>
        <button
          type="button"
          onClick={onCancelar}
          style={{
            flex: 1,
            padding: '12px 20px',
            borderRadius: 8,
            border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
            background: 'transparent',
            color: darkMode ? '#aaa' : '#666',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={salvando}
          style={{
            flex: 1,
            padding: '12px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#4caf50',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: salvando ? 'not-allowed' : 'pointer',
            opacity: salvando ? 0.7 : 1
          }}
        >
          {salvando ? 'Salvando...' : (evento ? 'Atualizar Evento' : 'Registrar Evento')}
        </button>
      </div>
    </form>
  );
}

// ============================================
// COMPONENTE: MODAL OPÇÕES DE CÁLCULO
// ============================================
function ModalOpcaoCalculo({ onConfirmar, onFechar, darkMode }) {
  const [fallback, setFallback] = useState('nasapower');

  const bg   = darkMode ? '#1a1a2e' : '#fff';
  const txt  = darkMode ? '#f1f5f9' : '#1e293b';
  const sub  = darkMode ? '#9ca3af' : '#64748b';
  const brd  = darkMode ? '#374151' : '#e2e8f0';
  const card = darkMode ? '#0f172a' : '#f8fafc';

  const opcoes = [
    {
      value: 'nasapower',
      icon: '🛰️',
      titulo: 'Usar NASA POWER como fallback',
      descricao: 'Quando a estação não tiver dados para um dia, preenche automaticamente com dados do NASA POWER (satélite). Ideal para manter o histórico contínuo.',
    },
    {
      value: 'pular',
      icon: '⏭️',
      titulo: 'Pular dias sem dados da estação',
      descricao: 'Usa somente dados da estação própria. Dias sem leitura ficam em branco no histórico. Mais conservador — sem estimativas externas.',
    },
  ];

  return (
    <div
      className="irrigation-modal-backdrop irrigation-calculation-backdrop"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onFechar}
    >
      <div
        className="irrigation-modal-dialog irrigation-calculation-dialog"
        style={{ background: bg, borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, boxShadow: '0 8px 40px rgba(0,0,0,0.35)' }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color: txt, marginBottom: 4 }}>🔄 Calcular Balanço Hídrico</div>
        <p style={{ fontSize: 13, color: sub, marginTop: 0, marginBottom: 20 }}>
          O cálculo será feito desde a <strong>data de plantio</strong> de cada talhão, preenchendo todos os dias sem registro até hoje.
        </p>

        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: sub, marginBottom: 10 }}>
          Quando a estação não tiver dados
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
          {opcoes.map(op => (
            <label
              key={op.value}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px',
                borderRadius: 10, border: `2px solid ${fallback === op.value ? '#0ea5e9' : brd}`,
                background: fallback === op.value ? (darkMode ? '#0c2a3a' : '#e0f2fe') : card,
                cursor: 'pointer', transition: 'border-color 0.15s',
              }}
            >
              <input
                type="radio"
                name="fallback"
                value={op.value}
                checked={fallback === op.value}
                onChange={() => setFallback(op.value)}
                style={{ marginTop: 3, accentColor: '#0ea5e9' }}
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: txt, marginBottom: 3 }}>
                  {op.icon} {op.titulo}
                </div>
                <div style={{ fontSize: 12, color: sub, lineHeight: 1.5 }}>{op.descricao}</div>
              </div>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onFechar}
            style={{ padding: '9px 20px', borderRadius: 8, border: `1px solid ${brd}`, background: 'transparent', color: sub, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            Cancelar
          </button>
          <button
            onClick={() => { onFechar(); onConfirmar({ fallbackSemEstacao: fallback }); }}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #0ea5e9, #0284c7)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(14,165,233,0.3)' }}
          >
            Calcular
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HELPER: STATUS / COR DE RECOMENDAÇÃO
// ============================================
function getRecomendacaoInfo(recomendacao) {
  switch (recomendacao) {
    case 'irrigar_urgente': return { icon: '🚨', label: 'IRRIGAR URGENTE', cor: '#f44336' };
    case 'irrigar':         return { icon: '💧', label: 'Irrigar',         cor: '#ff9800' };
    case 'aguardar_chuva': return { icon: '⏳', label: 'Aguardar Chuva',  cor: '#2196f3' };
    case 'nao_necessario': return { icon: '✓',  label: 'Não Necessário',  cor: '#4caf50' };
    case 'excesso':        return { icon: '💦', label: 'Excesso de Água', cor: '#9c27b0' };
    default:               return { icon: '?',  label: 'Desconhecido',    cor: '#666'    };
  }
}

// ============================================
// COMPONENTE: MODAL HISTÓRICO DO BALANÇO
// ============================================
function TelaHistoricoBalanco({ item, onClose, darkMode, onIrrigar }) {
  const isMobile = useIsMobile();
  const pivotSetting = normalizeBalanceItem(item).pivotSetting;
  const [historico, setHistorico] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [fad, setFad] = useState(50);
  const [loading, setLoading] = useState(true);
  const [dataMinima, setDataMinima] = useState(null);
  const [talhaoExtra, setTalhaoExtra] = useState(null); // data_plantio

  const hoje = new Date();
  const hojeStr = hoje.toISOString().split('T')[0];

  const calcDataInicio = (dias) => {
    const d = new Date(hoje);
    d.setDate(hoje.getDate() - (dias - 1));
    return d.toISOString().split('T')[0];
  };

  const [dataInicioPers, setDataInicioPers] = useState(calcDataInicio(30));
  const [dataFimPers, setDataFimPers] = useState(hojeStr);

  const dataInicio = dataInicioPers;
  const dataFim    = dataFimPers;

  // Busca data mínima e dados extras do talhão
  useEffect(() => {
    const talhaoId = item.talhao?.id;
    if (!talhaoId) return;
    Promise.all([
      supabase.from('balanco_hidrico_diario').select('data').eq('talhao_id', talhaoId).order('data', { ascending: true }).limit(1),
      supabase.from('talhoes').select('data_plantio').eq('id', talhaoId).maybeSingle()
    ]).then(([{ data: minData }, { data: extra }]) => {
      if (minData?.[0]?.data) setDataMinima(minData[0].data);
      if (extra) setTalhaoExtra(extra);
    });
  }, [item]);

  useEffect(() => {
    const talhaoId = item.talhao?.id;
    if (!talhaoId) return;
    setLoading(true);

    Promise.all([
      supabase
        .from('balanco_hidrico_diario')
        .select('data, armazenamento_percentual, et0_dia, etc_dia, precipitacao_mm, irrigacao_mm, recomendacao, deficit_acumulado')
        .eq('talhao_id', talhaoId)
        .gte('data', dataInicio)
        .lte('data', dataFim)
        .order('data', { ascending: true }),
      supabase
        .from('eventos_irrigacao')
        .select('data_executada, lamina_aplicada_mm, motivo')
        .eq('talhao_id', talhaoId)
        .eq('status', 'concluido')
        .gte('data_executada', dataInicio)
        .lte('data_executada', dataFim)
        .order('data_executada', { ascending: false }),
      supabase
        .from('configuracao_irrigacao_talhao')
        .select('fator_disponibilidade')
        .eq('talhao_id', talhaoId)
        .maybeSingle()
    ]).then(([{ data: balanco }, { data: evts }, { data: cfg }]) => {
      setHistorico(balanco || []);
      setEventos(evts || []);
      setFad((1 - (cfg?.fator_disponibilidade ?? 0.5)) * 100);
      setLoading(false);
    });
  }, [item, dataInicio, dataFim]);

  const info = getRecomendacaoInfo(item.recomendacao);

  const [gerandoPdf, setGerandoPdf] = useState(false);

  const datas       = historico.map(d => d.data.substring(5));
  const armazSeries = historico.map(d => d.armazenamento_percentual != null ? parseFloat(parseFloat(d.armazenamento_percentual).toFixed(1)) : null);
  const chuvaSeries = historico.map(d => parseFloat((d.precipitacao_mm || 0).toFixed(1)));
  const irrigSeries = historico.map(d => parseFloat((d.irrigacao_mm   || 0).toFixed(1)));
  const mmMax = Math.ceil(Math.max(...chuvaSeries, ...irrigSeries, 1) * 1.15);

  const totalChuva     = historico.reduce((s, d) => s + (d.precipitacao_mm || 0), 0);
  const totalIrrigacao = historico.reduce((s, d) => s + (d.irrigacao_mm   || 0), 0);
  const totalETc       = historico.reduce((s, d) => s + (d.etc_dia || 0), 0);
  const et0Vals        = historico.filter(d => d.et0_dia > 0);
  const mediaET0       = et0Vals.length ? et0Vals.reduce((s, d) => s + d.et0_dia, 0) / et0Vals.length : 0;

  // ── Insights derivados do histórico já carregado (sem consultas extras) ──
  const saldoPeriodo = totalChuva + totalIrrigacao - totalETc;

  const etcRecente7 = historico.filter(d => d.etc_dia > 0).slice(-7).map(d => d.etc_dia);
  const etcMedia7 = etcRecente7.length ? etcRecente7.reduce((s, v) => s + v, 0) / etcRecente7.length : 0;
  const cadEstimada = item.armazenamento_atual != null && item.armazenamento_percentual
    ? item.armazenamento_atual / (item.armazenamento_percentual / 100)
    : null;
  const fadMm = cadEstimada != null ? cadEstimada * (fad / 100) : null;
  const diasCritico = (item.armazenamento_atual != null && fadMm != null && etcMedia7 > 0)
    ? Math.max(0, Math.floor((item.armazenamento_atual - fadMm) / etcMedia7))
    : item.dias_ate_limite_critico;

  // Tendência: compara armazenamento atual com o de ~7 dias atrás (ou o mais antigo disponível)
  const armazComData = historico.filter(d => d.armazenamento_percentual != null);
  const tendencia = (() => {
    if (armazComData.length < 2) return null;
    const atual = armazComData[armazComData.length - 1];
    const refIdx = Math.max(0, armazComData.length - 8); // ~7 dias atrás
    const referencia = armazComData[refIdx];
    if (atual.data === referencia.data) return null;
    const delta = parseFloat(atual.armazenamento_percentual) - parseFloat(referencia.armazenamento_percentual);
    return { delta: parseFloat(delta.toFixed(1)), dias: Math.round((new Date(atual.data) - new Date(referencia.data)) / 86400000) };
  })();

  const insights = [];
  // A recomendação persistida (item.recomendacao) é a fonte da verdade — já reflete o p
  // ajustado pela ETc do dia. diasCritico é recalculado no cliente com o FAD fixo da
  // configuração e serve só como projeção; nunca deve contradizer o status atual.
  if (item.recomendacao === 'irrigar_urgente') {
    insights.push({ icon: '🚨', cor: '#e74a4a', texto: 'O armazenamento já está abaixo do limite de irrigação (FAD) — recomenda-se irrigar o quanto antes.' });
  } else if (item.recomendacao === 'irrigar') {
    insights.push({ icon: '💧', cor: '#ef9b22', texto: `O armazenamento está abaixo do limite de irrigação (FAD ${Math.round(fad)}%) — programe a irrigação para hoje.` });
  } else if (item.recomendacao === 'aguardar_chuva') {
    insights.push({ icon: '⏳', cor: '#2196f3', texto: 'O armazenamento está abaixo do limite de irrigação, mas há chuva recente — aguarde antes de irrigar.' });
  } else if (diasCritico != null && diasCritico < 999) {
    if (diasCritico <= 3) {
      insights.push({ icon: '⚠️', cor: '#ef9b22', texto: `Faltam cerca de ${diasCritico} dia${diasCritico !== 1 ? 's' : ''} para o solo atingir o limite de irrigação (FAD ${Math.round(fad)}%), no ritmo atual de consumo (ETc médio ${etcMedia7.toFixed(1)} mm/dia).` });
    } else {
      insights.push({ icon: '🗓️', cor: '#168fbd', texto: `No ritmo atual de consumo, o solo deve levar ~${diasCritico} dias para atingir o limite de irrigação (FAD ${Math.round(fad)}%).` });
    }
  }
  if (tendencia) {
    if (Math.abs(tendencia.delta) < 2) {
      insights.push({ icon: '➡️', cor: '#159473', texto: `Armazenamento estável nos últimos ${tendencia.dias} dias (variação de ${tendencia.delta} p.p.).` });
    } else if (tendencia.delta < 0) {
      insights.push({ icon: '📉', cor: '#ef9b22', texto: `Armazenamento caiu ${Math.abs(tendencia.delta)} pontos percentuais nos últimos ${tendencia.dias} dias.` });
    } else {
      insights.push({ icon: '📈', cor: '#159473', texto: `Armazenamento subiu ${tendencia.delta} pontos percentuais nos últimos ${tendencia.dias} dias — provável efeito de chuva ou irrigação recente.` });
    }
  }
  if (historico.length > 0) {
    if (saldoPeriodo < -5) {
      insights.push({ icon: '💧', cor: '#e74a4a', texto: `Saldo hídrico do período negativo (${saldoPeriodo.toFixed(0)} mm): a evapotranspiração superou chuva + irrigação aplicada.` });
    } else if (saldoPeriodo > 5) {
      insights.push({ icon: '💦', cor: '#168fbd', texto: `Saldo hídrico do período positivo (+${saldoPeriodo.toFixed(0)} mm): entradas de água superaram o consumo da cultura.` });
    }
  }
  if (totalIrrigacao === 0 && (item.recomendacao === 'irrigar' || item.recomendacao === 'irrigar_urgente')) {
    insights.push({ icon: '📌', cor: '#ef9b22', texto: 'Nenhuma irrigação foi registrada neste período, apesar da recomendação atual apontar necessidade de irrigar.' });
  }

  const exportarPDF = async () => {
    if (gerandoPdf) return;
    setGerandoPdf(true);
    try {
      const localStr = (d = new Date()) =>
        `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const fmtData = s => s ? s.slice(0,10).split('-').reverse().join('/') : '-';
      const hoje = localStr();
      const periodoLabel = `${fmtData(dataInicio)} a ${fmtData(dataFim)}`;

      // ── Logo ──────────────────────────────────────────────────────────────
      async function getLogoBase64() {
        try {
          const res = await fetch('/ClimoraX_vertical.png');
          const blob = await res.blob();
          return new Promise(resolve => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        } catch { return null; }
      }
      const logoBase64 = await getLogoBase64();

      // ── Dados do talhão (safra, plantio) ─────────────────────────────────
      const talhaoId = item.talhao?.id;
      const { data: talhaoFull } = await supabase
        .from('talhoes')
        .select('cultura, area, data_plantio')
        .eq('id', talhaoId)
        .maybeSingle();
      const talhaoInfo = talhaoFull || item.talhao || {};
      const safraLabel = (() => {
        if (!talhaoInfo.data_plantio) return '-';
        const d = new Date(talhaoInfo.data_plantio);
        const ano = d.getFullYear();
        const mes = d.getMonth() + 1;
        return mes >= 7 ? `${ano}/${ano + 1}` : `${ano - 1}/${ano}`;
      })();

      // ── Configuração do talhão + estação ──────────────────────────────────
      const [{ data: cfgTalhao }, { data: estacaoData }] = await Promise.all([
        supabase
          .from('configuracao_irrigacao_talhao')
          .select('*')
          .eq('talhao_id', talhaoId)
          .maybeSingle(),
        supabase
          .from('configuracao_irrigacao_talhao')
          .select('estacao_id, fonte_dados')
          .eq('talhao_id', talhaoId)
          .maybeSingle()
          .then(async ({ data: cfg }) => {
            if (!cfg?.estacao_id) return { data: null };
            return supabase
              .from('estacoes_irrigacao')
              .select('nome, tipo_fonte, device_id, codigo_estacao, latitude, longitude, altitude')
              .eq('id', cfg.estacao_id)
              .maybeSingle();
          })
      ]);
      const cfg = cfgTalhao;
      const estacao = estacaoData;
      const arm = item.armazenamento_atual;
      const pct = item.armazenamento_percentual;
      const etcMedia = item.etc_media_7d ?? item.etc_dia ?? 0;
      const cadCalc = arm && pct ? arm / (pct / 100) : null;
      const fadMm = cadCalc ? cadCalc * (fad / 100) : null;
      const diasCriticoCalc = (arm && fadMm != null && etcMedia > 0)
        ? Math.max(0, Math.floor((arm - fadMm) / etcMedia))
        : item.dias_ate_limite_critico;

      const statusCores = { irrigar_urgente: '#c62828', irrigar: '#e65100', aguardar_chuva: '#f9a825', nao_necessario: '#2e7d32' };
      const statusCor = statusCores[item.recomendacao] || '#546e7a';
      const relatorioFonte = estacao?.tipo_fonte === 'nasapower' || cfg?.fonte_dados === 'nasapower'
        ? 'Satélite NASA POWER'
        : estacao?.nome
          ? `Estação ${estacao.nome}`
          : 'Fonte meteorológica não identificada';
      const relatorioTendencia = tendencia
        ? Math.abs(tendencia.delta) < 2
          ? `Estável (${tendencia.delta} p.p. em ${tendencia.dias} dias)`
          : tendencia.delta < 0
            ? `Queda de ${Math.abs(tendencia.delta)} p.p. em ${tendencia.dias} dias`
            : `Alta de ${tendencia.delta} p.p. em ${tendencia.dias} dias`
        : 'Sem histórico suficiente';
      const relatorioJanela = diasCriticoCalc === 0
        ? 'Crítico agora'
        : diasCriticoCalc != null && diasCriticoCalc < 999
          ? `Aproximadamente ${diasCriticoCalc} dia${diasCriticoCalc !== 1 ? 's' : ''}`
          : 'Acompanhar';
      const relatorioRecomendacao = item.recomendacao === 'irrigar_urgente'
        ? 'Irrigue este talhão o quanto antes.'
        : item.recomendacao === 'irrigar'
          ? 'Programe a irrigação para hoje.'
          : item.recomendacao === 'aguardar_chuva'
            ? 'Acompanhe a chuva antes de irrigar.'
            : 'Mantenha o monitoramento do talhão.';
      const relatorioJustificativa = pct != null && pct < fad
        ? `O armazenamento está em ${parseFloat(pct).toFixed(0)}%, abaixo do limite FAD de ${Math.round(fad)}%.`
        : `O armazenamento está em ${pct != null ? `${parseFloat(pct).toFixed(0)}%` : 'nível não informado'}, com FAD configurado em ${Math.round(fad)}%.`;

      // ── Chart helpers (mesmos do relatório de talhão) ─────────────────────
      const PAD_L = 30, INNER_L = 2, PAD_T = 4, PAD_B = 4, CHART_H = 72, CHART_W = 480;
      const innerPlotW = CHART_W - PAD_L - INNER_L - 4;
      const innerPlotH = CHART_H - PAD_T - PAD_B;

      function niceAxis(rawMin, rawMax) {
        const range = (rawMax - rawMin) || 2;
        const roughStep = range / 4;
        const mag = Math.pow(10, Math.floor(Math.log10(roughStep || 1)));
        const step = [1, 2, 2.5, 5, 10].map(c => c * mag).find(s => s >= roughStep) || mag * 10;
        const lo = Math.floor(rawMin / step) * step;
        const hi = Math.ceil(rawMax / step) * step;
        const ticks = [];
        for (let v = lo; v <= hi + step * 0.001; v += step) ticks.push(Math.round(v * 10000) / 10000);
        return ticks;
      }

      function buildBarChart(values, color) {
        const positives = values.filter(v => v != null && v > 0);
        if (!positives.length) return [];
        const maxV = Math.max(...positives) * 1.1 || 1;
        const n = values.length;
        const barW = Math.max(2, innerPlotW / n - 1.5);
        const x0 = INNER_L, y0 = PAD_T, y1 = PAD_T + innerPlotH;
        const shapes = [];
        shapes.push({ type: 'rect', x: x0, y: y0, w: innerPlotW, h: innerPlotH, color: '#ffffff', lineColor: '#cfd8e3', lineWidth: 0.5 });
        shapes.push({ type: 'line', x1: x0, y1: y1, x2: x0 + innerPlotW, y2: y1, lineWidth: 0.8, lineColor: '#b0bacc' });
        for (let i = 0; i < n; i++) {
          if (!values[i] || values[i] <= 0) continue;
          const bh = Math.min((values[i] / maxV) * innerPlotH, innerPlotH);
          const bx = x0 + i * innerPlotW / n + (innerPlotW / n - barW) / 2;
          shapes.push({ type: 'rect', x: bx, y: y1 - bh, w: barW, h: bh, color, lineWidth: 0 });
        }
        // Sentinel: estabelece altura exata do canvas
        shapes.push({ type: 'line', x1: 0, y1: CHART_H - 0.5, x2: 0.5, y2: CHART_H - 0.5, lineWidth: 0.01, lineColor: '#ffffff' });
        return [{ canvas: shapes, margin: [0, 0, 0, 0] }];
      }

      function buildLineChart(values, color, fillColor) {
        const valid = values.filter(v => v != null && !isNaN(v));
        if (valid.length < 2) return [];
        const minV = Math.min(...valid), maxV = Math.max(...valid);
        const range = (maxV - minV) || 1;
        const n = values.length;
        const x0 = INNER_L, y0 = PAD_T, y1 = PAD_T + innerPlotH;
        const clampY = y => Math.max(y0, Math.min(y1, y));
        const shapes = [];
        shapes.push({ type: 'rect', x: x0, y: y0, w: innerPlotW, h: innerPlotH, color: '#ffffff', lineColor: '#cfd8e3', lineWidth: 0.5 });
        const pts = [];
        for (let i = 0; i < n; i++) {
          if (values[i] == null || isNaN(values[i])) continue;
          const px = x0 + (n > 1 ? i * innerPlotW / (n - 1) : innerPlotW / 2);
          const py = clampY(y0 + innerPlotH - ((values[i] - minV) / range) * innerPlotH);
          pts.push({ x: px, y: py });
        }
        // Fill via rects por segmento (sem polyline fill — evita overflow do canvas)
        if (fillColor && pts.length >= 2) {
          for (let i = 0; i < pts.length - 1; i++) {
            const lx = pts[i].x;
            const rx = pts[i + 1].x;
            const topY = (pts[i].y + pts[i + 1].y) / 2;
            const w = rx - lx;
            if (w > 0 && topY < y1) shapes.push({ type: 'rect', x: lx, y: topY, w, h: y1 - topY, color: fillColor, lineWidth: 0 });
          }
        }
        shapes.push({ type: 'line', x1: x0, y1: y1, x2: x0 + innerPlotW, y2: y1, lineWidth: 0.8, lineColor: '#b0bacc' });
        if (pts.length >= 2) shapes.push({ type: 'polyline', lineWidth: 1.5, lineColor: color, closePath: false, points: pts });
        // Sentinel: estabelece altura exata do canvas
        shapes.push({ type: 'line', x1: 0, y1: CHART_H - 0.5, x2: 0.5, y2: CHART_H - 0.5, lineWidth: 0.01, lineColor: '#ffffff' });
        return [{ canvas: shapes, margin: [0, 0, 0, 0] }];
      }

      // Gráfico de armazenamento com linha de FAD
      function buildArmazChart(values, fadPct) {
        const clamped = values.map(v => (v != null && !isNaN(v)) ? Math.min(v, 100) : v);
        const valid = clamped.filter(v => v != null && !isNaN(v));
        if (valid.length < 2) return [];
        const rawMin = Math.min(...valid, fadPct - 5);
        const rawMax = 100;
        const range = (rawMax - rawMin) || 1;
        const n = clamped.length;
        const x0 = INNER_L, y0 = PAD_T, y1 = PAD_T + innerPlotH;
        const clampY = y => Math.max(y0, Math.min(y1, y));
        const toY = v => clampY(y0 + innerPlotH - ((v - rawMin) / range) * innerPlotH);
        const shapes = [];
        // fundo
        shapes.push({ type: 'rect', x: x0, y: y0, w: innerPlotW, h: innerPlotH, color: '#f0f9ff', lineColor: '#cfd8e3', lineWidth: 0.5 });
        // zona de stress (abaixo do FAD)
        const fadY = clampY(toY(fadPct));
        if (fadY < y1) {
          shapes.push({ type: 'rect', x: x0, y: fadY, w: innerPlotW, h: y1 - fadY, color: '#fff0f0', lineWidth: 0 });
        }
        // pontos da linha
        const pts = [];
        for (let i = 0; i < n; i++) {
          if (clamped[i] == null || isNaN(clamped[i])) continue;
          const px = x0 + (n > 1 ? i * innerPlotW / (n - 1) : innerPlotW / 2);
          const py = toY(values[i]);
          pts.push({ x: px, y: py });
        }
        // Fill via rects por segmento (sem polyline fill — evita overflow do canvas)
        if (pts.length >= 2) {
          for (let i = 0; i < pts.length - 1; i++) {
            const lx = pts[i].x;
            const rx = pts[i + 1].x;
            const topY = (pts[i].y + pts[i + 1].y) / 2;
            const w = rx - lx;
            if (w > 0 && topY < y1) shapes.push({ type: 'rect', x: lx, y: topY, w, h: y1 - topY, color: '#bbdefb', lineWidth: 0 });
          }
        }
        // linha FAD (sobre o fill)
        shapes.push({ type: 'line', x1: x0, y1: fadY, x2: x0 + innerPlotW, y2: fadY, lineWidth: 1, lineColor: '#ef9a9a', dash: { length: 3, space: 3 } });
        // eixo base
        shapes.push({ type: 'line', x1: x0, y1: y1, x2: x0 + innerPlotW, y2: y1, lineWidth: 0.8, lineColor: '#b0bacc' });
        // linha de dados
        if (pts.length >= 2) shapes.push({ type: 'polyline', lineWidth: 2, lineColor: '#1565c0', closePath: false, points: pts });
        // Sentinel: estabelece altura exata do canvas
        shapes.push({ type: 'line', x1: 0, y1: CHART_H - 0.5, x2: 0.5, y2: CHART_H - 0.5, lineWidth: 0.01, lineColor: '#ffffff' });
        return [{ canvas: shapes, margin: [0, 0, 0, 0] }];
      }

      function xLabels(labels) {
        const nVis = Math.min(labels.length, 8);
        const step = labels.length <= nVis ? 1 : Math.floor((labels.length - 1) / (nVis - 1));
        const items = [];
        for (let i = 0; i < labels.length; i += step) items.push({ text: labels[i], xFrac: i / Math.max(1, labels.length - 1) });
        const W = CHART_W - PAD_L;
        const LW = 28;
        const cols = [];
        let prev = 0;
        for (const it of items) {
          const x = it.xFrac * W;
          const ls = Math.max(prev, x - LW / 2);
          const gap = ls - prev;
          if (gap > 0) cols.push({ text: '', width: gap });
          cols.push({ text: it.text, fontSize: 6, color: '#999', alignment: 'center', width: LW, noWrap: true });
          prev = ls + LW;
        }
        const rem = W - prev;
        if (rem > 0) cols.push({ text: '', width: rem });
        return { columns: cols, margin: [PAD_L, 0, 0, 6], columnGap: 0 };
      }

      function yLabels(ticks, dec = 0) {
        const rev = [...ticks].reverse();
        const n = rev.length;
        const tickGap = innerPlotH / (n - 1);
        const spacer = Math.max(1, PAD_T - 3);
        const heights = [spacer, ...Array(n - 1).fill(tickGap)];
        const body = [
          [{ text: '', border: [false,false,false,false] }],
          ...rev.map(v => [{ text: Number(v).toFixed(dec), fontSize: 6, color: '#777', alignment: 'right', border: [false,false,false,false], margin: [0,0,3,0] }])
        ];
        return {
          width: PAD_L,
          table: { widths: [PAD_L - 3], heights, body },
          layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 }
        };
      }

      function sectionTitle(label, color) {
        return {
          columns: [
            { canvas: [{ type: 'rect', x: 0, y: 0, w: 3, h: 11, color, lineWidth: 0 }], width: 7 },
            { text: label, color: '#102a43', fontSize: 9, bold: true, margin: [0, 1, 0, 0] }
          ],
          margin: [0, 8, 0, 4]
        };
      }

      // ── Dados para gráficos ─────────────────────────────────────────────
      const labelsGraf = historico.map(d => { const p = (d.data||'').split('-'); return p.length===3 ? `${p[2]}/${p[1]}` : d.data; });
      const armVals    = historico.map(d => d.armazenamento_percentual != null ? Math.min(parseFloat(d.armazenamento_percentual), 100) : null);
      const chuvaVals  = historico.map(d => Number(d.precipitacao_mm) || 0);
      const irrigVals  = historico.map(d => Number(d.irrigacao_mm) || 0);
      const etcVals    = historico.map(d => d.etc_dia > 0 ? d.etc_dia : null);

      const armazTicks = niceAxis(Math.max(0, Math.min(...armVals.filter(Boolean), fad) - 5), 100);
      const chuvaTicks = niceAxis(0, Math.max(...chuvaVals, 1));
      const irrigMax   = Math.max(...irrigVals, 1);
      const irrigTicks = niceAxis(0, irrigMax);
      const etcTicks   = niceAxis(0, Math.max(...etcVals.filter(Boolean), 1));

      // ── Tabela diária ───────────────────────────────────────────────────
      const headerTabela = [
        { text: 'Data',       bold: true, fillColor: '#0878a4', color: '#fff', fontSize: 7, alignment: 'center', margin:[2,3,2,3] },
        { text: 'Arm. %',     bold: true, fillColor: '#0878a4', color: '#fff', fontSize: 7, alignment: 'center', margin:[2,3,2,3] },
        { text: 'Chuva mm',   bold: true, fillColor: '#159473', color: '#fff', fontSize: 7, alignment: 'center', margin:[2,3,2,3] },
        { text: 'Irrig. mm',  bold: true, fillColor: '#0ea5b7', color: '#fff', fontSize: 7, alignment: 'center', margin:[2,3,2,3] },
        { text: 'ET₀ mm/d',   bold: true, fillColor: '#d88916', color: '#fff', fontSize: 7, alignment: 'center', margin:[2,3,2,3] },
        { text: 'ETc mm/d',   bold: true, fillColor: '#d88916', color: '#fff', fontSize: 7, alignment: 'center', margin:[2,3,2,3] },
        { text: 'Déficit mm', bold: true, fillColor: '#627d98', color: '#fff', fontSize: 7, alignment: 'center', margin:[2,3,2,3] },
      ];
      const linhasTabela = historico.map((d, i) => {
        const bg = i % 2 === 0 ? '#f7fbff' : '#ffffff';
        const abaFAD = d.armazenamento_percentual != null && d.armazenamento_percentual < fad;
        const bgArm = abaFAD ? '#ffebee' : bg;
        return [
          { text: fmtData(d.data), fontSize: 7, fillColor: bg, alignment: 'center', border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
          { text: d.armazenamento_percentual != null ? `${parseFloat(d.armazenamento_percentual).toFixed(1)}%` : '-', fontSize: 7, fillColor: bgArm, color: abaFAD ? '#c62828' : '#000', bold: abaFAD, alignment: 'center', border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
          { text: (d.precipitacao_mm||0).toFixed(1), fontSize: 7, fillColor: bg, color: '#0288d1', alignment: 'center', border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
          { text: (d.irrigacao_mm||0).toFixed(1),    fontSize: 7, fillColor: bg, color: d.irrigacao_mm > 0 ? '#0277bd' : '#999', bold: d.irrigacao_mm > 0, alignment: 'center', border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
          { text: d.et0_dia > 0 ? d.et0_dia.toFixed(2) : '-', fontSize: 7, fillColor: bg, alignment: 'center', border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
          { text: d.etc_dia > 0 ? d.etc_dia.toFixed(2) : '-', fontSize: 7, fillColor: bg, alignment: 'center', border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
          { text: d.deficit_acumulado > 0 ? d.deficit_acumulado.toFixed(1) : '0', fontSize: 7, fillColor: bg, alignment: 'center', border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
        ];
      });

      // ── Eventos ─────────────────────────────────────────────────────────
      const temEventos = eventos.length > 0;
      const linhasEventos = eventos.map((ev, i) => {
        const bg = i % 2 === 0 ? '#f0f9ff' : '#ffffff';
        return [
          { text: fmtData(ev.data_executada), fontSize: 7, fillColor: bg, border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
          { text: (ev.lamina_aplicada_mm||0).toFixed(1), fontSize: 7, fillColor: bg, color: '#0277bd', bold: true, alignment: 'center', border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
          { text: ev.motivo || '-', fontSize: 7, fillColor: bg, border: [false,false,false,true], borderColor: ['','','','#e8e8e8'], margin:[2,2,2,2] },
        ];
      });

      // ── docDefinition ───────────────────────────────────────────────────
      const docDefinition = {
        pageSize: 'A4',
        pageMargins: [28, 14, 28, 22],
        defaultStyle: { font: 'Roboto' },
        content: [
          // ─ Cabeçalho de marca ─
          {
            table: {
              widths: ['*'],
              body: [[{
                columns: [
                  ...(logoBase64 ? [{ image: logoBase64, width: 46, height: 46, margin: [0, 0, 10, 0] }] : []),
                  {
                    stack: [
                      { text: 'CLIMORAX  ·  INTELIGÊNCIA HÍDRICA', fontSize: 7, bold: true, color: '#0878a4' },
                      { text: 'Relatório de manejo de irrigação', fontSize: 16, bold: true, color: '#102a43', margin: [0, 3, 0, 2] },
                      { text: `${item.talhao?.nome || 'Talhão'}  ·  ${periodoLabel}`, fontSize: 8, color: '#627d98' }
                    ],
                    margin: [0, 3, 0, 0]
                  },
                  { text: `Gerado em\n${new Date().toLocaleString('pt-BR')}`, width: 88, fontSize: 6.5, color: '#627d98', alignment: 'right', margin: [0, 6, 0, 0] }
                ],
                fillColor: '#ffffff',
                color: '#102a43',
                border: [false, false, false, true],
                borderColor: ['', '', '', '#d6e6ee'],
                margin: [10, 8, 10, 8]
              }]]
            },
            layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
            margin: [0, 0, 0, 8]
          },

          // ─ Decisão operacional ─
          sectionTitle('DECISÃO OPERACIONAL', statusCor),
          {
            table: {
              widths: ['64%', '36%'],
              body: [[
                {
                  stack: [
                    { text: info.label, fontSize: 8, bold: true, color: statusCor },
                    { text: relatorioRecomendacao, fontSize: 16, bold: true, color: '#102a43', margin: [0, 4, 0, 4] },
                    { text: relatorioJustificativa, fontSize: 8, color: '#627d98', lineHeight: 1.35 },
                    { text: `Lâmina sugerida: ${item.necessidade_irrigacao?.toFixed(1) || '0.0'} mm`, fontSize: 9, bold: true, color: '#0878a4', margin: [0, 7, 0, 0] },
                    ...(pivotSetting ? [{ text: pivotSettingLabel(pivotSetting), fontSize: 8, bold: true, color: '#1e40af', margin: [0, 3, 0, 0] }] : [])
                  ],
                  fillColor: '#f2f8fc',
                  border: [true, true, false, true],
                  borderColor: [statusCor, '#d6e6ee', '', '#d6e6ee'],
                  margin: [10, 8, 10, 8]
                },
                {
                  stack: [
                    { text: 'CONTEXTO DA DECISÃO', fontSize: 7, bold: true, color: '#159473', margin: [0, 0, 0, 5] },
                    { text: `Janela: ${relatorioJanela}`, fontSize: 8, bold: true, color: '#102a43', margin: [0, 1, 0, 2] },
                    { text: `Tendência: ${relatorioTendencia}`, fontSize: 7, color: '#627d98', margin: [0, 1, 0, 2] },
                    { text: `Fonte: ${relatorioFonte}`, fontSize: 7, color: '#627d98', margin: [0, 1, 0, 2] },
                    { text: `Cobertura: ${historico.length} dias`, fontSize: 7, color: '#627d98' }
                  ],
                  fillColor: '#f2f8fc',
                  border: [false, true, true, true],
                  borderColor: ['', '#d6e6ee', '#d6e6ee', '#d6e6ee'],
                  margin: [10, 8, 10, 8]
                }
              ]]
            },
            layout: { hLineWidth: () => 0.6, vLineWidth: () => 0.6, hLineColor: () => '#d6e6ee', vLineColor: () => '#d6e6ee', paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
            margin: [0, 0, 0, 7]
          },

          // ─ Identificação do talhão ─
          {
            table: {
              widths: ['*', '*', '*', '*'],
              body: [[
                { text: [{ text: 'CULTURA\n', fontSize: 6, color: '#627d98', bold: true }, { text: talhaoInfo.cultura || item.talhao?.cultura || '-', fontSize: 10, color: '#0878a4', bold: true }], fillColor: '#ffffff', border: [false,false,false,false], margin: [7,5,7,5] },
                { text: [{ text: 'ÁREA\n', fontSize: 6, color: '#627d98', bold: true }, { text: (talhaoInfo.area || item.talhao?.area) ? `${talhaoInfo.area || item.talhao?.area} ha` : '-', fontSize: 10, color: '#0878a4', bold: true }], fillColor: '#ffffff', border: [false,false,false,false], margin: [7,5,7,5] },
                { text: [{ text: 'PLANTIO\n', fontSize: 6, color: '#627d98', bold: true }, { text: fmtData(talhaoInfo.data_plantio || item.talhao?.data_plantio), fontSize: 10, color: '#159473', bold: true }], fillColor: '#ffffff', border: [false,false,false,false], margin: [7,5,7,5] },
                { text: [{ text: 'SAFRA\n', fontSize: 6, color: '#627d98', bold: true }, { text: safraLabel, fontSize: 10, color: '#102a43', bold: true }], fillColor: '#ffffff', border: [false,false,false,false], margin: [7,5,7,5] }
              ]]
            },
            layout: { hLineWidth: () => 0.5, vLineWidth: i => i > 0 && i < 4 ? 0.5 : 0, hLineColor: () => '#d6e6ee', vLineColor: () => '#d6e6ee', paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
            margin: [0, 0, 0, 7]
          },

          // ─ Configuração do talhão + estação ─
          sectionTitle('PARÂMETROS DO MANEJO', '#627d98'),
          {
            columns: [
              // Coluna solo / cultura
              {
                stack: [
                  { text: 'Solo & Água', fontSize: 7, bold: true, color: '#546e7a', margin: [0,0,0,3] },
                  ...[
                    ['Textura', cfg?.textura_solo ? ({ arenoso:'Arenoso', medio:'Médio (Franco)', argiloso:'Argiloso', manual:'Manual' }[cfg.textura_solo] || cfg.textura_solo) : '-'],
                    ['CAD', cfg?.capacidade_agua_disponivel ? `${cfg.capacidade_agua_disponivel} mm` : '-'],
                    ['FAD', cfg?.fator_disponibilidade ? `${Math.round(cfg.fator_disponibilidade * 100)}%${cfg?.ajustar_fad_etc ? ' (ajustado pela ETc)' : ''}` : '-'],
                    ['Prof. raiz (meio)', cfg?.zr_meio ? `${cfg.zr_meio} cm` : '-'],
                    ['Sistema irrigação', cfg?.sistema_irrigacao ? ({ gotejamento:'Gotejamento', aspersao:'Aspersão', pivô:'Pivô', sulcos:'Sulcos', manual:'Manual' }[cfg.sistema_irrigacao] || cfg.sistema_irrigacao) : '-'],
                    ['Eficiência', cfg?.eficiencia_sistema ? `${cfg.eficiencia_sistema}%` : '-'],
                  ].map(([k,v]) => ({
                    columns: [
                      { text: k, fontSize: 7, color: '#777', width: 90 },
                      { text: v, fontSize: 7, bold: true, color: '#263238' }
                    ], margin: [0,1,0,1]
                  }))
                ],
                width: '50%'
              },
              // Coluna Kc + estação
              {
                stack: [
                  { text: 'Coeficientes de Cultura (Kc)', fontSize: 7, bold: true, color: '#546e7a', margin: [0,0,0,3] },
                  ...[
                    ['Kc inicial', cfg?.kc_inicial ?? '-'],
                    ['Kc mid', cfg?.kc_meio ?? '-'],
                    ['Kc end', cfg?.kc_final ?? '-'],
                    ['Fases (ini/dev/mid/final)', `${cfg?.duracao_fase_inicial || 20}/${cfg?.duracao_fase_desenvolvimento || 35}/${cfg?.duracao_fase_meio || 40}/${cfg?.duracao_fase_final || 25} d`],
                    ['Ajuste climático', cfg?.ajustar_kc_clima ? `Ativo (h=${cfg?.altura_cultura_m || 0.5} m)` : 'Inativo'],
                    ['Origem', cfg?.kc_origem || 'legado/manual'],
                  ].map(([k,v]) => ({
                    columns: [
                      { text: k, fontSize: 7, color: '#777', width: 110 },
                      { text: String(v), fontSize: 7, bold: true, color: '#263238' }
                    ], margin: [0,1,0,1]
                  })),
                  { text: 'Estação Meteorológica', fontSize: 7, bold: true, color: '#546e7a', margin: [0,6,0,3] },
                  ...(estacao ? [
                    ['Nome', estacao.nome || '-'],
                    ['Tipo', ({ sensor_proprio:'Sensor Próprio', rede_inmet:'Rede INMET', estacao_comercial:'Estação Comercial', manual:'Manual' }[estacao.tipo_fonte] || estacao.tipo_fonte || '-')],
                    ['Device / Código', estacao.device_id || estacao.codigo_estacao || '-'],
                    ['Altitude', estacao.altitude ? `${estacao.altitude} m` : '-'],
                  ] : [['', 'Não configurada']]).map(([k,v]) => ({
                    columns: [
                      { text: k, fontSize: 7, color: '#777', width: 90 },
                      { text: v, fontSize: 7, bold: true, color: '#263238' }
                    ], margin: [0,1,0,1]
                  }))
                ],
                width: '50%'
              }
            ],
            columnGap: 16,
            margin: [0, 0, 0, 6]
          },
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 0.5, lineColor: '#e8e8e8' }], margin: [0, 2, 0, 6] },

          // ─ Indicadores do período ─
          sectionTitle('INDICADORES HÍDRICOS DO PERÍODO', '#0878a4'),
          {
            table: {
              widths: ['*','*','*','*','*','*'],
              body: [
                [
                  { text: 'ARMAZENAMENTO', fontSize: 6, color: '#627d98', bold: true, fillColor: '#eef7fa', alignment: 'center', border: [false,false,false,false], margin:[2,4,2,2] },
                  { text: 'CHUVA',         fontSize: 6, color: '#627d98', bold: true, fillColor: '#edf9f4', alignment: 'center', border: [false,false,false,false], margin:[2,4,2,2] },
                  { text: 'IRRIGAÇÃO',     fontSize: 6, color: '#627d98', bold: true, fillColor: '#eef8fb', alignment: 'center', border: [false,false,false,false], margin:[2,4,2,2] },
                  { text: 'CONSUMO ETC',   fontSize: 6, color: '#627d98', bold: true, fillColor: '#fff8e8', alignment: 'center', border: [false,false,false,false], margin:[2,4,2,2] },
                  { text: 'SALDO HÍDRICO', fontSize: 6, color: '#627d98', bold: true, fillColor: saldoPeriodo < 0 ? '#fff1f0' : '#edf9f4', alignment: 'center', border: [false,false,false,false], margin:[2,4,2,2] },
                  { text: 'ET₀ MÉDIA',     fontSize: 6, color: '#627d98', bold: true, fillColor: '#fff8e8', alignment: 'center', border: [false,false,false,false], margin:[2,4,2,2] },
                ],
                [
                  { text: pct != null ? `${parseFloat(pct).toFixed(0)}%` : '-', fontSize: 13, bold: true, color: '#0878a4', fillColor: '#eef7fa', alignment: 'center', border: [false,false,false,false], margin:[2,2,2,6] },
                  { text: `${totalChuva.toFixed(0)} mm`, fontSize: 13, bold: true, color: '#159473', fillColor: '#edf9f4', alignment: 'center', border: [false,false,false,false], margin:[2,2,2,6] },
                  { text: `${totalIrrigacao.toFixed(0)} mm`, fontSize: 13, bold: true, color: '#0e8fa3', fillColor: '#eef8fb', alignment: 'center', border: [false,false,false,false], margin:[2,2,2,6] },
                  { text: `${totalETc.toFixed(0)} mm`, fontSize: 13, bold: true, color: '#d88916', fillColor: '#fff8e8', alignment: 'center', border: [false,false,false,false], margin:[2,2,2,6] },
                  { text: `${saldoPeriodo > 0 ? '+' : ''}${saldoPeriodo.toFixed(0)} mm`, fontSize: 13, bold: true, color: saldoPeriodo < 0 ? '#c94b45' : '#159473', fillColor: saldoPeriodo < 0 ? '#fff1f0' : '#edf9f4', alignment: 'center', border: [false,false,false,false], margin:[2,2,2,6] },
                  { text: mediaET0 > 0 ? `${mediaET0.toFixed(1)} mm/d` : '-', fontSize: 11, bold: true, color: '#d88916', fillColor: '#fff8e8', alignment: 'center', border: [false,false,false,false], margin:[2,3,2,7] },
                ],
              ],
            },
            layout: { hLineWidth: () => 0, vLineWidth: i => i > 0 && i < 6 ? 0.5 : 0, vLineColor: () => '#d6e6ee', paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
            margin: [0, 0, 0, 6]
          },
          {
            columns: [
              { text: `Método: Kc único FAO-56  ·  ETc = ET₀ × Kc × Kl  ·  ajuste climático Eq. 62/65 quando ativo  ·  FAD = ${Math.round(fad)}% da CAD`, fontSize: 7, color: '#999', italics: true },
            ],
            margin: [0, 0, 0, 2]
          },
          { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 0.5, lineColor: '#e8e8e8' }], margin: [0, 4, 0, 6] },

          // ─ Gráfico Armazenamento ─
          {
            unbreakable: true,
            stack: [
              sectionTitle(`ARMAZENAMENTO DO SOLO — ${periodoLabel} (%)`, '#0878a4'),
              {
                columns: [
                  yLabels(armazTicks, 0),
                  { width: '*', stack: buildArmazChart(armVals, fad) }
                ]
              },
              xLabels(labelsGraf),
              {
                columns: [
                  { canvas: [{ type: 'rect', x: 0, y: 2, w: 18, h: 3, color: '#0878a4', lineWidth: 0 }], width: 22 },
                  { text: 'Armazenamento (%)', fontSize: 6.5, color: '#555', margin: [0, 0, 16, 0] },
                  { canvas: [{ type: 'line', x1: 0, y1: 4, x2: 18, y2: 4, lineWidth: 1, lineColor: '#ef9a9a', dash: { length: 3, space: 3 } }], width: 22 },
                  { text: `FAD = ${Math.round(fad)}%  (limite de irrigação)`, fontSize: 6.5, color: '#c62828' },
                ],
                margin: [PAD_L, 2, 0, 0],
                columnGap: 4
              },
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 0.5, lineColor: '#e8e8e8' }], margin: [0, 8, 0, 6] },
            ]
          },

          // ─ Gráfico Chuva ─
          {
            unbreakable: true,
            stack: [
              sectionTitle(`PRECIPITAÇÃO — ${periodoLabel} (mm)`, '#159473'),
              { text: 'Precipitação (mm)', fontSize: 7, color: '#159473', bold: true, margin: [PAD_L, 0, 0, 2] },
              {
                columns: [
                  yLabels(chuvaTicks, 1),
                  { width: '*', stack: buildBarChart(chuvaVals, '#25aa83') }
                ]
              },
              xLabels(labelsGraf),
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 0.5, lineColor: '#e8e8e8' }], margin: [0, 4, 0, 6] },
            ]
          },

          // ─ Gráfico Irrigação ─
          ...(irrigVals.some(v => v > 0) ? [
            {
              unbreakable: true,
              stack: [
                sectionTitle(`IRRIGAÇÃO APLICADA — ${periodoLabel} (mm)`, '#0e8fa3'),
                { text: 'Irrigação (mm)', fontSize: 7, color: '#0e8fa3', bold: true, margin: [PAD_L, 0, 0, 2] },
                {
                  columns: [
                    yLabels(irrigTicks, 1),
                    { width: '*', stack: buildBarChart(irrigVals, '#20a4b5') }
                  ]
                },
                xLabels(labelsGraf),
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 0.5, lineColor: '#e8e8e8' }], margin: [0, 4, 0, 6] },
              ]
            },
          ] : []),


          // ─ Gráfico ETc ─
          ...(etcVals.some(v => v != null) ? [
            {
              unbreakable: true,
              stack: [
                sectionTitle('EVAPOTRANSPIRAÇÃO DA CULTURA (ETc mm/dia)', '#e65100'),
                {
                  columns: [
                    yLabels(etcTicks, 1),
                    { width: '*', stack: buildLineChart(etcVals, '#ef6c00', '#fff3e0') }
                  ]
                },
                xLabels(labelsGraf),
                { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 0.5, lineColor: '#e8e8e8' }], margin: [0, 8, 0, 6] },
              ]
            },
          ] : []),

          // ─ Tabela Diária ─
          sectionTitle(`HISTÓRICO DIÁRIO — ${periodoLabel}`, '#546e7a'),
          {
            table: {
              headerRows: 1,
              widths: [44, 36, 36, 36, 36, 36, '*'],
              body: [headerTabela, ...linhasTabela]
            },
            layout: {
              hLineWidth: () => 0,
              vLineWidth: () => 0,
              paddingLeft: () => 2,
              paddingRight: () => 2,
              paddingTop: () => 0,
              paddingBottom: () => 0,
            },
            margin: [0, 0, 0, 4]
          },
          { text: '* Células em vermelho = armazenamento abaixo do FAD (irrigação recomendada)', fontSize: 7, color: '#c62828', italics: true, margin: [0, 0, 0, 6] },

          // ─ Eventos ─
          ...(temEventos ? [
            { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 535, y2: 0, lineWidth: 0.5, lineColor: '#e8e8e8' }], margin: [0, 4, 0, 6] },
            sectionTitle('EVENTOS DE IRRIGAÇÃO NO PERÍODO', '#0277bd'),
            {
              table: {
                headerRows: 1,
                widths: [50, 54, '*'],
                body: [
                  [
                    { text: 'DATA',       fontSize: 6.5, bold: true, color: '#fff', fillColor: '#0277bd', border: [false,false,false,false], margin:[2,3,2,3] },
                    { text: 'LÂMINA (mm)',fontSize: 6.5, bold: true, color: '#fff', fillColor: '#0277bd', alignment: 'center', border: [false,false,false,false], margin:[2,3,2,3] },
                    { text: 'MOTIVO',     fontSize: 6.5, bold: true, color: '#fff', fillColor: '#0277bd', border: [false,false,false,false], margin:[2,3,2,3] },
                  ],
                  ...linhasEventos,
                ]
              },
              layout: { hLineWidth: () => 0, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0, paddingTop: () => 0, paddingBottom: () => 0 },
              margin: [0, 0, 0, 6]
            }
          ] : []),
        ],

        footer: (currentPage, pageCount) => ({
          columns: [
            { text: `Página ${currentPage} de ${pageCount}`, fontSize: 7, color: '#aaa', margin: [28, 8, 0, 0] },
            { text: `ClimoraX  ·  Inteligência hídrica  ·  ${new Date().toLocaleDateString('pt-BR')}`, fontSize: 7, color: '#0878a4', bold: true, alignment: 'right', margin: [0, 8, 28, 0] }
          ]
        })
      };

      const nomeArq = `irrigacao_${(item.talhao?.nome || 'talhao').replace(/[^a-zA-Z0-9_-]/g,'_')}_${hoje}.pdf`;

      let isCapacitor = false;
      try {
        isCapacitor = typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
      } catch (e) { isCapacitor = false; }

      if (isCapacitor && Filesystem && Directory) {
        pdfMake.createPdf(docDefinition).getBase64(async (pdfBase64) => {
          try {
            const result = await Filesystem.writeFile({
              path: nomeArq,
              data: pdfBase64,
              directory: Directory.Documents,
            });
            if (FileOpener && result.uri) {
              try {
                await FileOpener.open({ filePath: result.uri, contentType: 'application/pdf' });
                alert(`Relatório salvo com sucesso!\n\nArquivo: ${nomeArq}\n\nSe não abrir automaticamente, procure na pasta Documentos do seu gerenciador de arquivos.`);
              } catch (openErr) {
                alert(`PDF salvo com sucesso!\n\nArquivo: ${nomeArq}\n\nProcure na pasta "Documentos" do seu gerenciador de arquivos.\n\nCaminho: ${result.uri}`);
              }
            } else {
              alert(`PDF salvo com sucesso!\n\nArquivo: ${nomeArq}\n\nProcure na pasta "Documentos" do seu gerenciador de arquivos.`);
            }
          } catch (err) {
            alert('Erro ao salvar PDF no dispositivo: ' + (err && err.message ? err.message : JSON.stringify(err)));
          } finally {
            setGerandoPdf(false);
          }
        }, (err) => {
          alert('Erro ao gerar PDF: ' + (err && err.message ? err.message : JSON.stringify(err)));
          setGerandoPdf(false);
        });
        return;
      } else {
        pdfMake.createPdf(docDefinition).download(nomeArq);
      }
    } catch (err) {
      console.error('Erro ao gerar PDF de irrigação:', err);
    } finally {
      setGerandoPdf(false);
    }
  };

  const chartOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      zoom: { enabled: false },
      background: 'transparent',
      fontFamily: 'var(--font-body)',
      animations: { speed: 450 }
    },
    theme: { mode: darkMode ? 'dark' : 'light' },
    dataLabels: { enabled: false },
    stroke: { width: [3, 0, 0], curve: 'smooth', lineCap: 'round' },
    plotOptions: { bar: { columnWidth: '42%', borderRadius: 4, borderRadiusApplication: 'end' } },
    grid: {
      borderColor: darkMode ? 'rgba(159,184,203,0.12)' : 'rgba(22,92,126,0.1)',
      strokeDashArray: 3,
      padding: { left: 6, right: 8, top: 4, bottom: 0 }
    },
    xaxis: {
      categories: datas,
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: {
        rotate: 0,
        hideOverlappingLabels: true,
        style: { fontSize: '10px', colors: darkMode ? '#9fb8cb' : '#627d98' },
        formatter: value => value?.replace('-', '/')
      }
    },
    yaxis: [
      {
        seriesName: 'Armazenamento (%)',
        min: 0, max: 100,
        tickAmount: 5,
        labels: { style: { colors: darkMode ? '#9fb8cb' : '#627d98' }, formatter: v => `${v != null ? Math.round(v) : 0}%` }
      },
      {
        seriesName: 'Chuva (mm)',
        opposite: true,
        min: 0, max: mmMax,
        tickAmount: 4,
        labels: { style: { colors: darkMode ? '#9fb8cb' : '#627d98' }, formatter: v => `${Math.round(v)} mm` }
      },
      { seriesName: 'Chuva (mm)', show: false, opposite: true, min: 0, max: mmMax }
    ],
    annotations: {
      yaxis: [{
        y: fad,
        borderColor: '#ff9800',
        strokeDashArray: 5,
        label: {
          text: `FAD ${Math.round(fad)}%`,
          style: { color: '#fff', background: '#ff9800', fontSize: '11px', fontWeight: 700 }
        }
      }]
    },
    legend: { show: false },
    colors: ['#0878a4', '#159473', '#21aee5'],
    fill: {
      type: ['gradient', 'solid', 'solid'],
      gradient: { shade: 'light', type: 'vertical', shadeIntensity: 0.25, opacityFrom: 0.42, opacityTo: 0.03 }
    },
    markers: { size: 0, hover: { size: 5 } },
    tooltip: { shared: true, intersect: false, theme: darkMode ? 'dark' : 'light' }
  };

  const chartSeries = [
    { name: 'Armazenamento (%)', type: 'area', data: armazSeries },
    { name: 'Chuva (mm)',        type: 'bar',  data: chuvaSeries },
    { name: 'Irrigação (mm)',    type: 'bar',  data: irrigSeries }
  ];

  const insightPrincipal = insights[0] || null;
  const sinaisPeriodo = [
    tendencia && {
      label: 'Tendência',
      valor: Math.abs(tendencia.delta) < 2 ? 'Estável' : tendencia.delta < 0 ? `↓ ${Math.abs(tendencia.delta)} p.p.` : `↑ ${tendencia.delta} p.p.`,
      tipo: tendencia.delta < -2 ? 'attention' : 'stable'
    },
    {
      label: 'Balanço',
      valor: saldoPeriodo < 0 ? `${saldoPeriodo.toFixed(0)} mm` : `+${saldoPeriodo.toFixed(0)} mm`,
      tipo: saldoPeriodo < -5 ? 'attention' : 'stable'
    },
    {
      label: 'Registros',
      valor: totalIrrigacao > 0 ? `${eventos.length} irrigações` : 'Sem irrigação',
      tipo: totalIrrigacao === 0 && item.recomendacao?.startsWith('irrigar') ? 'attention' : 'neutral'
    }
  ].filter(Boolean);
  const fonteDados = item.metodo_calculo_et0?.includes('média')
    ? 'Estimativa pela média de 7 dias'
    : item.metodo_calculo_et0?.includes('NASA POWER')
      ? 'Satélite NASA POWER'
      : 'Estação meteorológica';
  const recomendacaoTitulo = item._dadosDesatualizados
    ? 'Atualize o balanço antes de decidir'
    : item.recomendacao === 'irrigar_urgente'
    ? 'Irrigue este talhão agora'
    : item.recomendacao === 'irrigar'
      ? 'Programe a irrigação para hoje'
      : item.recomendacao === 'aguardar_chuva'
        ? 'Acompanhe a chuva antes de irrigar'
        : 'Mantenha o monitoramento';
  const periodoDias = historico.length;

  return (
    <section className="irrigation-history-page" aria-labelledby="irrigation-history-title">
      <button type="button" className="irrigation-history-back" onClick={onClose}>
        <FaArrowLeft aria-hidden="true" /> Voltar aos talhões
      </button>
      <div className="irrigation-history-card">
        {/* Hero: identidade do talhão + status + período */}
        <div className="irrigation-history-hero">
          <div className="irrigation-history-hero-top">
            <div className="irrigation-history-title">
              <h2 id="irrigation-history-title">🌾 {item.talhao?.nome}</h2>
              {item.talhao?.area && <span>{item.talhao.area} ha · {item.talhao?.cultura || 'Cultura não informada'}</span>}
            </div>
            <div className="irrigation-history-actions">
              <button
                className="irrigation-history-pdf-btn"
                onClick={exportarPDF}
                disabled={gerandoPdf || loading || historico.length === 0}
                title="Gerar relatório PDF"
              >
                {gerandoPdf ? '⏳' : '📄'} {gerandoPdf ? 'Gerando...' : 'PDF'}
              </button>
            </div>
          </div>

          <div className="irrigation-history-status-row">
            <span className="irrigation-history-status-pill">{info.icon} {info.label}</span>
            <div className="irrigation-history-period-row">
              <input
                type="date"
                value={dataInicioPers}
                min={dataMinima || undefined}
                max={dataFimPers}
                onChange={e => setDataInicioPers(e.target.value)}
              />
              <span>até</span>
              <input
                type="date"
                value={dataFimPers}
                min={dataInicioPers}
                max={hojeStr}
                onChange={e => setDataFimPers(e.target.value)}
              />
              {(talhaoExtra?.data_plantio || item.talhao?.data_plantio) && (
                <button
                  className="irrigation-history-plantio-btn"
                  onClick={() => { setDataInicioPers(talhaoExtra?.data_plantio || item.talhao?.data_plantio); setDataFimPers(hojeStr); }}
                >
                  🌱 Desde o plantio
                </button>
              )}
            </div>
          </div>

          <div className="irrigation-history-hero-metrics">
            <div>
              <span>Armazenamento hoje</span>
              <strong>{item.armazenamento_percentual?.toFixed(0) ?? '–'}%</strong>
            </div>
            <div>
              <span>Janela operacional</span>
              <strong>{diasCritico === 0 ? 'Crítico agora' : diasCritico != null && diasCritico < 999 ? `~${diasCritico} dia${diasCritico !== 1 ? 's' : ''}` : 'Acompanhar'}</strong>
            </div>
            <div>
              <span>Plantio</span>
              <strong>{(talhaoExtra?.data_plantio || item.talhao?.data_plantio) ? (talhaoExtra?.data_plantio || item.talhao?.data_plantio).slice(0,10).split('-').reverse().join('/') : '—'}</strong>
            </div>
            <div>
              <span>Safra</span>
              <strong>{(() => { const dp = talhaoExtra?.data_plantio || item.talhao?.data_plantio; if (!dp) return '—'; const d = new Date(dp); const mes = d.getMonth()+1; const ano = d.getFullYear(); return mes >= 7 ? `${ano}/${ano+1}` : `${ano-1}/${ano}`; })()}</strong>
            </div>
          </div>
        </div>

        <div className="irrigation-history-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: darkMode ? '#aaa' : '#666' }}>Carregando histórico...</div>
          ) : historico.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: darkMode ? '#aaa' : '#888' }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>📊</div>
              Sem balanço calculado para este talhão ainda.
            </div>
          ) : (
            <>
              <section className="irrigation-history-decision">
                <div className="irrigation-history-decision-main">
                  <span className="irrigation-eyebrow">Recomendação operacional</span>
                  <h3>{recomendacaoTitulo}</h3>
                  <p>{item._dadosDesatualizados
                    ? `A referência de ${formatLocalDate(item._dataReferencia)} é histórica e não deve orientar uma irrigação para hoje.`
                    : insightPrincipal?.texto || 'Os dados atuais não indicam necessidade de intervenção imediata.'}</p>
                  <div className="irrigation-history-decision-facts">
                    <span><b>{item.necessidade_irrigacao?.toFixed(1) || '0.0'} mm</b> de lâmina sugerida</span>
                    {pivotSetting && <span><b>{pivotSetting.percent}%</b> no percentímetro{pivotSetting.passes > 1 ? ` · ${pivotSetting.passes} voltas` : ''}</span>}
                    <span><b>{Math.round(fad)}%</b> limite FAD</span>
                    <span><b>{etcMedia7.toFixed(1)} mm/d</b> consumo recente</span>
                  </div>
                  {onIrrigar && !item._dadosDesatualizados && (
                    <button type="button" onClick={() => onIrrigar(item.talhao)}>
                      💧 Registrar irrigação
                    </button>
                  )}
                </div>
                <aside className="irrigation-history-context">
                  <span className="irrigation-eyebrow">Contexto do cálculo</span>
                  <dl>
                    <div><dt>Fonte meteorológica</dt><dd>{fonteDados}</dd></div>
                    <div><dt>Período analisado</dt><dd>{periodoDias} dia{periodoDias !== 1 ? 's' : ''}</dd></div>
                    <div><dt>ET₀ média</dt><dd>{mediaET0 > 0 ? `${mediaET0.toFixed(1)} mm/d` : 'Sem dados'}</dd></div>
                    <div><dt>Kc do último dia</dt><dd>{item.kc_dia != null ? `${Number(item.kc_base_dia ?? item.kc_dia).toFixed(2)}${item.correcao_climatica_kc ? ` ${item.correcao_climatica_kc > 0 ? '+' : ''}${Number(item.correcao_climatica_kc).toFixed(2)}` : ''} = ${Number(item.kc_dia).toFixed(2)}` : 'Sem dados'}</dd></div>
                    <div><dt>Kl do sistema</dt><dd>{item.kl_dia != null ? Number(item.kl_dia).toFixed(3) : '1,000'}</dd></div>
                    <div><dt>Saldo hídrico</dt><dd className={saldoPeriodo < 0 ? 'is-negative' : 'is-positive'}>{saldoPeriodo >= 0 ? '+' : ''}{saldoPeriodo.toFixed(0)} mm</dd></div>
                  </dl>
                  <small>{item.origem_ajuste_kc || 'Kc tabulado/configurado sem ajuste climático.'}</small>
                </aside>
              </section>

              <div className="irrigation-history-signals" aria-label="Sinais do período">
                {sinaisPeriodo.map(sinal => (
                  <div key={sinal.label} className={`is-${sinal.tipo}`}>
                    <span>{sinal.label}</span>
                    <strong>{sinal.valor}</strong>
                  </div>
                ))}
              </div>

              <div className="irrigation-history-period-metrics">
                {[
                  { label: 'Chuva',    value: `${totalChuva.toFixed(0)} mm`,                          color: '#159473', icon: '🌧️' },
                  { label: 'Irrigação', value: `${totalIrrigacao.toFixed(0)} mm`,                      color: '#21aee5', icon: '💦' },
                  { label: 'Consumo ETc', value: `${totalETc.toFixed(0)} mm`,                           color: '#f59e0b', icon: '☀️' },
                ].map(s => (
                  <div key={s.label} style={{ '--metric-color': s.color }}>
                    <span className="irrigation-history-metric-icon">{s.icon}</span>
                    <span>{s.label}<small>no período</small></span>
                    <strong>{s.value}</strong>
                  </div>
                ))}
              </div>

              <section className="irrigation-history-chart-panel">
                <div className="irrigation-history-chart-heading">
                  <div>
                    <span className="irrigation-eyebrow">Evolução no período</span>
                    <h3>Água disponível no solo</h3>
                  </div>
                  <span>{dataInicio.split('-').reverse().join('/')} a {dataFim.split('-').reverse().join('/')}</span>
                </div>
                <div className="irrigation-history-chart-legend" aria-label="Legenda do gráfico">
                  <span><i className="is-storage" /> Armazenamento</span>
                  <span><i className="is-rain" /> Chuva</span>
                  <span><i className="is-irrigation" /> Irrigação</span>
                  <span><i className="is-fad" /> Limite FAD</span>
                </div>
                <div className="irrigation-history-chart-canvas">
                  <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={isMobile ? 280 : 330} />
                </div>
                <div className="irrigation-history-chart-note">
                  A linha mostra a reserva do solo; as barras registram entradas de água. Abaixo do FAD, a irrigação é recomendada.
                </div>
              </section>

              {eventos.length > 0 && (
                <div className="irrigation-history-events-section">
                  <div className="irrigation-history-events-heading">
                    <h3 className="irrigation-history-section-title">Irrigações registradas</h3>
                    <span>{eventos.length} evento{eventos.length !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="irrigation-history-events">
                    {eventos.map((ev, i) => (
                      <div key={i} className="irrigation-history-event">
                        <span>📅 {ev.data_executada?.split('-').reverse().join('/')}</span>
                        <strong>{(ev.lamina_aplicada_mm || 0).toFixed(1)} mm</strong>
                        {ev.motivo && <span style={{ color: darkMode ? '#9ca3af' : '#64748b', fontSize: 12 }}>{ev.motivo}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}

// ============================================
// COMPONENTE: LISTA DE TALHÕES COM BALANÇO HÍDRICO
// ============================================
function ListaTalhoesBalanco({ propriedadeId, canonicalPlots = [], darkMode, refreshKey, onIrrigar, onSnapshot }) {
  const [talhoes, setTalhoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [talhaoDetalhado, setTalhaoDetalhado] = useState(null);
  const [dataUltimoDado, setDataUltimoDado] = useState(null);    // última data com dado (pode ser 2 dias atrás)
  const [dataUltimoCalculo, setDataUltimoCalculo] = useState(null); // quando o cálculo foi rodado (created_at)
  const [isNasaPower, setIsNasaPower] = useState(false);
  const [temEstimadoHoje, setTemEstimadoHoje] = useState(false);  // true quando hoje foi preenchido com média 7d
  
  useEffect(() => {
    carregarDados();
  }, [propriedadeId, refreshKey, canonicalPlots]);
  
  const carregarDados = async () => {
    if (!propriedadeId) return;
    setLoading(true);
    onSnapshot?.({ loading: true, ready: false, items: [], dataReferencia: null, stale: false });
    
    try {
      const now = new Date();
      const hoje = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      // Tenta buscar dados de hoje
      const { data: dadosHoje, error: erroHoje } = await supabase
        .from('balanco_hidrico_diario')
        .select(`
          *,
          talhao:talhoes!inner(
            id,
            nome,
            area,
            cultura,
            data_plantio,
            data_colheita,
            configuracao_irrigacao:configuracao_irrigacao_talhao(fator_disponibilidade)
          )
        `)
        .eq('data', hoje)
        .eq('talhoes.propriedade_id', propriedadeId)
        .is('talhoes.data_colheita', null)
        .order('prioridade', { ascending: true });
      
      if (erroHoje) throw erroHoje;
      
      let baseItems = null;
      let dataRef = null;

      if (dadosHoje && dadosHoje.length > 0) {
        baseItems = dadosHoje;
        dataRef = hoje; // dados de hoje disponíveis
      } else {
        // Sem dados de hoje: busca o registro mais recente por talhão (apenas não colhidos)
        const { data: dadosUltimos, error: erroUltimos } = await supabase
          .from('balanco_hidrico_diario')
          .select(`
            *,
            talhao:talhoes!inner(
              id,
              nome,
              area,
              cultura,
              data_plantio,
              data_colheita,
              configuracao_irrigacao:configuracao_irrigacao_talhao(fator_disponibilidade)
            )
          `)
          .eq('talhoes.propriedade_id', propriedadeId)
          .is('talhoes.data_colheita', null)
          .order('data', { ascending: false });

        if (erroUltimos) throw erroUltimos;

        const vistos = new Set();
        const ultimos = (dadosUltimos || [])
          .filter(item => {
            if (vistos.has(item.talhao_id)) return false;
            vistos.add(item.talhao_id);
            return true;
          })
          .sort((a, b) => (a.prioridade ?? 99) - (b.prioridade ?? 99));

        baseItems = ultimos;
        dataRef = ultimos.length > 0 ? ultimos[0].data : null;
      }

      // Registros calculados antes da regra de plantio futuro podem permanecer no banco.
      // Eles não devem participar do status hídrico até a data de início do manejo.
      baseItems = (baseItems || []).filter(item => {
        const dataPlantio = item.talhao?.data_plantio?.slice(0, 10);
        return !isFutureLocalDate(dataPlantio, now) && (!dataPlantio || item.data >= dataPlantio);
      });
      if (dataRef !== hoje) dataRef = baseItems[0]?.data ?? null;

      if (!baseItems || baseItems.length === 0) {
        setTalhoes([]);
        setDataUltimoDado(null);
        setDataUltimoCalculo(null);
        setTemEstimadoHoje(false);
        onSnapshot?.({ loading: false, ready: true, items: [], dataReferencia: null, stale: false });
        return;
      }

      // Último cálculo = MAX(created_at) dos itens recuperados
      const maxCreatedAt = baseItems.reduce((max, item) => {
        const t = item.created_at ? new Date(item.created_at).getTime() : 0;
        return t > max ? t : max;
      }, 0);
      const dataCalcStr = maxCreatedAt
        ? new Date(maxCreatedAt).toLocaleDateString('pt-BR')
        : null;

      // Detectar NASA POWER (incluindo dias estimados via média, que também são originados do NASA POWER)
      const usaNasa = baseItems.some(i =>
        i.metodo_calculo_et0?.includes('NASA POWER') ||
        i.metodo_calculo_et0?.includes('média')
      );
      // Detectar se o balanço de hoje usou estimativa por ausência de dado NASA POWER
      const estimadoHoje = baseItems.some(i => i.metodo_calculo_et0?.includes('média'));

      // Buscar ETc média dos últimos 7 dias para cada talhão (ETc média é mais estável que ETc de 1 dia)
      const talhaoIds = baseItems.map(i => i.talhao_id);
      const { data: etcRecentes } = await supabase
        .from('balanco_hidrico_diario')
        .select('talhao_id, etc_dia, data')
        .in('talhao_id', talhaoIds)
        .gt('etc_dia', 0)
        .order('data', { ascending: false })
        .limit(talhaoIds.length * 7);

      // Média de ETc por talhão (janela de 7 dias)
      const etcMediaPorTalhao = {};
      if (etcRecentes) {
        const contagem = {};
        for (const r of etcRecentes) {
          if (!etcMediaPorTalhao[r.talhao_id]) {
            etcMediaPorTalhao[r.talhao_id] = 0;
            contagem[r.talhao_id] = 0;
          }
          if (contagem[r.talhao_id] < 7) {
            etcMediaPorTalhao[r.talhao_id] += r.etc_dia;
            contagem[r.talhao_id]++;
          }
        }
        for (const id of talhaoIds) {
          if (contagem[id]) etcMediaPorTalhao[id] = etcMediaPorTalhao[id] / contagem[id];
        }
      }

      // Injetar etc_media_7d em cada item
      const itemsComMedia = applyCanonicalAreasToIrrigationBalances(baseItems.map(item => ({
        ...item,
        etc_media_7d: etcMediaPorTalhao[item.talhao_id] ?? item.etc_dia ?? 0,
      })), canonicalPlots);

      setTalhoes(itemsComMedia);

      // Quando hoje foi estimado, buscar a data do último dado real do NASA POWER
      let ultimaDataReal = dataRef !== hoje ? dataRef : null;
      if (estimadoHoje && baseItems.length > 0) {
        const { data: ultimoReal } = await supabase
          .from('balanco_hidrico_diario')
          .select('data')
          .in('talhao_id', talhaoIds)
          .ilike('metodo_calculo_et0', '%NASA POWER%')
          .order('data', { ascending: false })
          .limit(1)
          .maybeSingle();
        ultimaDataReal = ultimoReal?.data ?? null;
      }

      setDataUltimoDado(ultimaDataReal);
      setDataUltimoCalculo(dataCalcStr);
      setIsNasaPower(usaNasa);
      setTemEstimadoHoje(estimadoHoje);
      // dataRef reflete o dia do último balanço calculado (real ou estimado por média 7d).
      // O delay natural do NASA POWER (ultimaDataReal) não deve, sozinho, marcar o balanço
      // como desatualizado — senão nenhum recálculo jamais resolveria o aviso.
      const dataReferencia = dataRef;
      onSnapshot?.({
        loading: false,
        ready: true,
        items: itemsComMedia,
        dataReferencia,
        stale: isIrrigationDataStale(dataReferencia),
      });
    } catch (error) {
      console.error('Erro ao carregar balanço:', error);
      onSnapshot?.({ loading: false, ready: true, items: [], dataReferencia: null, stale: true, error: error.message });
    } finally {
      setLoading(false);
    }
  };
  
  // getRecomendacaoInfo é a função standalone acima

  // Mapeia a recomendação salva no banco para a classe de status do design system
  // (mesma paleta usada no cartão de prévia — ver .irrigation-plot-card.status-*)
  const statusClasse = (recomendacao) => {
    switch (recomendacao) {
      case 'irrigar_urgente': return 'urgente';
      case 'irrigar':         return 'irrigar';
      case 'aguardar_chuva':  return 'aguardar';
      default:                return 'ok';
    }
  };

  const fonteLabel = (metodo) => {
    if (!metodo) return 'Estação';
    if (metodo.includes('média')) return 'Estimado (7d)';
    if (metodo.includes('NASA POWER')) return 'NASA POWER';
    return 'Estação própria';
  };

  // O delay do NASA POWER (dataUltimoDado) é informativo, não deve ditar "desatualizado":
  // o dia mais recente calculado (talhoes[0]?.data) já cobre "hoje" mesmo quando estimado.
  const dataReferencia = talhoes[0]?.data || dataUltimoDado || null;
  const dadosDesatualizados = talhoes.length > 0 && isIrrigationDataStale(dataReferencia);

  if (loading) {
    return (
      <section className="irrigation-plots-panel">
        <div className="irrigation-management-empty" style={{ padding: '28px 20px' }}>
          <p style={{ color: darkMode ? '#9ca3af' : '#64748b', margin: 0 }}>Carregando balanço hídrico...</p>
        </div>
      </section>
    );
  }

  if (talhoes.length === 0) {
    return (
      <section className="irrigation-plots-panel">
        <div className="irrigation-management-empty" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 10 }}><FaChartBar aria-hidden="true" /></div>
          <p style={{ color: darkMode ? '#9ca3af' : '#64748b', margin: 0 }}>
            Clique em "Calcular Balanço" para processar os dados de irrigação.
          </p>
        </div>
      </section>
    );
  }

  if (talhaoDetalhado) {
    return (
      <TelaHistoricoBalanco
        item={{ ...talhaoDetalhado, _dadosDesatualizados: dadosDesatualizados, _dataReferencia: dataReferencia }}
        onClose={() => setTalhaoDetalhado(null)}
        darkMode={darkMode}
        onIrrigar={onIrrigar ? (talhao) => {
          setTalhaoDetalhado(null);
          onIrrigar(talhao);
        } : null}
      />
    );
  }

  return (
    <>
      <section className="irrigation-plots-panel">
        <div className="irrigation-section-heading">
          <div>
            <span className="irrigation-eyebrow">Status hídrico</span>
            <h3>Talhões por ordem de prioridade</h3>
            <p>Toque em um talhão para ver o histórico e detalhes do cálculo.</p>
          </div>
          <span className="irrigation-section-count">{talhoes.length} {talhoes.length === 1 ? 'talhão' : 'talhões'}</span>
        </div>

        {dadosDesatualizados && (
          <div role="alert" className="irrigation-stale-warning">
            <FaExclamationTriangle aria-hidden="true" />
            <div><strong>Dados desatualizados</strong><span>Última referência: {formatLocalDate(dataReferencia)}. Recalcule o balanço antes de programar irrigação.</span></div>
          </div>
        )}

        {(dataUltimoDado || dataUltimoCalculo) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
            {dataUltimoCalculo && (
              <span style={{
                fontSize: 11,
                padding: '3px 9px',
                borderRadius: 6,
                background: darkMode ? '#1f2937' : '#f1f5f9',
                color: darkMode ? '#9ca3af' : '#64748b',
                border: `1px solid ${darkMode ? '#374151' : '#e2e8f0'}`,
                fontWeight: 600
              }}>
                🔄 Calculado: {dataUltimoCalculo}
              </span>
            )}
            {temEstimadoHoje && (
              <span style={{
                fontSize: 11,
                padding: '3px 9px',
                borderRadius: 6,
                background: darkMode ? '#1a1f2e' : '#eff6ff',
                color: darkMode ? '#93c5fd' : '#1d4ed8',
                border: `1px solid ${darkMode ? '#2d3f6e' : '#bfdbfe'}`,
                fontWeight: 600
              }}>
                ⚡ Hoje estimado (média 7d)
              </span>
            )}
            {dataUltimoDado && (
              isNasaPower ? (
                <span style={{
                  fontSize: 11,
                  padding: '3px 9px',
                  borderRadius: 6,
                  background: darkMode ? '#0c1a2e' : '#e0f2fe',
                  color: darkMode ? '#7dd3fc' : '#0369a1',
                  border: `1px solid ${darkMode ? '#1e3a5f' : '#bae6fd'}`,
                  fontWeight: 600
                }}>
                  🛰️ Dados até: {new Date(dataUltimoDado + 'T12:00:00').toLocaleDateString('pt-BR')}
                </span>
              ) : (
                <span style={{
                  fontSize: 11,
                  padding: '3px 9px',
                  borderRadius: 6,
                  background: darkMode ? '#422006' : '#fef3c7',
                  color: darkMode ? '#fbbf24' : '#b45309',
                  border: `1px solid ${darkMode ? '#78350f' : '#fcd34d'}`,
                  fontWeight: 600
                }}>
                  ⚠️ Dados até: {new Date(dataUltimoDado + 'T12:00:00').toLocaleDateString('pt-BR')}
                </span>
              )
            )}
          </div>
        )}

        <div className="irrigation-plot-list">
          {talhoes.map((item, index) => {
            const info = dadosDesatualizados ? { icon: '!', label: 'Dados desatualizados' } : getRecomendacaoInfo(item.recomendacao);
            const statusCls = dadosDesatualizados ? 'stale' : statusClasse(item.recomendacao);
            const diasCritico = (() => {
              const arm = item.armazenamento_atual;
              const pct = item.armazenamento_percentual;
              const etcMedia = item.etc_media_7d;
              if (!arm || !pct || !etcMedia || etcMedia <= 0) return item.dias_ate_limite_critico;
              const cfg = item.talhao?.configuracao_irrigacao;
              const fadFator = (Array.isArray(cfg) ? cfg[0] : cfg)?.fator_disponibilidade ?? 0.5;
              const cad = arm / (pct / 100);
              const fadMm = cad * fadFator;
              return Math.max(0, Math.floor((arm - fadMm) / etcMedia));
            })();
            const janela = dadosDesatualizados
              ? formatLocalDate(dataReferencia)
              : statusCls === 'urgente' || statusCls === 'irrigar'
              ? 'Hoje'
              : (diasCritico != null && diasCritico < 999 ? `~${diasCritico} dia${diasCritico !== 1 ? 's' : ''}` : 'Acompanhar');

            // Alerta de delay NASA POWER vs dias até crítico
            const alertaDelay = (() => {
              if (!isNasaPower || !dataUltimoDado || item.metodo_calculo_et0?.includes('média')) return null;
              if (diasCritico == null || diasCritico >= 999) return null;
              const hoje = new Date();
              const ultimoDado = new Date(dataUltimoDado + 'T12:00:00');
              const delayDias = Math.round((hoje - ultimoDado) / (1000 * 60 * 60 * 24));
              if (delayDias <= 0) return null;
              const diasEfetivos = diasCritico - delayDias;
              if (diasEfetivos > 5) return null;
              const [cor, icon, msg] = diasEfetivos <= 0
                ? ['#e74a4a', '🚨', `Atraso de ${delayDias}d — limite crítico pode já ter sido atingido`]
                : diasEfetivos <= 2
                ? ['#ef9b22', '⚠️', `Atraso de ${delayDias}d — limite crítico em ~${diasEfetivos} dia${diasEfetivos !== 1 ? 's' : ''} efetivamente`]
                : ['#78716c', '🛰️', `Atraso de ${delayDias}d — limite crítico em ~${diasEfetivos} dias efetivamente`];
              return { cor, icon, msg };
            })();

            const pivotSetting = normalizeBalanceItem(item).pivotSetting;

            return (
              <article
                className={`irrigation-plot-card status-${statusCls}`}
                key={item.id}
                onClick={() => setTalhaoDetalhado(item)}
                title="Clique para ver o histórico do balanço"
                style={{ cursor: 'pointer' }}
              >
                <div className="irrigation-plot-rank" aria-label={`Prioridade ${index + 1}`}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div className="irrigation-plot-content">
                  <div className="irrigation-plot-title-row">
                    <div>
                      <h4>🌾 {item.talhao?.nome || 'Talhão'}</h4>
                      <p>{item.talhao?.cultura || 'Cultura não informada'}{item.talhao?.area ? ` · ${item.talhao.area} ha` : ''}</p>
                    </div>
                    <span className="irrigation-status-pill">
                      <b aria-hidden="true">{info.icon}</b>
                      {info.label}
                    </span>
                  </div>

                  <div className="irrigation-water-level">
                    <div>
                      <span>Água disponível no solo</span>
                      <strong>{dadosDesatualizados ? 'Aguardando atualização' : `${item.armazenamento_percentual?.toFixed(0)}%`}</strong>
                    </div>
                    <div className="irrigation-water-track" aria-label={dadosDesatualizados ? 'Percentual indisponível até a atualização' : `${item.armazenamento_percentual?.toFixed(0)}% de água disponível`}>
                      <span style={{ width: dadosDesatualizados ? '0%' : `${Math.max(4, item.armazenamento_percentual || 0)}%` }} />
                    </div>
                  </div>

                  <div className="irrigation-plot-facts">
                    <div><span>Lâmina</span><strong>{dadosDesatualizados ? '—' : item.necessidade_irrigacao > 0 ? `${item.necessidade_irrigacao.toFixed(1)} mm` : '0 mm'}</strong></div>
                    <div><span>Referência</span><strong>{dadosDesatualizados ? `Último dado: ${janela}` : janela}</strong></div>
                    <div><span>Fonte</span><strong>{fonteLabel(item.metodo_calculo_et0)}</strong></div>
                    {pivotSetting && (
                      <div className="irrigation-pivot-setting"><span>Ajuste operacional</span><strong>{pivotSettingLabel(pivotSetting)}</strong></div>
                    )}
                  </div>

                  {alertaDelay && (
                    <div style={{
                      marginTop: 10, fontSize: 12, fontWeight: 600,
                      padding: '4px 10px', borderRadius: 6,
                      background: `${alertaDelay.cor}14`, border: `1px solid ${alertaDelay.cor}40`, color: alertaDelay.cor,
                      display: 'inline-flex', alignItems: 'center', gap: 5
                    }}>
                      {alertaDelay.icon} {alertaDelay.msg}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="irrigation-plot-action"
                  onClick={(e) => { e.stopPropagation(); setTalhaoDetalhado(item); }}
                >
                  Ver histórico
                  <span aria-hidden="true">→</span>
                </button>
              </article>
            );
          })}
        </div>
      </section>
    </>
  );
}

// Componente auxiliar: Card de resumo
function CardResumo({ icon, label, valor, cor, darkMode }) {
  return (
    <div className="irrigation-summary-card" style={{
      padding: '16px 12px',
      borderRadius: 12,
      background: darkMode ? '#111827' : `${cor}08`,
      border: `1px solid ${cor}30`,
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <span className="irrigation-summary-icon" style={{ color: cor }}>{icon}</span>
      <div className="irrigation-summary-copy">
      <div style={{
        fontSize: 32,
        fontWeight: 800,
        color: cor,
        lineHeight: 1,
        marginBottom: 6
      }}>
        {valor}
      </div>
      <div style={{
        fontSize: 11,
        color: darkMode ? '#6b7280' : '#94a3b8',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
      }}>
        {label}
      </div>
      <small>Situação dos talhões</small>
      </div>
    </div>
  );
}

// Componente: Introdução e configuração inicial
function IntroducaoIrrigacao({ darkMode, onIrEstacoes, onIrTalhoes }) {
  return (
    <div className="irrigation-intro-panel" style={{
      background: darkMode ? '#2a2a2a' : '#fff',
      borderRadius: 8,
      padding: 24,
      marginBottom: 20,
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{
        fontSize: 18,
        fontWeight: 700,
        color: darkMode ? '#fff' : '#333',
        marginBottom: 16
      }}>
        🚀 Começando com Manejo de Irrigação
      </h3>
      
      <div className="irrigation-intro-steps" style={{
        color: darkMode ? '#aaa' : '#666',
        fontSize: 14,
        lineHeight: 1.6,
        marginBottom: 20
      }}>
        <p style={{ marginBottom: 12 }}>
          Para começar a usar o sistema de manejo de irrigação, você precisa:
        </p>
        
        <ol style={{ paddingLeft: 20, lineHeight: 2 }}>
          <li>
            <strong>Vincular uma estação meteorológica</strong>
            <br />
            <span style={{ fontSize: 13, opacity: 0.8 }}>
              Pode ser um sensor próprio da aba "Sensores" ou uma estação virtual NASA Power — criada automaticamente com dados climáticos da NASA para qualquer localização, sem necessidade de equipamento físico
            </span>
          </li>
          <li>
            <strong>Configurar os talhões</strong>
            <br />
            <span style={{ fontSize: 13, opacity: 0.8 }}>
              Definir parâmetros de solo, cultura e sistema de irrigação
            </span>
          </li>
          <li>
            <strong>Ativar o manejo</strong>
            <br />
            <span style={{ fontSize: 13, opacity: 0.8 }}>
              O sistema começará a calcular balanço hídrico e gerar recomendações
            </span>
          </li>
        </ol>
      </div>
      
      <div className="irrigation-intro-tip" style={{
        padding: 16,
        background: darkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(33, 150, 243, 0.05)',
        borderLeft: '4px solid #2196f3',
        borderRadius: 4,
        fontSize: 13,
        color: darkMode ? '#64b5f6' : '#1976d2',
        marginBottom: 20
      }}>
        <strong>💡 Dica:</strong> O sistema usa dados de estações meteorológicas locais para cálculos precisos de ET0 
        (Penman-Monteith ou Hargreaves-Samani) e balanço hídrico operacional.
      </div>
      
      {/* Botões de Ação */}
      <div className="irrigation-intro-actions" style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap'
      }}>
        <button
          className="irrigation-primary-action"
          onClick={onIrEstacoes}
          style={{
            flex: 1,
            minWidth: 200,
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #2196f3, #1976d2)',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }}
        >
          📡 Configurar Estações
        </button>
        
        <button
          className="irrigation-secondary-action"
          onClick={onIrTalhoes}
          style={{
            flex: 1,
            minWidth: 200,
            padding: '12px 24px',
            background: darkMode ? '#2a2a2a' : '#fff',
            color: darkMode ? '#64b5f6' : '#1976d2',
            border: `2px solid ${darkMode ? '#2196f3' : '#1976d2'}`,
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8
          }}
        >
          🌾 Configurar Talhões
        </button>
      </div>
    </div>
  );
}

// ============================================
// TELA: ESTAÇÕES METEOROLÓGICAS
// ============================================
function TelaEstacoes({ darkMode, propriedade, estacoes, sensoresDisponiveis, onAtualizar, toast, user, onNovaEstacao, onEditarEstacao, onExcluirEstacao }) {
  const isMobile = useIsMobile();
  return (
    <div className="irrigation-management-screen irrigation-stations-screen">
      <div className="irrigation-management-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        marginBottom: 20
      }}>
        <h2 className="irrigation-management-title" style={{
          fontSize: 20,
          fontWeight: 700,
          color: darkMode ? '#fff' : '#333',
          margin: 0
        }}>
          <FaBroadcastTower /> Estações Meteorológicas
        </h2>
        <button
          className="irrigation-primary-action"
          onClick={onNovaEstacao}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#4caf50',
            color: '#fff',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          + Nova Estação
        </button>
      </div>
      
      {estacoes.length === 0 ? (
        <div className="irrigation-management-empty" style={{
          background: darkMode ? '#2a2a2a' : '#fff',
          borderRadius: 8,
          padding: 40,
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📡</div>
          <h3 style={{ color: darkMode ? '#fff' : '#333', marginBottom: 8 }}>
            Nenhuma estação cadastrada
          </h3>
          <p style={{ color: darkMode ? '#aaa' :'#666', fontSize: 14 }}>
            Vincule sensores próprios ou estações meteorológicas externas para começar.
            <br />
            Clique no botão "Nova Estação" para cadastrar sua primeira estação.
          </p>
        </div>
      ) : (
        <div className="irrigation-management-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {estacoes.map(estacao => {
            const geoCheck = validateStationLocation(propriedade, estacao);
            return (
            <div
              className="irrigation-management-card irrigation-station-card"
              key={estacao.id}
              style={{
                background: darkMode ? '#2a2a2a' : '#fff',
                borderRadius: 8,
                padding: 16,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: estacao.ativa ? '2px solid #4caf50' : `1px solid ${darkMode ? '#444' : '#ddd'}`
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{
                    fontSize: 16,
                    fontWeight: 600,
                    color: darkMode ? '#fff' : '#333',
                    marginBottom: 8
                  }}>
                    {estacao.nome}
                  </h3>
                  <div style={{ color: darkMode ? '#aaa' : '#666', fontSize: 14 }}>
                    <div>
                      <strong>Tipo:</strong>{' '}
                      {estacao.tipo_fonte === 'nasapower'
                        ? <span style={{ color: darkMode ? '#c084fc' : '#7c3aed', fontWeight: 700 }}>🛰️ NASA POWER (Virtual)</span>
                        : estacao.tipo_fonte === 'sensor_proprio'
                          ? '📱 Sensor Próprio'
                          : estacao.tipo_fonte}
                    </div>
                    {estacao.tipo_fonte === 'nasapower' && estacao.latitude && (
                      <div style={{ fontSize: 13, color: darkMode ? '#9ca3af' : '#64748b' }}>
                        📍 {Number(estacao.latitude).toFixed(4)}, {Number(estacao.longitude).toFixed(4)}
                        {estacao.altitude ? ` · ${estacao.altitude}m` : ''}
                      </div>
                    )}
                    {estacao.device_id && <div style={{ wordBreak: 'break-all' }}><strong>Device ID:</strong> {estacao.device_id}</div>}
                    <div><strong>Status:</strong> {estacao.ativa ? '✓ Ativa' : '⏸️ Inativa'}</div>
                    {!geoCheck.valid && (
                      <div role="alert" style={{ marginTop: 7, color: '#dc2626', fontWeight: 700 }}>
                        ⚠ Vínculo geográfico incompatível: {geoCheck.message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="irrigation-card-actions" style={{ display: 'flex', gap: 8, flexDirection: isMobile ? 'column' : 'row' }}>
                  <button
                    className="irrigation-card-edit"
                    onClick={() => onEditarEstacao(estacao)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 6,
                      border: `1px solid ${darkMode ? '#444' : '#ddd'}`,
                      background: darkMode ? '#1a1a1a' : '#f5f5f5',
                      color: darkMode ? '#aaa' : '#666',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    ✏️ Editar
                  </button>
                  <button
                    className="irrigation-card-delete"
                    onClick={() => onExcluirEstacao(estacao)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 6,
                      border: `1px solid ${darkMode ? '#7f1d1d' : '#fca5a5'}`,
                      background: darkMode ? '#1a0a0a' : '#fff5f5',
                      color: darkMode ? '#f87171' : '#dc2626',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      width: isMobile ? '100%' : 'auto'
                    }}
                  >
                    🗑️ Excluir
                  </button>
                </div>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

// ============================================
// TELA: TALHÕES
// ============================================
function TelaTalhoes({ darkMode, propriedade, talhoes, estacoes, onAtualizar, toast, onConfigurarTalhao }) {
  const isMobile = useIsMobile();
  return (
    <div className="irrigation-management-screen irrigation-plots-screen">
      <h2 className="irrigation-management-title" style={{
        fontSize: 20,
        fontWeight: 700,
        color: darkMode ? '#fff' : '#333',
        marginBottom: 20
      }}>
        <FaLayerGroup /> Configuração de Talhões
      </h2>
      
      {talhoes.length === 0 ? (
        <div className="irrigation-management-empty" style={{
          background: darkMode ? '#2a2a2a' : '#fff',
          borderRadius: 8,
          padding: 40,
          textAlign: 'center',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🌾</div>
          <h3 style={{ color: darkMode ? '#fff' : '#333', marginBottom: 8 }}>
            Nenhum talhão encontrado
          </h3>
          <p style={{ color: darkMode ? '#aaa' : '#666', fontSize: 14 }}>
            Cadastre ou desenhe os talhões na Central de Operações para configurar a irrigação.
          </p>
        </div>
      ) : (
        <div className="irrigation-management-list" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {talhoes.map(talhao => {
            const config = Array.isArray(talhao.configuracao_irrigacao)
              ? talhao.configuracao_irrigacao[0]
              : talhao.configuracao_irrigacao || null;
            const temConfig = !!config;
            const plantioFuturo = isFutureLocalDate(talhao.data_plantio);
            
            return (
              <div
                className="irrigation-management-card irrigation-plot-config-card"
                key={talhao.id}
                style={{
                  background: darkMode ? '#2a2a2a' : '#fff',
                  borderRadius: 8,
                  padding: 16,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: plantioFuturo
                    ? `1px solid ${darkMode ? '#78350f' : '#fcd34d'}`
                    : temConfig && config.manejo_ativo
                    ? '2px solid #4caf50' 
                    : `1px solid ${darkMode ? '#444' : '#ddd'}`,
                  opacity: plantioFuturo ? 0.78 : 1
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                      <h3 style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: darkMode ? '#fff' : '#333',
                        margin: 0
                      }}>
                        {talhao.nome}
                      </h3>

                      {plantioFuturo && (
                        <span style={{
                          padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700,
                          background: darkMode ? '#78350f' : '#fef3c7',
                          color: darkMode ? '#fbbf24' : '#92400e'
                        }}>
                          Plantio futuro
                        </span>
                      )}
                      
                      {temConfig && config.manejo_ativo && (
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          background: 'var(--irrigation-green)',
                          color: '#fff'
                        }}>
                          ✓ Manejo Ativo
                        </span>
                      )}
                      
                      {temConfig && !config.manejo_ativo && (
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          border: '1px solid var(--irrigation-line)',
                          background: 'transparent',
                          color: 'var(--irrigation-muted)'
                        }}>
                          ⏸️ Manejo Inativo
                        </span>
                      )}
                      
                      {!temConfig && (
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          background: darkMode ? '#78350f' : '#fef3c7',
                          color: darkMode ? '#fbbf24' : '#b45309'
                        }}>
                          ⚠️ Não Configurado
                        </span>
                      )}
                    </div>
                    
                    <div style={{ color: darkMode ? '#aaa' : '#666', fontSize: 14, lineHeight: 1.8 }}>
                      {talhao.cultura && <div><strong>Cultura:</strong> {talhao.cultura}</div>}
                      {talhao.area && <div><strong>Área:</strong> {talhao.area} ha</div>}
                      {plantioFuturo && (
                        <div style={{ color: darkMode ? '#fbbf24' : '#92400e', fontWeight: 600 }}>
                          Manejo disponível em {new Date(`${talhao.data_plantio.slice(0, 10)}T12:00:00`).toLocaleDateString('pt-BR')}
                        </div>
                      )}
                      
                      {temConfig && (
                        <>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? '2px 12px' : '0 8px' }}>
                            <span><strong>Sistema:</strong> {config.sistema_irrigacao}</span>
                            <span><strong>CAD:</strong> {(() => {
                              const zrs = [config.zr_inicial||20, config.zr_desenvolvimento||40, config.zr_meio||70, config.zr_final||70];
                              const cadCm = config.cad_por_cm_solo;
                              return cadCm
                                ? `${Math.round(cadCm*Math.min(...zrs))}–${Math.round(cadCm*Math.max(...zrs))} mm`
                                : `${config.capacidade_agua_disponivel} mm`;
                            })()}</span>
                            <span><strong>Eficiência:</strong> {config.eficiencia_sistema}%</span>
                          </div>
                          {config.estacao_id && (
                            <div style={{ color: 'var(--irrigation-green)', fontSize: 13, marginTop: 4, fontWeight: 600 }}>
                              📡 Estação configurada
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  
                  <button
                    className="irrigation-primary-action irrigation-config-action"
                    onClick={() => onConfigurarTalhao(talhao)}
                    disabled={plantioFuturo}
                    style={{
                      padding: '10px 20px',
                      borderRadius: 8,
                      border: 'none',
                      background: plantioFuturo ? (darkMode ? '#4b5563' : '#cbd5e1') : temConfig ? '#2196f3' : '#ff9800',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: plantioFuturo ? 'not-allowed' : 'pointer',
                      width: isMobile ? '100%' : 'auto',
                      flexShrink: 0
                    }}
                  >
                    {plantioFuturo ? 'Aguardar plantio' : temConfig ? '⚙️ Editar Config' : '📝 Configurar'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
